import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Room, RoomEvent, createLocalAudioTrack } from "livekit-client";
import {
  TALKBACK_TRACK_NAME,
  talkbackPermissions,
  talkbackTargetLabel,
  type TalkbackTarget,
  type TalkbackSignal,
} from "./talkback";
import { DIRECTOR_ONAIR_TRACK } from "./directorMic";
import { useLiveChat, useSentFanQuestions } from "./liveChat";
import { useProductionChat, ProductionChatPanel } from "./productionChat";
import WhiteboardCanvas, { whiteboardLayout } from "./Whiteboard";
import { AdSurface } from "./AdSurface";
import { SC, SCLowerThird, SCMonitor, SCPanel } from "./studioChrome";
import {
  broadcastToken,
  createGuestInvite,
  createSlateAd,
  deckListPairings,
  deckPair,
  deckRevoke,
  deleteBackground,
  endBroadcast,
  ensureBroadcast,
  fetchBroadcastProgram,
  fetchCaveAds,
  fetchCaveBackgrounds,
  listGuestInvites,
  uploadBackground,
  muteBroadcast,
  pauseBroadcast,
  resumeBroadcast,
  revokeGuestInvite,
  setBroadcastProgram,
  stopBroadcast,
  unmuteBroadcast,
  uploadSlateAd,
  uploadVideoAd,
  type Ad,
  type Background,
  type BoardId,
  type Broadcast,
  type CaveEvent,
  type DeckAction,
  type DeckAudioChannel,
  type DeckPairing,
  type DuelSlot,
  type SourceSlot,
  type GuestInvite,
  type ProgramLayout,
  type ProgramOverlay,
  type ProgramState,
  type TransitionType,
  type WhiteboardLayout,
  type WhiteboardStroke,
} from "./api";
import { ConnectionBanner, isGuestIdentity, participantSourceKey, useConnState } from "./liveConnection";
import { useDeckBridge } from "./deckBridge";
import { PLUGIN_DOWNLOAD_PATH } from "./streamdeckPlugin";
import { boardOptions, resolveBoardImageUrl, useBoardSlates } from "./boardSlates";
import { brandedQrDataUrl } from "./brandedQr";
import {
  isMultiHead,
  layoutGridTemplate,
  layoutSlots,
  layoutHasSharedBoard,
  slotSource,
  slotSourcePatch,
  SLOT_ACCENTS,
} from "./programLayout";
import { HOLD_DURATIONS } from "./holdDurations";
import { sampleVideoColor } from "./chromaKey";
import { loadPref, savePref } from "./prefs";
import ChromaControls from "./ChromaControls";
import {
  HOME_WORKSPACE_ID, layoutEqual, placeWidget, reconcileWorkspaces, removeWidget,
  seedWidgetDefaults, setWorkspaceLayout, validateWidgetPlacement, visibleWorkspaces, WorkspaceTabsEditor,
  type WidgetLayout, type WorkspaceDef, type WorkspaceWidgetDefaults,
} from "./studioWorkspaces";
import { WorkspaceCanvas, type WidgetDef } from "./WorkspaceCanvas";

// ── Slot identity (ADR 0108) ─────────────────────────────────────────────────
// Two consistent accent colours for the SOURCE slots of a multi-head layout
// (ADR 0054), applied everywhere a Slot A / Slot B appears — the multiview
// assignment controls, the Compose SLOT·STAGED rack, and the PREVIEW/PROGRAM
// monitor tiles — so the operator can never mistake which feed is A vs B. This
// SLOT identity is ORTHOGONAL to the bus tally (PREVIEW = blue, PROGRAM = red):
// the bus says "staged vs on-air", the slot accent says "which head". Picked from
// the studioChrome (ADR 0087) iOS-dark palette: A = amber, B = violet — both
// distinct from red/blue/green and carrying dark ink at AA contrast.
// A/B/C/D accents live in programLayout.SLOT_ACCENTS (shared with the fan, ADR
// 0108/0212): A = amber, B = violet, C = teal, D = rose — all distinct from
// red/blue/green and from each other. A/B keep dark ink at AA contrast.
const SLOT_INK = "#0f0f11"; // near-black ink: readable on the accents
const slotAccent = (slot: SourceSlot): string => SLOT_ACCENTS[slot];

// Director console (ADR 0012 — Phase 2). A director joins an event's broadcast
// as a publisher who mostly *subscribes*: they see every legend's live feed in a
// multiview, pick which one is "on program", and pop a simple overlay. Those
// choices are the shared program state — pushed both to the API (persisted for
// late-joiners) and as a LiveKit data message (instant fan-out to viewers).

/** The slice of a LiveKit track the console uses: attach it to a <video>. */
interface Attachable {
  attach(el: HTMLMediaElement): HTMLMediaElement;
  detach(): HTMLMediaElement[];
}

/** One legend's feed in the multiview. `identity` is unique per connection (ADR
 *  0027); `key` is the STABLE program key (username) that `program.source` holds
 *  and that survives reconnects (ADR 0042). */
interface Feed {
  identity: string;
  key: string;
  name?: string;
  hasVideo: boolean;
  hasAudio: boolean;
}

/** One operator-local monitor-mixer channel (ADR 0081/0085): how loud the DIRECTOR
 *  hears this source at their own desk. Volume + mute are LOCAL (a media element),
 *  never the SFU — the authoritative program mute stays in Program hold (ADR 0055).
 *  Channels default MUTED so opening the console never dumps every mic at once. */
interface MixChannel {
  volume: number;
  muted: boolean;
}

/** One run-of-show cue (ADR 0088; extended ADR 0122) — a named, captured program
 *  SCENE the Director can send to preview and TAKE in order. The `scene` is a
 *  `sceneOf` snapshot, so a cue carries the full stageable scene: source / sourceB /
 *  layout AND the graphics overlay (lower-third / whiteboard / **ad**) — matching
 *  everything the console can put on program. Client-side (MVP), like the scene presets. */
interface Cue {
  id: string;
  name: string;
  scene: ProgramState;
}

type OverlayKind = ProgramOverlay["kind"];

export interface DirectorConsoleProps {
  caveId: string;
  event: CaveEvent;
  onClose?: () => void;
}

// A single, per-browser "default Director profile" (ADR 0120) — the operating choices
// worth remembering between sessions. Deliberately NOT a profile-management system;
// per-user / per-legend profiles are a documented, deferred extension.
interface DirectorProfile {
  transitionType: TransitionType;
  durationMs: number;
  titleSafe: boolean;
  cellGap: number;
  collapsed: { audio: boolean; health: boolean };
  workspaces?: WorkspaceDef[]; // ADR 0123 (tabs) → ADR 0124 (widget layouts) → ADR 0125 (custom)
}
const PROFILE_KEY = "legends.director.profile";
// The built-in tabs (ADR 0123) — reproduce today's tab set + order. Phase 2 (ADR 0124)
// seeds each with its default widgets/layout; here they carry name/order/visibility.
const DIRECTOR_BUILTIN_WORKSPACES: WorkspaceDef[] = [
  { id: "console", name: "Console", builtin: true, hidden: false },
  { id: "compose", name: "Compose", builtin: true, hidden: false },
  { id: "chroma", name: "Chroma key", builtin: true, hidden: false },
  { id: "graphics", name: "Graphics", builtin: true, hidden: false },
  { id: "ads", name: "Ads & breaks", builtin: true, hidden: false },
  { id: "rundown", name: "Rundown", builtin: true, hidden: false },
  { id: "guests", name: "Guests", builtin: true, hidden: false },
  { id: "questions", name: "Questions", builtin: true, hidden: false },
  { id: "stream", name: "Stream", builtin: true, hidden: false },
  { id: "deck", name: "Deck", builtin: true, hidden: false },
];
const DEFAULT_PROFILE: DirectorProfile = {
  transitionType: "cut",
  durationMs: 500,
  titleSafe: true,
  cellGap: 2,
  collapsed: { audio: false, health: false },
  workspaces: DIRECTOR_BUILTIN_WORKSPACES,
};

// Widget catalog (ADR 0124): each console panel/section is a draggable widget with a
// default add-size + min-size (12-col grid units). The node — the existing in-render JSX
// — is paired in the render so widgets stay closures over console state.
const DIRECTOR_WIDGETS: { id: string; title: string; w: number; h: number; minW: number; minH: number }[] = [
  { id: "sources", title: "Sources", w: 3, h: 9, minW: 2, minH: 4 },
  { id: "ask", title: "Ask the Legend", w: 3, h: 7, minW: 2, minH: 3 },
  { id: "scenes", title: "Scenes", w: 5, h: 5, minW: 3, minH: 3 },
  { id: "graphicsBus", title: "Graphics bus", w: 5, h: 6, minW: 3, minH: 3 },
  { id: "audio", title: "Audio", w: 4, h: 8, minW: 3, minH: 4 },
  { id: "health", title: "Health", w: 4, h: 4, minW: 2, minH: 2 },
  { id: "hold", title: "Program hold", w: 4, h: 4, minW: 2, minH: 2 },
  { id: "prodChat", title: "Production chat", w: 4, h: 8, minW: 3, minH: 4 },
  { id: "compose", title: "Compose", w: 12, h: 16, minW: 4, minH: 6 },
  { id: "chroma", title: "Chroma key", w: 12, h: 13, minW: 4, minH: 6 },
  { id: "graphics", title: "Graphics", w: 12, h: 14, minW: 4, minH: 6 },
  { id: "ads", title: "Ads & breaks", w: 12, h: 13, minW: 4, minH: 6 },
  { id: "rundown", title: "Rundown", w: 12, h: 9, minW: 4, minH: 4 },
  { id: "guests", title: "Guests", w: 8, h: 7, minW: 3, minH: 3 },
  { id: "questions", title: "Questions", w: 8, h: 9, minW: 3, minH: 3 },
  { id: "stream", title: "Stream", w: 12, h: 9, minW: 4, minH: 4 },
  { id: "deck", title: "Deck", w: 8, h: 10, minW: 3, minH: 4 },
];
const DIRECTOR_WIDGET_META: Record<string, { id: string; title: string; w: number; h: number; minW: number; minH: number }> =
  Object.fromEntries(DIRECTOR_WIDGETS.map((w) => [w.id, w]));
const DIRECTOR_WIDGET_IDS = DIRECTOR_WIDGETS.map((w) => w.id);
const gi = (id: string, x: number, y: number): WidgetLayout => {
  const m = DIRECTOR_WIDGET_META[id];
  return { i: id, x, y, w: m.w, h: m.h, minW: m.minW, minH: m.minH };
};
// The default widget→tab placement + layout — reproduces today's arrangement (ADR 0124).
const DIRECTOR_WIDGET_DEFAULTS: WorkspaceWidgetDefaults = {
  console: { widgets: ["sources", "ask", "prodChat", "scenes", "graphicsBus", "audio", "health", "hold"], layout: [gi("sources", 0, 0), gi("ask", 0, 9), gi("prodChat", 0, 16), gi("scenes", 3, 0), gi("graphicsBus", 3, 5), gi("audio", 8, 0), gi("health", 8, 8), gi("hold", 8, 12)] },
  compose: { widgets: ["compose"], layout: [gi("compose", 0, 0)] },
  chroma: { widgets: ["chroma"], layout: [gi("chroma", 0, 0)] },
  graphics: { widgets: ["graphics"], layout: [gi("graphics", 0, 0)] },
  ads: { widgets: ["ads"], layout: [gi("ads", 0, 0)] },
  rundown: { widgets: ["rundown"], layout: [gi("rundown", 0, 0)] },
  guests: { widgets: ["guests"], layout: [gi("guests", 0, 0)] },
  questions: { widgets: ["questions"], layout: [gi("questions", 0, 0)] },
  stream: { widgets: ["stream"], layout: [gi("stream", 0, 0)] },
  deck: { widgets: ["deck"], layout: [gi("deck", 0, 0)] },
};

export default function DirectorConsole({ caveId, event, onClose }: DirectorConsoleProps) {
  // Load the persisted Director profile once (ADR 0120); seeds the operating defaults
  // + collapsed panels below, and a single effect writes it back on any change.
  const [profile0] = useState<DirectorProfile>(() => loadPref(PROFILE_KEY, DEFAULT_PROFILE));
  const [broadcast, setBroadcast] = useState<Broadcast | null>(null);
  const [roomName, setRoomName] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [program, setProgram] = useState<ProgramState>({});
  // The STAGED next scene for the program bus (ADR 0081) — source / sourceB /
  // layout / dueling board templates. Editing the multiview + Compose tab mutates
  // ONLY this; PROGRAM (what fans see) is untouched until a TAKE. Overlays keep
  // their own graphics-bus take (ADR 0063) and chroma stays a live override — the
  // classic switcher bus separation. Initialized from the persisted program on
  // bootstrap so the preview opens matching what's live.
  const [preview, setPreview] = useState<ProgramState>({});
  const previewRef = useRef<ProgramState>({});
  // TAKE-bar controls (ADR 0081): the transition a timed take carries + the auto-out
  // duration + the manual T-bar throw. A CUT is instant; AUTO / a full T-bar throw
  // carry {transitionType, durationMs}; FTB toggles the live program blackout.
  const [transitionType, setTransitionType] = useState<TransitionType>(profile0.transitionType);
  const [durationMs, setDurationMs] = useState(profile0.durationMs);
  const [tbar, setTbar] = useState(0);
  const [taking, setTaking] = useState(false);
  const [tab, setTab] = useState("console");
  // Tabs-as-data (ADR 0123): the nav renders these; the operator show/hides + reorders
  // (and, ADR 0125, creates custom ones). Reconciled against the built-ins on load.
  const [workspaces, setWorkspaces] = useState<WorkspaceDef[]>(() =>
    validateWidgetPlacement(
      seedWidgetDefaults(reconcileWorkspaces(profile0.workspaces, DIRECTOR_BUILTIN_WORKSPACES), DIRECTOR_WIDGET_DEFAULTS),
      new Set(DIRECTOR_WIDGET_IDS),
    ),
  );
  const [tabsEditorOpen, setTabsEditorOpen] = useState(false);
  // Layout edit mode (ADR 0124) — transient, not persisted; unlocks drag/resize + toolbox.
  const [editMode, setEditMode] = useState(false);
  // Operator-local monitor mixer (ADR 0085): per-source channel + a master gain,
  // all local to the Director's own ears. Channels default muted (opt-in monitor).
  const [mix, setMix] = useState<Record<string, MixChannel>>({});
  const [master, setMaster] = useState(0.8);
  const [masterMuted, setMasterMuted] = useState(false);
  // Director talkback (ADR 0213, LSC-423): the target currently being spoken to while
  // a PTT control is held, or null when idle. A PRIVATE Director→talent audio path —
  // never program state, never the fan/recording mix.
  const [talkbackTarget, setTalkbackTarget] = useState<TalkbackTarget | null>(null);
  // Director PUBLIC mic → On Air (LSC-633, ADR 0247): a LATCHED control (distinct from
  // the push-to-talk talkback above) that joins the Director's voice to the FAN Program
  // mix + recording, without a video slot or a TAKE. Off by default; resets to off on a
  // fresh console (no silent resume). A `micErr` surfaces a mic failure (acceptance #8).
  const [micOnAir, setMicOnAir] = useState(false);
  const [micErr, setMicErr] = useState<string | null>(null);
  // The browser's autoplay policy can suspend the monitor <audio> elements until a
  // user gesture (ADR 0147, LSC-182): the faders showed but nothing was audible
  // because playback was never unlocked. Track it + offer an explicit unlock.
  const [monitorAudioBlocked, setMonitorAudioBlocked] = useState(false);
  // Which preview slot a source-tap stages to in a multi-head layout (ADR 0087).
  const [stageSlot, setStageSlot] = useState<SourceSlot>("a");
  // ON-AIR elapsed clock (ADR 0087) — ticks from when the console connects.
  const [elapsedMs, setElapsedMs] = useState(0);
  const liveSinceRef = useRef<number | null>(null);
  const [strokes, setStrokes] = useState<WhiteboardStroke[]>([]);
  const [wbLayout, setWbLayout] = useState<WhiteboardLayout>("pip-legend");
  const [wbBoard, setWbBoard] = useState<BoardId>("blank");
  // The board-slate catalog (ADR 0058) — built-in vector boards + admin uploads.
  // Feeds the board pickers and resolves a `board` value to its background image.
  const boardSlates = useBoardSlates();
  const boardOpts = boardOptions(boardSlates);
  const boardImg = (board?: BoardId) => resolveBoardImageUrl(board, boardSlates);
  const [ads, setAds] = useState<Ad[]>([]);
  const [selectedAdId, setSelectedAdId] = useState("");
  // Chroma-key background library (ADR 0070) — for the Director's override control.
  const [backgrounds, setBackgrounds] = useState<Background[]>([]);
  // Eyedropper: when armed, clicking the on-program tile samples the key color.
  const [eyedropper, setEyedropper] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [l3Title, setL3Title] = useState("");
  const [l3Subtitle, setL3Subtitle] = useState("");
  // Lower-third STYLE (ADR 0088) — accent colour + band position, published with the
  // overlay; an optional auto-out timer clears it after N seconds (0 = manual).
  const [l3Accent, setL3Accent] = useState("#ffd60a");
  const [l3Position, setL3Position] = useState<"left" | "center">("left");
  const [autoOutS, setAutoOutS] = useState(0);
  const autoOutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Run-of-show cues (ADR 0088) — client-side, ordered scene recipes.
  const [rundown, setRundown] = useState<Cue[]>([]);
  const cueSeqRef = useRef(0);
  // Compose cosmetics (ADR 0088) — multiview cell gap + title-safe guides.
  const [cellGap, setCellGap] = useState(profile0.cellGap);
  const [titleSafe, setTitleSafe] = useState(profile0.titleSafe);
  // Collapsed state for the secondary Console racks (ADR 0113), LIFTED here (ADR 0120)
  // so it survives a tab switch (the panels unmount on tab change) AND persists across
  // sessions via the Director profile.
  const [collapsedPanels, setCollapsedPanels] = useState(profile0.collapsed);
  // Intermission board (ADR 0088) — a countdown slate the Director takes as a slate ad.
  const [boardTitle, setBoardTitle] = useState("We'll be right back");
  const [boardSub, setBoardSub] = useState("Northwind Live");
  // The overlay kind being PREPARED off-air (ADR 0063). Distinct from the LIVE
  // overlay (program.overlay): selecting a kind, a board, or typing a lower third
  // stages into this pane and does NOT touch Fan Program — only an explicit Send
  // to Program / Take off air mutates program.overlay.
  const [stagedKind, setStagedKind] = useState<OverlayKind>("none");
  // Timed auto-resume duration for the production hold (ADR 0055); "0" = manual.
  const [pauseSecondsStr, setPauseSecondsStr] = useState("0");
  const pauseSeconds = Number(pauseSecondsStr) || 0;
  const [ending, setEnding] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pushError, setPushError] = useState<string | null>(null);
  const [conn, setConn] = useConnState();
  const [reloadKey, setReloadKey] = useState(0); // bumped by Rejoin to reconnect
  const [, setRoomState] = useState<Room | null>(null); // re-render on connect; chat is API-driven now
  const chat = useLiveChat(broadcast?.id ?? null, connected);
  const sentQuestions = useSentFanQuestions(broadcast?.id ?? null, connected);
  // Private Legend↔Director production chat (LSC-404) — polls once connected.
  const prodChat = useProductionChat(broadcast?.id ?? null, connected);
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);
  const [sendingQuestion, setSendingQuestion] = useState(false);
  const [questionError, setQuestionError] = useState<string | null>(null);
  // Guest publishers (ADR 0044): the director invites a non-operator into this
  // one broadcast; the invite yields a shareable public join link.
  const [guestInvites, setGuestInvites] = useState<GuestInvite[]>([]);
  const [guestLabel, setGuestLabel] = useState("");
  const [invitingGuest, setInvitingGuest] = useState(false);
  const [guestError, setGuestError] = useState<string | null>(null);
  // Per-invite QR data-URLs (branded, ADR 0141) so a guest can scan the join link,
  // and the code of the link most recently copied (for the copy-button feedback).
  const [guestQrs, setGuestQrs] = useState<Record<string, string>>({});
  const [copiedGuest, setCopiedGuest] = useState<string | null>(null);

  const roomRef = useRef<Room | null>(null);
  const mountedRef = useRef(true);
  // A deliberate Exit (ADR 0105) flags this so the room teardown's Disconnected
  // event is expected and never drives the reconnect banner to "Connection lost".
  const exitingRef = useRef(false);
  const programRef = useRef<ProgramState>({});
  const tracksRef = useRef<Map<string, Attachable>>(new Map());
  const tileElsRef = useRef<Map<string, HTMLVideoElement>>(new Map());
  // Audio-mixer plumbing (ADR 0085): the subscribed audio track per participant +
  // the operator-local <audio> element playing it, keyed by identity.
  const audioTracksRef = useRef<Map<string, Attachable>>(new Map());
  const audioElsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  const dirTuneTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const programVideoRef = useRef<HTMLVideoElement | null>(null);
  const programVideoRefB = useRef<HTMLVideoElement | null>(null); // slot B (ADR 0054)
  const programVideoRefC = useRef<HTMLVideoElement | null>(null); // slot C (ADR 0212)
  const programVideoRefD = useRef<HTMLVideoElement | null>(null); // slot D (ADR 0212)
  // PREVIEW-bus monitor video refs (ADR 0081) — show the STAGED scene's source(s).
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);
  const previewVideoRefB = useRef<HTMLVideoElement | null>(null);
  const previewVideoRefC = useRef<HTMLVideoElement | null>(null); // slot C (ADR 0212)
  const previewVideoRefD = useRef<HTMLVideoElement | null>(null); // slot D (ADR 0212)
  // The Director's published talkback mic (ADR 0213) — created lazily on first PTT,
  // published named `talkback`, kept muted between presses, and stopped on teardown.
  const talkbackTrackRef = useRef<{ mute: () => Promise<void>; unmute: () => Promise<void>; stop: () => void } | null>(null);
  // The Director's published PUBLIC on-air mic (ADR 0247) — created lazily when On Air
  // is first latched, published named `director-onair`, muted unless on air (and not
  // mid-PTT), stopped on teardown. Refs mirror the latch + the active talkback target so
  // the PTT and the latch toggle read each other's current value without stale closures.
  const onAirTrackRef = useRef<{ mute: () => Promise<void>; unmute: () => Promise<void>; stop: () => void } | null>(null);
  const micOnAirRef = useRef(false);
  const talkbackTargetRef = useRef<TalkbackTarget | null>(null);

  // Derive the multiview from the room's current remote participants + their
  // subscribed video tracks. Called on every participant/track lifecycle event.
  const rebuild = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;
    const list: Feed[] = [];
    const seen = new Set<string>();
    room.remoteParticipants.forEach((p) => {
      let videoTrack: Attachable | undefined;
      p.videoTrackPublications.forEach((pub) => {
        if (pub.videoTrack) videoTrack = pub.videoTrack as Attachable;
      });
      let audioTrack: Attachable | undefined;
      p.audioTrackPublications?.forEach((pub) => {
        if (pub.audioTrack) audioTrack = pub.audioTrack as Attachable;
      });
      // Only publishers (legends/directors/guests) are program sources; skip
      // viewers (ticket-holding fans). A pure viewer has no publish grant AND no
      // live media. The old guard skipped only when permissions were KNOWN and
      // canPublish was false, so a fan whose `permissions` hadn't loaded yet leaked
      // in as a blank feed — which then got auto-assigned as the "legend"
      // (feeds.find(!isGuestIdentity)), dropping the real source on a layout/scene
      // change and surfacing a phantom blank source (LSC-175). Requiring
      // publish-or-media excludes viewers deterministically while still including a
      // publisher the instant it has a track, even if permissions lag.
      const canPublish = p.permissions?.canPublish ?? false;
      if (!canPublish && !videoTrack && !audioTrack) return;
      seen.add(p.identity);
      if (videoTrack) tracksRef.current.set(p.identity, videoTrack);
      else tracksRef.current.delete(p.identity);
      if (audioTrack) audioTracksRef.current.set(p.identity, audioTrack);
      else audioTracksRef.current.delete(p.identity);
      list.push({ identity: p.identity, key: participantSourceKey(p), name: p.name, hasVideo: !!videoTrack, hasAudio: !!audioTrack });
    });
    // Drop tracks for participants who have left.
    tracksRef.current.forEach((_track, id) => {
      if (!seen.has(id)) tracksRef.current.delete(id);
    });
    audioTracksRef.current.forEach((_track, id) => {
      if (!seen.has(id)) audioTracksRef.current.delete(id);
    });
    if (mountedRef.current) setFeeds(list);
  }, []);

  // Persist the Director profile (ADR 0120) on any change to a remembered operating
  // choice — best-effort, per-browser (savePref never throws). Runs on mount too, which
  // is an idempotent write of the loaded values.
  useEffect(() => {
    savePref<DirectorProfile>(PROFILE_KEY, { transitionType, durationMs, titleSafe, cellGap, collapsed: collapsedPanels, workspaces });
  }, [transitionType, durationMs, titleSafe, cellGap, collapsedPanels, workspaces]);

  // Never sit on a tab the operator hid or deleted (ADR 0123) — fall back to home.
  useEffect(() => {
    const active = workspaces.find((w) => w.id === tab);
    if (!active || active.hidden) setTab(HOME_WORKSPACE_ID);
  }, [workspaces, tab]);

  // On open: get-or-create the broadcast, mint a publisher token, connect the
  // room, and read the persisted program so the console opens on current state.
  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      try {
        const b = await ensureBroadcast(caveId, event.id);
        if (!mountedRef.current) return;
        setBroadcast(b);
        const [t, prog] = await Promise.all([
          // Tag this as the DIRECTOR connection (ADR 0225, LSC-471): the server mints a
          // reserved identity so this console's ISO capture — the private talkback mic
          // only (ADR 0213), never a program source — is excluded from the VOD composite.
          broadcastToken(b.id, { as: "director" }),
          fetchBroadcastProgram(b.id),
        ]);
        if (!mountedRef.current) return;
        programRef.current = prog;
        setProgram(prog);
        // Open the preview matching what's live (nothing staged yet, ADR 0081).
        const initScene = sceneOf(prog);
        previewRef.current = initScene;
        setPreview(initScene);
        setStrokes(prog.overlay?.kind === "whiteboard" ? (prog.overlay.strokes ?? []) : []);
        if (prog.overlay?.kind === "whiteboard" && prog.overlay.layout) setWbLayout(prog.overlay.layout);
        if (prog.overlay?.kind === "whiteboard" && prog.overlay.board) setWbBoard(prog.overlay.board);
        if (prog.overlay?.kind === "lower-third") {
          setL3Title(prog.overlay.title ?? "");
          setL3Subtitle(prog.overlay.subtitle ?? "");
          // Seed the STYLE too (ADR 0116): the staging effect re-derives preview.overlay
          // from these fields, and the program TAKE commits it — so a mid-show operator
          // must inherit the live accent/position or a later take would revert them.
          if (prog.overlay.accent) setL3Accent(prog.overlay.accent);
          if (prog.overlay.position) setL3Position(prog.overlay.position);
        }
        // Open the staging pane on whatever is currently LIVE so an operator who
        // joins mid-show edits the on-air overlay (ADR 0063), not a blank pane.
        setStagedKind(prog.overlay?.kind ?? "none");
        setSelectedAdId(prog.overlay?.adId ?? "");
        fetchCaveAds(caveId)
          .then((list) => {
            if (!mountedRef.current) return;
            setAds(list);
            setSelectedAdId((cur) => cur || list[0]?.id || "");
          })
          .catch(() => undefined);
        // Chroma-key background library (ADR 0070) so the Director can override
        // the Legend's background selection.
        fetchCaveBackgrounds(caveId)
          .then((list) => mountedRef.current && setBackgrounds(list))
          .catch(() => undefined);

        const room = new Room();
        roomRef.current = room;
        room
          .on(RoomEvent.ParticipantConnected, rebuild)
          .on(RoomEvent.ParticipantDisconnected, rebuild)
          .on(RoomEvent.TrackSubscribed, rebuild)
          .on(RoomEvent.TrackUnsubscribed, rebuild)
          // Monitor-audio autoplay gate (ADR 0147/LSC-182): the browser can suspend
          // playback until a gesture; surface it so the operator can unlock.
          .on(RoomEvent.AudioPlaybackStatusChanged, () => {
            if (mountedRef.current) setMonitorAudioBlocked(!room.canPlaybackAudio);
          })
          // Shared whiteboard (ADR 0045): the Legend telestrates on the same
          // board and relays program state over data. Fold their board+strokes
          // into ours so both operators (and the preview) see the shared drawing.
          // We only merge while the Director's own overlay is the whiteboard, so
          // an incoming update never yanks the console out of an ad/lower-third.
          .on(RoomEvent.DataReceived, (payload: Uint8Array) => {
            try {
              const parsed = JSON.parse(new TextDecoder().decode(payload)) as {
                type?: string;
                state?: ProgramState;
              };
              if (parsed.type !== "program" || !parsed.state) return;
              // Reflect the fan-facing program hold flags (ADR 0053) set by the
              // Legend, so both operators see the current mute/pause state. These
              // are independent scalars — merged regardless of the overlay so an
              // incoming hold never yanks the console out of its own overlay.
              const st = parsed.state;
              if (mountedRef.current && (typeof st.audioMuted === "boolean" || typeof st.videoPaused === "boolean")) {
                const held: ProgramState = {
                  ...programRef.current,
                  audioMuted: st.audioMuted ?? programRef.current.audioMuted,
                  videoPaused: st.videoPaused ?? programRef.current.videoPaused,
                };
                programRef.current = held;
                setProgram(held);
              }
              // Reflect the Legend's Ask-the-Legend toggle (ADR 0077/LSC-174). Like
              // the hold flags + chroma, askDisabled is an independent scalar the
              // Legend can set from Go-Live; merge an inbound askDisabled so the
              // Director's toggle reflects the LIVE state regardless of who set it.
              // Director→Legend already worked (the Legend reads program.askDisabled);
              // this closes the Legend→Director direction. Merged regardless of overlay.
              if (mountedRef.current && typeof st.askDisabled === "boolean") {
                const withAsk: ProgramState = { ...programRef.current, askDisabled: st.askDisabled };
                programRef.current = withAsk;
                setProgram(withAsk);
              }
              // Fold in a publisher's dueling per-slot boards (ADR 0054) so the
              // console preview reflects each publisher's own telestration. Merged
              // regardless of overlay — independent of the shared-board merge below.
              if (mountedRef.current && st.boards) {
                const mergedBoards: ProgramState = {
                  ...programRef.current,
                  boards: { ...programRef.current.boards, ...st.boards },
                };
                programRef.current = mergedBoards;
                setProgram(mergedBoards);
              }
              // Reflect the Legend's chroma/background selection (ADR 0070/0071/0102).
              // Chroma rides the opaque program state and the Legend can set it from
              // Go-Live too; cross-role sync was asymmetric — a Director override
              // reached the Legend (its client re-composites), but a Legend-side change
              // never updated the Director's ChromaControls. Merge an inbound chroma so
              // the Director's background controls reflect the LIVE program.chroma
              // regardless of who set it. Merged regardless of the current overlay; the
              // Legend relays the full chroma object, so replace (don't deep-merge).
              if (mountedRef.current && st.chroma) {
                const withChroma: ProgramState = { ...programRef.current, chroma: st.chroma };
                programRef.current = withChroma;
                setProgram(withChroma);
              }
              const incoming = parsed.state.overlay;
              if (incoming?.kind !== "whiteboard") return;
              if (!mountedRef.current || programRef.current.overlay?.kind !== "whiteboard") return;
              const merged: ProgramState = {
                ...programRef.current,
                overlay: {
                  ...programRef.current.overlay,
                  kind: "whiteboard",
                  strokes: incoming.strokes ?? [],
                  layout: programRef.current.overlay?.layout,
                  board: incoming.board ?? programRef.current.overlay?.board,
                },
              };
              programRef.current = merged;
              setProgram(merged);
              setStrokes(incoming.strokes ?? []);
              if (incoming.board) setWbBoard(incoming.board);
            } catch {
              // ignore malformed / unrelated data messages
            }
          })
          // Reconnect messaging (ADR 0042).
          .on(RoomEvent.Reconnecting, () => {
            if (mountedRef.current) setConn("reconnecting");
          })
          .on(RoomEvent.Reconnected, () => {
            if (mountedRef.current) {
              setConn("reconnected");
              rebuild();
            }
          })
          .on(RoomEvent.Disconnected, () => {
            // A deliberate Exit (ADR 0105) or our own teardown (which nulls roomRef
            // first) is expected — only a genuine, unexpected drop shows the banner.
            if (mountedRef.current && roomRef.current === room && !exitingRef.current) {
              setConnected(false);
              setConn("lost");
            }
            // The public mic (ADR 0247) resets to OFF on any disconnect — the published
            // track is gone with the room, so never silently resume it (acceptance #8);
            // the Director must re-latch On Air after reconnecting / rejoining.
            micOnAirRef.current = false;
            onAirTrackRef.current = null;
            if (mountedRef.current) setMicOnAir(false);
          });
        await room.connect(t.url, t.token);
        if (!mountedRef.current) {
          void room.disconnect();
          return;
        }
        setRoomName(t.room);
        setConnected(true);
        setRoomState(room); // enables the live chat / Q&A channel
        rebuild();
      } catch (err: unknown) {
        if (mountedRef.current) setError(msg(err));
      }
    })();

    return () => {
      mountedRef.current = false;
      tracksRef.current.forEach((t) => {
        try {
          t.detach();
        } catch {
          // best-effort teardown — never throw on unmount
        }
      });
      tracksRef.current.clear();
      tileElsRef.current.clear();
      const room = roomRef.current;
      roomRef.current = null;
      if (room) void room.disconnect();
      setRoomState(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caveId, event.id, reloadKey]);

  // Rejoin after a hard disconnect — re-run the connect effect.
  const rejoin = () => {
    setConn("live");
    setReloadKey((k) => k + 1);
  };

  // Exit (ADR 0092/0105): leave the console WITHOUT ending the show. A DELIBERATE
  // teardown — flag it (exitingRef) and clear any in-flight reconnect state BEFORE
  // releasing the LiveKit room, so the room's Disconnected event never flashes a
  // spurious "Connection lost" on the way out. A genuine mid-show drop (flag NOT
  // set) still surfaces the banner (ADR 0042).
  const exitConsole = () => {
    if (!onClose) return;
    exitingRef.current = true;
    setConn("live"); // hide any reconnecting/lost banner on the way out
    const room = roomRef.current;
    roomRef.current = null;
    if (room) void room.disconnect();
    onClose();
  };

  // Tick the ON-AIR elapsed clock while connected (ADR 0087).
  useEffect(() => {
    if (!connected) {
      liveSinceRef.current = null;
      setElapsedMs(0);
      return;
    }
    if (liveSinceRef.current == null) liveSinceRef.current = Date.now();
    const id = window.setInterval(() => {
      if (liveSinceRef.current != null) setElapsedMs(Date.now() - liveSinceRef.current);
    }, 1000);
    return () => window.clearInterval(id);
  }, [connected]);

  // Attach subscribed tracks to their tiles after any feed change (covers the
  // track-arrives-after-tile order; the tile ref callback covers the reverse).
  useEffect(() => {
    feeds.forEach((f) => {
      const el = tileElsRef.current.get(f.identity);
      const track = tracksRef.current.get(f.identity);
      if (el && track) {
        try {
          track.attach(el);
        } catch {
          // attach is cosmetic under jsdom
        }
      }
    });
  }, [feeds]);

  // Keep the program preview showing the selected source's live feed. Resolve
  // the source's STABLE key to its current (possibly reconnected) identity, then
  // its track (ADR 0042).
  useEffect(() => {
    const el = programVideoRef.current;
    if (!el) return;
    const feed = program.source ? feeds.find((f) => f.key === program.source) : undefined;
    const track = feed ? tracksRef.current.get(feed.identity) : undefined;
    if (track) {
      try {
        track.attach(el);
      } catch {
        // no-op under jsdom
      }
    } else {
      try {
        el.srcObject = null;
      } catch {
        // no-op
      }
    }
    // Full-screen-only (ADR 0074): the preview <video> mounts once, so no layout
    // toggle to re-attach for — re-attach when the program source or feeds change.
  }, [program.source, feeds]);

  // Keep the slot B preview showing its assigned source's feed in a multi-head
  // layout (ADR 0054), resolved by STABLE key like slot A.
  useEffect(() => {
    const el = programVideoRefB.current;
    if (!el) return;
    const key = isMultiHead(program.layout) ? program.sourceB : undefined;
    const feed = key ? feeds.find((f) => f.key === key) : undefined;
    const track = feed ? tracksRef.current.get(feed.identity) : undefined;
    if (track) {
      try {
        track.attach(el);
      } catch {
        // no-op under jsdom
      }
    } else {
      try {
        el.srcObject = null;
      } catch {
        // no-op
      }
    }
  }, [program.sourceB, program.layout, feeds]);

  // Keep the PREVIEW monitor showing the STAGED scene's source(s) (ADR 0081),
  // resolved by STABLE key like the program monitor (ADR 0042).
  useEffect(() => {
    const el = previewVideoRef.current;
    if (!el) return;
    const feed = preview.source ? feeds.find((f) => f.key === preview.source) : undefined;
    const track = feed ? tracksRef.current.get(feed.identity) : undefined;
    if (track) {
      try {
        track.attach(el);
      } catch {
        // no-op under jsdom
      }
    } else {
      try {
        el.srcObject = null;
      } catch {
        // no-op
      }
    }
  }, [preview.source, feeds]);
  useEffect(() => {
    const el = previewVideoRefB.current;
    if (!el) return;
    const key = isMultiHead(preview.layout) ? preview.sourceB : undefined;
    const feed = key ? feeds.find((f) => f.key === key) : undefined;
    const track = feed ? tracksRef.current.get(feed.identity) : undefined;
    if (track) {
      try {
        track.attach(el);
      } catch {
        // no-op under jsdom
      }
    } else {
      try {
        el.srcObject = null;
      } catch {
        // no-op
      }
    }
  }, [preview.sourceB, preview.layout, feeds]);

  // Slots C/D (ADR 0212 — 3/4-up + board-dominant layouts): keep each monitor showing
  // its assigned source's feed, resolved by STABLE key like A/B, and only when the
  // layout actually uses that slot (so switching to a smaller layout clears it).
  const attachSlotMonitor = (el: HTMLVideoElement | null, key: string | undefined) => {
    if (!el) return;
    const feed = key ? feeds.find((f) => f.key === key) : undefined;
    const track = feed ? tracksRef.current.get(feed.identity) : undefined;
    if (track) {
      try {
        track.attach(el);
      } catch {
        // no-op under jsdom
      }
    } else {
      try {
        el.srcObject = null;
      } catch {
        // no-op
      }
    }
  };
  useEffect(() => {
    const use = layoutSlots(program.layout).includes("c");
    attachSlotMonitor(programVideoRefC.current, use ? program.sourceC : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program.sourceC, program.layout, feeds]);
  useEffect(() => {
    const use = layoutSlots(program.layout).includes("d");
    attachSlotMonitor(programVideoRefD.current, use ? program.sourceD : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program.sourceD, program.layout, feeds]);
  useEffect(() => {
    const use = layoutSlots(preview.layout).includes("c");
    attachSlotMonitor(previewVideoRefC.current, use ? preview.sourceC : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview.sourceC, preview.layout, feeds]);
  useEffect(() => {
    const use = layoutSlots(preview.layout).includes("d");
    attachSlotMonitor(previewVideoRefD.current, use ? preview.sourceD : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview.sourceD, preview.layout, feeds]);

  const setTileEl = (identity: string, el: HTMLVideoElement | null) => {
    if (el) {
      tileElsRef.current.set(identity, el);
      const track = tracksRef.current.get(identity);
      if (track) {
        try {
          track.attach(el);
        } catch {
          // no-op under jsdom
        }
      }
    } else {
      tileElsRef.current.delete(identity);
    }
  };

  // ── operator-local monitor mixer (ADR 0085) ─────────────────────────────────
  // A channel defaults to volume 1, MUTED — the operator opts in to monitoring a
  // source so opening the console never dumps every mic at once.
  const channelOf = (key: string): MixChannel => mix[key] ?? { volume: 1, muted: true };
  // Effective element gain for a feed = master × channel (ADR 0106). Muted if the
  // channel OR master is muted; master-mute also pins the level to 0 so there is
  // genuinely no output, not merely a flagged-muted element. Clamped to [0,1]. All
  // LOCAL to the Director's ears — never the SFU.
  const applyGain = (el: HTMLAudioElement, key: string) => {
    const ch = channelOf(key);
    try {
      el.muted = ch.muted || masterMuted;
      el.volume = masterMuted ? 0 : Math.min(1, Math.max(0, master * ch.volume));
      // Nudge playback: a monitor element that autoplay-blocked (or paused) shows
      // its fader but stays silent (LSC-182). Once it's audible, make sure it's
      // actually playing. Best-effort — the promise rejects when a gesture is still
      // required (the Enable-monitor-audio button covers that) or under jsdom.
      if (!el.muted) void el.play?.()?.catch(() => {});
    } catch {
      // jsdom / older browsers — the monitor gain is cosmetic there
    }
  };
  // Unlock the desk monitor after a user gesture (ADR 0147/LSC-182), mirroring the
  // Fan's Enable-audio (ADR 0035): resume LiveKit's audio context, then (re)play +
  // re-gain every monitor element. LOCAL to the Director's ears — never the SFU.
  const enableMonitorAudio = async () => {
    const room = roomRef.current;
    try {
      await room?.startAudio();
    } catch {
      // ignore — the per-element play below still runs
    }
    audioElsRef.current.forEach((el, identity) => {
      const feed = feeds.find((f) => f.identity === identity);
      if (feed) applyGain(el, feed.key);
      void el.play?.()?.catch(() => {});
    });
    if (mountedRef.current) setMonitorAudioBlocked(room ? !room.canPlaybackAudio : false);
  };
  const setAudioEl = (feed: Feed, el: HTMLAudioElement | null) => {
    if (el) {
      audioElsRef.current.set(feed.identity, el);
      const track = audioTracksRef.current.get(feed.identity);
      if (track) {
        try {
          track.attach(el);
        } catch {
          // no-op under jsdom
        }
      }
      applyGain(el, feed.key);
    } else {
      audioElsRef.current.delete(feed.identity);
    }
  };

  // ── Director talkback (ADR 0213, LSC-423) — a PRIVATE Director→talent PTT audio
  // path. The Director publishes one mic track named `talkback` and restricts it at
  // the SFU to only the targeted talent (setTrackSubscriptionPermissions with
  // allParticipantsAllowed=false), so fans + the composite/program-render egress
  // recorder are excluded; it is never program state and never the fan/recording mix.
  // Best-effort throughout — a missing mic / mock room just makes talkback a no-op.
  const talkbackSignal = (active: boolean, target: TalkbackTarget | null) => {
    const room = roomRef.current;
    if (!room) return;
    const sig: TalkbackSignal = {
      type: "talkback",
      active,
      target: target && target.kind === "one" ? target.key : "all",
      by: "director",
    };
    try {
      room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(sig)), { reliable: true });
    } catch {
      // best-effort — the SFU permission is what actually gates who hears talkback
    }
  };
  // The ONE SFU subscription-permission state machine for BOTH the Director's mics
  // (ADR 0247). `setTrackSubscriptionPermissions` is participant-level (it governs ALL
  // the Director's published tracks at once), so the latch + the PTT must share it:
  //   • PTT held → allow ONLY the targeted talent (talkback private, public mic muted);
  //     this always wins, so talkback never reaches a non-target even if the public mic
  //     is on (acceptance #5).
  //   • else public mic On Air → allow ALL (fans + talent + the program-render egress
  //     subscribe the public mic; the idle talkback track is muted so opening it carries
  //     no private audio — and the instant PTT is keyed this narrows again).
  //   • else idle → allow NO ONE (talkback idle, public mic off — ADR 0213's default).
  // The recording is correct by construction: the program-render egress is a subscriber,
  // so it only captures the public mic while "allow all" holds (not during PTT or idle).
  const applyDirectorAudioPermissions = useCallback(
    (micOn: boolean, target: TalkbackTarget | null) => {
      const room = roomRef.current as unknown as {
        localParticipant?: { setTrackSubscriptionPermissions?: (all: boolean, perms: unknown[]) => void };
      } | null;
      const lp = room?.localParticipant;
      if (!lp?.setTrackSubscriptionPermissions) return;
      try {
        if (target) lp.setTrackSubscriptionPermissions(false, talkbackPermissions(target, feeds));
        else if (micOn) lp.setTrackSubscriptionPermissions(true, []);
        else lp.setTrackSubscriptionPermissions(false, []);
      } catch {
        // best-effort — a mock room / denied call just makes the control a no-op
      }
    },
    [feeds],
  );
  const ensureTalkbackTrack = useCallback(async () => {
    if (talkbackTrackRef.current) return talkbackTrackRef.current;
    const room = roomRef.current;
    if (!room) return null;
    try {
      const track = await createLocalAudioTrack();
      await room.localParticipant.publishTrack(track, { name: TALKBACK_TRACK_NAME });
      await track.mute(); // published but silent until a PTT press unmutes it
      talkbackTrackRef.current = track as unknown as typeof talkbackTrackRef.current;
      return talkbackTrackRef.current;
    } catch {
      return null; // no mic / denied — talkback simply unavailable
    }
  }, []);
  // Publish the PUBLIC on-air mic (ADR 0247) lazily on first use, muted. A SEPARATE
  // track from talkback (a distinct name) so the two planes never collide — at most one
  // is unmuted at a time (public when on air & not mid-PTT; talkback while PTT held), so
  // the Director never double-publishes their voice. Lets the error propagate so the
  // toggle can surface a mic failure (acceptance #8).
  const ensureOnAirTrack = useCallback(async () => {
    if (onAirTrackRef.current) return onAirTrackRef.current;
    const room = roomRef.current;
    if (!room) throw new Error("not connected");
    const track = await createLocalAudioTrack();
    await room.localParticipant.publishTrack(track, { name: DIRECTOR_ONAIR_TRACK });
    await track.mute(); // published silent; unmuted only while on air and not mid-PTT
    onAirTrackRef.current = track as unknown as typeof onAirTrackRef.current;
    return onAirTrackRef.current;
  }, []);
  // Latched "Director mic → On Air" toggle (ADR 0247): join / leave the fan Program mix.
  const toggleMicOnAir = useCallback(async () => {
    if (micOnAirRef.current) {
      // OFF — stop reaching fans + the recording immediately (acceptance #4).
      micOnAirRef.current = false;
      if (mountedRef.current) setMicOnAir(false);
      try {
        await onAirTrackRef.current?.mute();
      } catch {
        // best-effort
      }
      applyDirectorAudioPermissions(false, talkbackTargetRef.current);
    } else {
      try {
        await ensureOnAirTrack();
        micOnAirRef.current = true;
        if (mountedRef.current) {
          setMicOnAir(true);
          setMicErr(null);
        }
        applyDirectorAudioPermissions(true, talkbackTargetRef.current);
        // Unmute now ONLY if not mid-PTT — a held private cue keeps the public mic
        // suppressed; it un-suppresses on release (stopTalkback).
        if (!talkbackTargetRef.current) {
          try {
            await onAirTrackRef.current?.unmute();
          } catch {
            // best-effort
          }
        }
      } catch {
        micOnAirRef.current = false;
        if (mountedRef.current) {
          setMicOnAir(false);
          setMicErr("Microphone unavailable — check mic access.");
        }
      }
    }
  }, [applyDirectorAudioPermissions, ensureOnAirTrack]);
  const startTalkback = useCallback(
    async (target: TalkbackTarget) => {
      const track = await ensureTalkbackTrack();
      talkbackTargetRef.current = target;
      applyDirectorAudioPermissions(micOnAirRef.current, target); // permit only the target BEFORE unmuting
      if (mountedRef.current) setTalkbackTarget(target);
      // Suppress the PUBLIC mic while a private cue is held (restored on release) — the
      // same voice must not also ride the fan mix mid-cue (acceptance #5/#6).
      if (micOnAirRef.current) {
        try {
          await onAirTrackRef.current?.mute();
        } catch {
          // best-effort
        }
      }
      try {
        await track?.unmute();
      } catch {
        // best-effort
      }
      talkbackSignal(true, target);
    },
    [ensureTalkbackTrack, applyDirectorAudioPermissions],
  );
  const stopTalkback = useCallback(() => {
    void talkbackTrackRef.current?.mute().catch(() => {});
    talkbackTargetRef.current = null;
    applyDirectorAudioPermissions(micOnAirRef.current, null); // restore prior routing (public-on → all; else none)
    // Restore the public mic if it was latched on before the cue.
    if (micOnAirRef.current) void onAirTrackRef.current?.unmute().catch(() => {});
    if (mountedRef.current) setTalkbackTarget(null);
    talkbackSignal(false, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyDirectorAudioPermissions]);
  // Stop + unpublish BOTH Director mics when the console tears down (ADR 0213/0247).
  useEffect(
    () => () => {
      try {
        talkbackTrackRef.current?.stop();
      } catch {
        // best-effort
      }
      talkbackTrackRef.current = null;
      try {
        onAirTrackRef.current?.stop();
      } catch {
        // best-effort
      }
      onAirTrackRef.current = null;
    },
    [],
  );
  // Re-apply gains + (re)attach audio whenever the mix, master, or feeds change.
  useEffect(() => {
    feeds.forEach((f) => {
      const el = audioElsRef.current.get(f.identity);
      const track = audioTracksRef.current.get(f.identity);
      if (el && track) {
        try {
          track.attach(el);
        } catch {
          // no-op under jsdom
        }
      }
      if (el) applyGain(el, f.key);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feeds, mix, master, masterMuted]);

  const setChannelVolume = (key: string, volume: number) =>
    setMix((m) => ({ ...m, [key]: { volume, muted: m[key]?.muted ?? true } }));
  const setChannelMuted = (key: string, muted: boolean) =>
    setMix((m) => ({ ...m, [key]: { volume: m[key]?.volume ?? 1, muted } }));

  // Push program state both ways: a LiveKit data message (instant fan-out to
  // connected viewers) and the API (persisted for anyone who joins later).
  const push = useCallback(
    async (next: ProgramState) => {
      // Always carry an explicit `live` (false until the Director's TAKE sets it true, ADR
      // 0149). A relayed staged update that merely OMITS `live` replaces the fan's program
      // wholesale, so a client that reads absent-vs-false on purpose (Android) would re-arm
      // its frame heuristic and leak pre-TAKE (truncated pre-roll LSC-443 / early audio
      // LSC-440). Mirrors the server-side viewerProgram normalization (#359) on the publish
      // side, so every delivery path (data channel + PUT) carries the field.
      if (next.live === undefined) next = { ...next, live: false };
      programRef.current = next;
      setProgram(next);
      setPushError(null);
      const room = roomRef.current;
      try {
        if (room) {
          const payload = new TextEncoder().encode(
            JSON.stringify({ type: "program", state: next }),
          );
          room.localParticipant.publishData(payload, { reliable: true });
        }
        if (broadcast) await setBroadcastProgram(broadcast.id, next);
      } catch (err: unknown) {
        if (mountedRef.current) setPushError(msg(err));
      }
    },
    [broadcast],
  );

  // Stage a change to the next scene (program bus) — local only, PROGRAM untouched
  // until a TAKE (ADR 0081). Merge onto the current staged scene.
  const stageScene = useCallback((patch: ProgramState) => {
    const next = { ...previewRef.current, ...patch };
    previewRef.current = next;
    if (mountedRef.current) setPreview(next);
  }, []);

  // Apply a SCENE to PROGRAM (ADR 0081): overlay the scene's source/layout/slots/overlay
  // onto the CURRENT live program (preserving the live chroma / hold / lifecycle flags),
  // carrying a transition hint for a timed take. CUT = instant (no hint). The fan animates
  // the swap client-side (ADR 0082). Shared by the TAKE bar (from the staged preview) and
  // the Run of Show (from a saved cue — ADR 0122), so a cue applies exactly like a take.
  const applyScene = useCallback(
    async (scene: ProgramState, type: TransitionType) => {
      const next: ProgramState = { ...programRef.current, ...scene };
      // Telestration guard (ADR 0116/0045): the overlay is committed by TAKE. If we are
      // taking a whiteboard that is ALREADY on program, preserve the LIVE strokes so a
      // TAKE (e.g. changing the board template/layout) never clobbers the Legend's
      // in-progress drawing — only a brand-new whiteboard uses the staged (empty) strokes.
      if (
        next.overlay?.kind === "whiteboard" &&
        programRef.current.overlay?.kind === "whiteboard"
      ) {
        next.overlay = { ...next.overlay, strokes: programRef.current.overlay.strokes ?? next.overlay.strokes };
      }
      // Seed the dueling per-slot boards on the take so each publisher's editor has
      // a template (ADR 0054); the templates + strokes then live on program.boards.
      if (next.layout === "dueling" && (!next.boards?.a || !next.boards?.b)) {
        next.boards = {
          a: { board: next.boards?.a?.board ?? "blank", strokes: next.boards?.a?.strokes ?? [] },
          b: { board: next.boards?.b?.board ?? "blank", strokes: next.boards?.b?.strokes ?? [] },
        };
      }
      if (type === "cut") delete next.transition;
      else next.transition = { type, ms: durationMs };
      // Live begins at the Director's TAKE (LSC-268 / iOS ADR 0014): mark the program
      // live-to-fans on any TAKE. The legend merely publishing (warming up) never sets
      // this, so fans hold on the pre-start slate until the Director actually takes the
      // show on air. Cleared for free by the fresh-go-live program reset ({}).
      next.live = true;
      setTaking(true);
      try {
        await push(next);
      } finally {
        if (mountedRef.current) {
          setTaking(false);
          setTbar(0);
          // Re-sync the preview to what's now live (nothing staged after a take).
          const scene2 = sceneOf(next);
          previewRef.current = scene2;
          setPreview(scene2);
        }
      }
    },
    [durationMs, push],
  );
  // TAKE the staged preview scene to PROGRAM (ADR 0081).
  const take = useCallback((type: TransitionType) => applyScene(sceneOf(previewRef.current), type), [applyScene]);

  // Fade to black (ADR 0081/0082) — a LIVE program action (not staged): toggle the
  // opaque `blackout` flag on the current program so fans fade to/from black.
  const toggleBlackout = () => {
    void push({ ...programRef.current, blackout: !programRef.current.blackout });
  };

  // Chroma-key background OVERRIDE (ADR 0070). The Director sets program.chroma
  // with by:"director"; the relay reaches the Legend's client, which re-composites
  // its camera over the chosen background. Same opaque program state as the Legend
  // sets — the Director's push simply wins (last writer). null clears it.
  const chroma = program.chroma;
  const setDirectorChroma = (patch: Partial<NonNullable<ProgramState["chroma"]>> | null) => {
    const cur = programRef.current;
    const next: ProgramState = { ...cur };
    if (patch === null) delete next.chroma;
    else next.chroma = { ...(cur.chroma ?? {}), ...patch, by: "director" };
    void push(next);
  };
  // Debounced tune (continuous controls) — reflect locally now, relay after a beat.
  const tuneDirectorChroma = (patch: Partial<NonNullable<ProgramState["chroma"]>>) => {
    const cur = programRef.current;
    const next: ProgramState = { ...cur, chroma: { ...(cur.chroma ?? {}), ...patch, by: "director" } };
    programRef.current = next;
    setProgram(next);
    if (dirTuneTimerRef.current) clearTimeout(dirTuneTimerRef.current);
    dirTuneTimerRef.current = setTimeout(() => void push(next), 400);
  };
  const selectDirectorBackground = (id: string) => setDirectorChroma({ backgroundId: id, enabled: true });
  const toggleDirectorBackground = () => {
    const first = chroma?.backgroundId || backgrounds[0]?.id;
    if (!chroma?.enabled && !first) return;
    setDirectorChroma({ enabled: !chroma?.enabled, backgroundId: chroma?.backgroundId || first });
  };
  const onDirUploadBackground = async (file: File) => {
    try {
      const bg = await uploadBackground(caveId, file);
      if (!mountedRef.current) return;
      await fetchCaveBackgrounds(caveId)
        .then((bs) => mountedRef.current && setBackgrounds(bs))
        .catch(() => undefined);
      setDirectorChroma({ backgroundId: bg.id, enabled: true });
    } catch (err: unknown) {
      if (mountedRef.current) setPushError(msg(err));
    }
  };
  const onDirRemoveBackground = async (id: string) => {
    try {
      await deleteBackground(caveId, id);
      if (!mountedRef.current) return;
      if (chroma?.backgroundId === id) setDirectorChroma(null);
      fetchCaveBackgrounds(caveId)
        .then((bs) => mountedRef.current && setBackgrounds(bs))
        .catch(() => undefined);
    } catch (err: unknown) {
      if (mountedRef.current) setPushError(msg(err));
    }
  };
  // Eyedropper: sample the key color from the ON-PROGRAM tile (the Legend's feed
  // the Director is watching). Best with chroma off / a raw feed showing the key.
  const sampleFromProgramTile = (clientX: number, clientY: number) => {
    const feed = program.source ? feeds.find((f) => f.key === program.source) : undefined;
    const el = feed ? tileElsRef.current.get(feed.identity) : undefined;
    if (el) {
      const hex = sampleVideoColor(el, clientX, clientY);
      if (hex) tuneDirectorChroma({ color: hex, enabled: true });
    }
    setEyedropper(false);
  };

  // Default slot assignment (ADR 0062). In a multi-head layout, once a GUEST is a
  // live publisher, default the Legend (first non-guest) to slot A and the guest to
  // slot B, so the Director needn't hand-assign every time. Only fills an EMPTY
  // slot — a manual A/B override still wins — and a reconnecting guest keeps its
  // slot for free because `sourceB` holds the STABLE key (ADR 0042), which
  // re-resolves to the fresh identity. Gated on a guest being present so a
  // dual-of-two-legends layout is still hand-assigned.
  useEffect(() => {
    const slots = layoutSlots(preview.layout);
    if (slots.length < 2) return; // single is handled by changeProgramLayout
    const guest = feeds.find((f) => isGuestIdentity(f.identity));
    if (!guest) return; // no guest yet — a dual-of-two-legends layout stays hand-assigned
    const legend = feeds.find((f) => !isGuestIdentity(f.identity));
    // Fill order: the Legend takes slot A (the dominant pane in the *-large layouts),
    // then the other publishers (guests + any extra) in join order fill B/C/D. Only
    // EMPTY slots are filled — a manual assignment always wins — and no publisher is
    // placed in two slots. A reconnecting publisher keeps its slot for free (stable
    // key, ADR 0042). This extends the two-person default (ADR 0062) to 3/4-up.
    const order = [legend, ...feeds.filter((f) => f !== legend)].filter(Boolean) as typeof feeds;
    let next = preview;
    const taken = new Set(slots.map((s) => slotSource(next, s)).filter(Boolean) as string[]);
    let oi = 0;
    for (const slot of slots) {
      if (slotSource(next, slot)) continue; // keep an existing/manual assignment
      while (oi < order.length && taken.has(order[oi].key)) oi++;
      if (oi >= order.length) break;
      const key = order[oi++].key;
      taken.add(key);
      next = { ...next, ...slotSourcePatch(slot, key) };
    }
    if (next !== preview) stageScene(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feeds, preview.layout, preview.source, preview.sourceB, preview.sourceC, preview.sourceD]);

  // Keep the staged-slot target valid: switching to a smaller layout (e.g. quad → dual)
  // must not strand stageSlot on a slot the layout no longer has (ADR 0212).
  useEffect(() => {
    if (!layoutSlots(preview.layout).includes(stageSlot)) setStageSlot("a");
  }, [preview.layout, stageSlot]);

  // Stage a feed to the PREVIEW bus by its STABLE key (username), not its per-join
  // identity (ADR 0042) — so the selection re-binds automatically if that publisher
  // drops and rejoins. PROGRAM is untouched until a TAKE (ADR 0081).
  const selectSource = (key: string) => {
    stageScene({ source: key });
  };

  // Multi-head program layouts (ADR 0054). MVP multi-speaker is switch-not-composed
  // (ADR 0044): the two heads are composited CLIENT-SIDE by every viewer, no
  // server-side egress. `source` is slot A, `sourceB` is slot B; both are STABLE
  // keys so a reconnecting publisher stays in their slot (ADR 0042).
  // Layout is STAGED on the preview bus (ADR 0081) — the composition changes for
  // fans only on a TAKE. Program-monitor composition reads program.layout; the
  // Compose controls read the staged preview.layout.
  const programMulti = isMultiHead(program.layout);
  const previewMulti = isMultiHead(preview.layout);
  // Which head slot (a/b/c/d) a publisher key occupies on a bus, or null — covers
  // single (slot A only) and every multi-head layout (ADR 0212). Drives the SOURCES
  // rack PGM/PVW tally + the per-feed slot badge.
  const programSlotForKey = (key: string): SourceSlot | null =>
    (layoutSlots(program.layout).find((s) => slotSource(program, s) === key) ?? null);
  const previewSlotForKey = (key: string): SourceSlot | null =>
    (layoutSlots(preview.layout).find((s) => slotSource(preview, s) === key) ?? null);
  const changeProgramLayout = (l: ProgramLayout) => {
    const next: ProgramState = { ...previewRef.current, layout: l };
    // Collapsing to a single source must NEVER blank Program (ADR 0062): default
    // slot A to the Legend (the first non-guest publisher) when it's unset, so the
    // one full-frame source is the Legend by our two-person convention. A source
    // already staged (a deliberate slot-A pick) is preserved.
    if (l === "single" && !next.source) {
      const legend = feeds.find((f) => !isGuestIdentity(f.identity));
      if (legend) next.source = legend.key;
    }
    stageScene(next);
  };
  // Stage a live publisher to a preview slot by STABLE key (ADR 0042/0054/0212).
  const assignSlot = (slot: SourceSlot, key: string) => {
    stageScene(slotSourcePatch(slot, key));
  };
  // Set a dueling slot's board template (ADR 0045/0054) — LIVE, like the strokes:
  // the assigned publisher draws on it immediately, and the DataReceived fold merges
  // their board into program.boards, so the template rides the live board, not the
  // staged scene.
  const changeDuelBoard = (slot: DuelSlot, board: BoardId) => {
    const boards = { ...(programRef.current.boards ?? {}) };
    boards[slot] = { ...(boards[slot] ?? {}), board };
    void push({ ...programRef.current, boards });
  };
  // Clear a dueling slot's strokes (ADR 0045/0054) — the Director-side counterpart
  // to the shared-whiteboard Clear (drawWhiteboard([])), for the per-slot Dueling
  // boards (LSC-503). LIVE like changeDuelBoard: preserves the slot's board template
  // AND the other slot (only this slot's strokes reset), relayed + persisted via push.
  const clearDuelBoard = (slot: DuelSlot) => {
    const boards = { ...(programRef.current.boards ?? {}) };
    boards[slot] = { ...(boards[slot] ?? {}), strokes: [] };
    void push({ ...programRef.current, boards });
  };

  // Production hold (ADR 0055) — the director toggles the fan-facing program hold
  // ON BEHALF OF the legend: mute program audio and/or pause program video (which
  // pops the rotating hold playlist for fans). Each does BOTH: an instant data
  // relay AND the authoritative server endpoint (real SFU track mute + persist).
  // The director's own monitor is a subscriber too, so a server mute affects it —
  // accepted for MVP (ADR 0055). Pause offers a timed auto-resume.
  const relayHoldLocal = (next: ProgramState) => {
    programRef.current = next;
    setProgram(next);
    const room = roomRef.current;
    if (room) {
      room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify({ type: "program", state: next })),
        { reliable: true },
      );
    }
  };
  const toggleAudioMuted = () => {
    if (!broadcast) return;
    const nextOn = !programRef.current.audioMuted;
    const next: ProgramState = { ...programRef.current, audioMuted: nextOn };
    if (!nextOn && !next.videoPaused) delete next.resumeAt;
    relayHoldLocal(next);
    (nextOn ? muteBroadcast(broadcast.id) : unmuteBroadcast(broadcast.id)).catch((err: unknown) => {
      if (mountedRef.current) setPushError(msg(err));
    });
  };
  const toggleVideoPaused = () => {
    if (!broadcast) return;
    const nextOn = !programRef.current.videoPaused;
    const next: ProgramState = { ...programRef.current, videoPaused: nextOn };
    if (nextOn && pauseSeconds > 0) next.resumeAt = Date.now() + pauseSeconds * 1000;
    if (!nextOn && !next.audioMuted) delete next.resumeAt;
    relayHoldLocal(next);
    (nextOn ? pauseBroadcast(broadcast.id, pauseSeconds || undefined) : resumeBroadcast(broadcast.id)).catch(
      (err: unknown) => {
        if (mountedRef.current) setPushError(msg(err));
      },
    );
  };

  // The program source is set but no live feed currently matches it — an
  // involuntary drop (ADR 0042). We KEEP program.source set so the rejoin
  // auto-restores; distinct from standby/ended (voluntary).
  const programSourceLost =
    !!program.source &&
    !program.standby &&
    !program.ended &&
    !feeds.some((f) => f.key === program.source);

  // What is actually LIVE on Fan Program right now (source of truth for the ON
  // AIR chips). Distinct from stagedKind, which is what the operator is preparing.
  const liveOverlay = program.overlay;
  const liveKind: OverlayKind = liveOverlay?.kind ?? "none";
  const whiteboardOnAir = liveKind === "whiteboard";
  const adOnAir = liveKind === "ad" && !!liveOverlay?.adId;
  // A lower third is on air only once viewers would actually render it (non-empty).
  const lowerThirdOnAir = liveKind === "lower-third" && !!(liveOverlay?.title || liveOverlay?.subtitle);
  const lowerThirdHasText = !!(l3Title.trim() || l3Subtitle.trim());

  // ── overlay STAGING (ADR 0063) ──────────────────────────────────────────────
  // Configuring an overlay is OFF AIR: selecting the kind, a board template, a
  // layout, or typing a lower third stages into the pane and NEVER touches Fan
  // Program. Program.overlay changes only on an explicit Send to Program / Take
  // off air. This aligns lower-third + whiteboard/telestration with the ad
  // Play/End-break flow, which already worked this way.

  // Select the overlay kind to prepare — staging only, no relay.
  const stageKind = (kind: OverlayKind) => setStagedKind(kind);

  // Board + layout selection STAGE (the gate is on changing the board — ADR 0063)
  // — no relay until Take Live, even while the whiteboard is already on air.
  const stageBoard = (b: BoardId) => setWbBoard(b);
  const stageLayout = (l: WhiteboardLayout) => setWbLayout(l);

  // Whiteboard drawing. Off air, strokes stay in the local draft. Once the
  // whiteboard is ON AIR the ongoing strokes of that live board relay live —
  // that's telestration (ADR 0045), and NOT gated. We preserve the LIVE overlay's
  // board/layout on the relay so a staged-but-not-taken board change never rides
  // out on a stroke.
  const drawWhiteboard = (next: WhiteboardStroke[]) => {
    setStrokes(next);
    const live = programRef.current.overlay;
    if (live?.kind === "whiteboard") {
      void push({ ...programRef.current, overlay: { ...live, strokes: next } });
    }
  };

  // The Stream Deck (a live-switching surface) puts the CONFIGURED whiteboard on
  // program IMMEDIATELY — ADR 0116: the tabbed UI stages into preview + commits on
  // the program TAKE, but a physical deck button acts live like a real switcher.
  const takeWhiteboardLive = () => {
    void push({ ...programRef.current, overlay: { kind: "whiteboard", strokes, layout: wbLayout, board: wbBoard } });
  };

  // Direct clear of whatever overlay is on Fan Program — used by the deck + the
  // lower-third auto-out timer (a system action, immediate). The tabbed UI stages a
  // clear via stageOverlayOff + the program TAKE instead.
  const takeOverlayOffAir = () => {
    if (autoOutTimerRef.current) clearTimeout(autoOutTimerRef.current);
    void push({ ...programRef.current, overlay: { kind: "none" } });
  };

  // Stage the graphics bus OFF in the preview (ADR 0116) — the next program TAKE
  // removes the overlay. (The immediate clear is takeOverlayOffAir, deck/auto-out only.)
  const stageOverlayOff = () => setStagedKind("none");

  // ── run-of-show cues (ADR 0088; overlays/ads captured — ADR 0122) ────────────
  // A short human label for a cue's graphic overlay (ad / lower-third / whiteboard).
  const overlayLabel = (ov?: ProgramOverlay): string => {
    if (!ov || ov.kind === "none") return "";
    if (ov.kind === "ad") return `Ad: ${ov.adName || ads.find((a) => a.id === ov.adId)?.name || "break"}`;
    if (ov.kind === "lower-third") return `Lower third: ${ov.title || ov.subtitle || "—"}`;
    if (ov.kind === "whiteboard") return `Whiteboard: ${ov.board || "board"}`;
    return "";
  };
  // Cue name derived from what it actually shows — the graphic if any, else the scene.
  const cueName = (scene: ProgramState): string => {
    const gfx = overlayLabel(scene.overlay);
    if (gfx) return gfx;
    const layout = scene.layout ?? "single";
    if (layout === "single") return `Cam · ${scene.source || "source"}`;
    const extra = [scene.sourceB, scene.sourceC, scene.sourceD].filter(Boolean);
    return `${layout} · ${scene.source || "—"}${extra.length ? ` + ${extra.join(" + ")}` : ""}`;
  };
  // Capture the WHOLE staged scene (source/sourceB/layout/overlay via sceneOf), so a
  // cue can be a camera scene, a lower third, a whiteboard, or an ad break (ADR 0122).
  const addCue = () => {
    const n = ++cueSeqRef.current;
    const scene = sceneOf(previewRef.current);
    setRundown((r) => [...r, { id: `cue-${n}`, name: cueName(scene), scene }]);
  };
  const removeCue = (id: string) => setRundown((r) => r.filter((c) => c.id !== id));
  // Load a cue's overlay back into the editing buffers so the PREVIEW composes it and a
  // subsequent manual edit/TAKE derives the same overlay (the stagedOverlay effect keys
  // off these). Mirrors the bootstrap seed (ADR 0116).
  const loadOverlayBuffers = (ov?: ProgramOverlay) => {
    if (!ov || ov.kind === "none") { setStagedKind("none"); return; }
    if (ov.kind === "ad") { if (ov.adId) setSelectedAdId(ov.adId); setStagedKind("ad"); return; }
    if (ov.kind === "lower-third") {
      setL3Title(ov.title ?? "");
      setL3Subtitle(ov.subtitle ?? "");
      if (ov.accent) setL3Accent(ov.accent);
      if (ov.position) setL3Position(ov.position);
      setStagedKind("lower-third");
      return;
    }
    if (ov.kind === "whiteboard") {
      setWbBoard(ov.board ?? "blank");
      if (ov.layout) setWbLayout(ov.layout);
      setStrokes(ov.strokes ?? []);
      setStagedKind("whiteboard");
      return;
    }
  };
  const cueToPreview = (c: Cue) => {
    stageScene({ source: c.scene.source, sourceB: c.scene.sourceB, sourceC: c.scene.sourceC, sourceD: c.scene.sourceD, layout: c.scene.layout, overlay: c.scene.overlay });
    loadOverlayBuffers(c.scene.overlay);
  };
  const takeCue = (c: Cue) => {
    cueToPreview(c); // reflect in preview + load the buffers for any follow-on editing
    void applyScene(c.scene, "cut"); // apply the cue's scene directly (no stage-effect race)
  };
  // A stable scene SIGNATURE for LIVE/NEXT matching — ignores live-changing strokes so a
  // whiteboard cue still reads "LIVE" while the Legend keeps drawing on it.
  const overlaySig = (ov?: ProgramOverlay): string => {
    if (!ov || ov.kind === "none") return "none";
    if (ov.kind === "ad") return `ad:${ov.adId ?? ""}`;
    if (ov.kind === "lower-third") return `lt:${ov.title ?? ""}|${ov.subtitle ?? ""}`;
    if (ov.kind === "whiteboard") return `wb:${ov.board ?? ""}|${ov.layout ?? ""}`;
    return ov.kind;
  };
  const sceneSig = (s: ProgramState): string =>
    `${s.layout ?? "single"}|${s.source ?? ""}|${s.sourceB ?? ""}|${s.sourceC ?? ""}|${s.sourceD ?? ""}|${overlaySig(s.overlay)}`;
  const cueMatches = (c: Cue, s: ProgramState) => sceneSig(c.scene) === sceneSig(sceneOf(s));

  // Build the ProgramOverlay for a library ad — shared by the staged preview and
  // the explicit Play ad (the ad's take-live).
  const adOverlayFor = (adId: string): ProgramOverlay | null => {
    const ad = ads.find((a) => a.id === adId);
    if (!ad) return null;
    return ad.kind === "video"
      ? { kind: "ad", adId: ad.id, adName: ad.name, adVideoUrl: ad.videoUrl }
      : {
          kind: "ad",
          adId: ad.id,
          adName: ad.name,
          adImageUrl: ad.imageUrl || undefined, // uploaded slate graphic (ADR 0025)
          adSlate: { brand: ad.brand, offer: ad.offer, tier: ad.tier },
        };
  };
  // The staged ad (for the off-air preview); null when nothing is selected.
  // Memoized so the staged-overlay derivation (ADR 0116) has a STABLE ad reference —
  // adOverlayFor builds a fresh object each call, which would otherwise churn the
  // staging effect every render (the seeded ad is auto-selected on load).
  const stagedAd = useMemo(() => adOverlayFor(selectedAdId), [selectedAdId, ads]);

  // Play the selected ad full-frame (the ad's Take Live); the legend goes off-air
  // until it clears.
  // Arm the selected ad into the preview bus (ADR 0116) — the program TAKE plays it.
  const armAd = (adId: string) => {
    setSelectedAdId(adId);
    setStagedKind("ad");
  };

  // Stage the derived graphics-bus overlay into preview.overlay (ADR 0116) so the
  // PREVIEW monitor composes it and the single program TAKE commits it — off-air to
  // fans until then. Keeps the config buffers (stagedKind + the lower-third/board/ad
  // fields) as the editing state; this derives + stages the overlay object from them.
  const stagedOverlay: ProgramOverlay = useMemo(() => {
    switch (stagedKind) {
      case "whiteboard":
        return { kind: "whiteboard", strokes, layout: wbLayout, board: wbBoard };
      case "lower-third":
        return lowerThirdHasText
          ? { kind: "lower-third", title: l3Title, subtitle: l3Subtitle, accent: l3Accent, position: l3Position }
          : { kind: "none" };
      case "ad":
        return stagedAd ?? { kind: "none" };
      default:
        return { kind: "none" };
    }
  }, [stagedKind, strokes, wbLayout, wbBoard, l3Title, l3Subtitle, l3Accent, l3Position, stagedAd, lowerThirdHasText]);
  const lastStagedOverlayRef = useRef<string>("");
  useEffect(() => {
    // Idempotent: only re-stage when the overlay CONTENT changes, so a churning
    // derivation can never loop setPreview → re-render → setPreview.
    const key = JSON.stringify(stagedOverlay);
    if (key === lastStagedOverlayRef.current) return;
    lastStagedOverlayRef.current = key;
    stageScene({ overlay: stagedOverlay });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stagedOverlay]);

  // Lower-third auto-out (ADR 0088): once a lower third is actually ON PROGRAM (after
  // a TAKE), arm a timer that clears it directly after N seconds. Auto-out is a system
  // action, so it clears program immediately rather than staging.
  useEffect(() => {
    if (program.overlay?.kind !== "lower-third" || autoOutS <= 0) return;
    if (autoOutTimerRef.current) clearTimeout(autoOutTimerRef.current);
    autoOutTimerRef.current = setTimeout(() => takeOverlayOffAir(), autoOutS * 1000);
    return () => {
      if (autoOutTimerRef.current) clearTimeout(autoOutTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program.overlay?.kind, autoOutS]);

  const onUploadAd = (kind: "video" | "image") => async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    setPushError(null);
    try {
      const ad = kind === "video" ? await uploadVideoAd(caveId, file) : await uploadSlateAd(caveId, file);
      if (!mountedRef.current) return;
      setAds((list) => [ad, ...list]);
      setSelectedAdId(ad.id);
    } catch (err: unknown) {
      if (mountedRef.current) setPushError(msg(err));
    } finally {
      if (mountedRef.current) setUploading(false);
    }
  };

  // Stop into a recoverable standby (ADR 0039): mark it stopped server-side and
  // relay standby to viewers, so they see a "paused" slate rather than the end
  // slate. The console stays open — the legend going live again resumes the show.
  const stopShow = async () => {
    if (!broadcast) return;
    setStopping(true);
    try {
      await stopBroadcast(broadcast.id);
      const room = roomRef.current;
      if (room) {
        room.localParticipant.publishData(
          new TextEncoder().encode(JSON.stringify({ type: "program", state: { standby: true } })),
          { reliable: true },
        );
      }
      if (mountedRef.current) setProgram((p) => ({ ...p, standby: true }));
    } catch (err: unknown) {
      if (mountedRef.current) setPushError(msg(err));
    } finally {
      if (mountedRef.current) setStopping(false);
    }
  };

  // End the whole broadcast (ADR 0025): mark it ended server-side and relay the
  // end state to viewers, then close the console.
  const endShow = async () => {
    if (!broadcast) return;
    setEnding(true);
    // Relay the END intent FIRST so a fan never flashes the involuntary "Signal
    // lost" slate for a deliberate end (ADR 0062) — the on-program source (usually
    // the Legend) may drop around the same moment. The persisted end state + the
    // fan-side grace cover the residual ordering race.
    const room = roomRef.current;
    if (room) {
      room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify({ type: "program", state: { ended: true } })),
        { reliable: true },
      );
    }
    // Reflect ENDED locally so the console STAYS on an ended state (the header shows
    // ENDED) rather than yanking the operator out — leaving is the explicit Exit
    // button now, mirroring the Legend Go-Live. (Was: onClose on end.)
    programRef.current = { ...programRef.current, ended: true };
    if (mountedRef.current) setProgram(programRef.current);
    try {
      await endBroadcast(broadcast.id);
    } catch (err: unknown) {
      if (mountedRef.current) setPushError(msg(err));
    } finally {
      if (mountedRef.current) setEnding(false);
    }
  };

  const addSponsorSlate = async () => {
    setPushError(null);
    try {
      const ad = await createSlateAd(caveId, {
        name: "Sponsor slate",
        brand: "Our Sponsor",
        offer: "Support the Cave",
        tier: "Sponsored",
      });
      if (!mountedRef.current) return;
      setAds((list) => [ad, ...list]);
      setSelectedAdId(ad.id);
    } catch (err: unknown) {
      if (mountedRef.current) setPushError(msg(err));
    }
  };

  const fanQuestions = chat.messages.filter((message) => message.question);
  const sentMessageIds = new Set(sentQuestions.questions.map((question) => question.messageId));
  const selectedQuestion = fanQuestions.find((question) => question.id === selectedQuestionId) ?? null;
  const sendSelectedQuestion = async () => {
    if (!selectedQuestion || sendingQuestion) return;
    setSendingQuestion(true);
    setQuestionError(null);
    try {
      await sentQuestions.send(selectedQuestion.id);
    } catch (err: unknown) {
      if (mountedRef.current) setQuestionError(msg(err));
    } finally {
      if (mountedRef.current) setSendingQuestion(false);
    }
  };

  // Load this broadcast's guest invites (best-effort; a 404/older API is a no-op
  // so the rest of the console still works).
  const loadGuestInvites = useCallback((broadcastId: string) => {
    listGuestInvites(broadcastId)
      .then((list) => mountedRef.current && setGuestInvites(list))
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    if (broadcast) loadGuestInvites(broadcast.id);
  }, [broadcast, loadGuestInvites]);

  const inviteGuest = async () => {
    if (!broadcast || invitingGuest) return;
    setInvitingGuest(true);
    setGuestError(null);
    try {
      await createGuestInvite(broadcast.id, guestLabel.trim());
      if (!mountedRef.current) return;
      setGuestLabel("");
      loadGuestInvites(broadcast.id);
    } catch (err: unknown) {
      if (mountedRef.current) setGuestError(msg(err));
    } finally {
      if (mountedRef.current) setInvitingGuest(false);
    }
  };

  const revokeGuest = async (invCode: string) => {
    if (!broadcast) return;
    setGuestError(null);
    try {
      await revokeGuestInvite(broadcast.id, invCode);
      if (mountedRef.current) loadGuestInvites(broadcast.id);
    } catch (err: unknown) {
      if (mountedRef.current) setGuestError(msg(err));
    }
  };

  const activeGuestInvites = guestInvites.filter((inv) => !inv.revoked);

  // Build a branded QR (ADR 0141) for each active invite's join link so a guest can
  // scan it from their phone. Best-effort + cached by code — a failed QR just leaves
  // the link (which still works). Keyed on the set of active codes so a new invite
  // gets a QR and revoked ones stop being recomputed.
  const activeGuestCodes = activeGuestInvites.map((inv) => inv.code).join(",");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const inv of activeGuestInvites) {
        if (guestQrs[inv.code]) continue;
        try {
          const qr = await brandedQrDataUrl(inv.joinUrl, { size: 220 });
          if (cancelled) return;
          setGuestQrs((m) => (m[inv.code] ? m : { ...m, [inv.code]: qr }));
        } catch {
          // best-effort — the link + copy still work without the QR
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGuestCodes]);

  // Copy a guest join link to the clipboard, with a brief per-invite confirmation.
  const copyGuestLink = async (url: string, code: string) => {
    try {
      await navigator.clipboard.writeText(url);
      if (!mountedRef.current) return;
      setCopiedGuest(code);
      setTimeout(() => {
        if (mountedRef.current) setCopiedGuest((c) => (c === code ? null : c));
      }, 1600);
    } catch {
      // clipboard unavailable (permissions / older browser) — the link stays selectable
    }
  };

  // Whether Fan Program is actually on air (source of truth for the ON-AIR chips
  // and the deck lifecycle key). Distinct from standby/ended (voluntary) and from
  // programSourceLost (an involuntary drop we keep source set through).
  const onAir = connected && !!program.source && !program.standby && !program.ended && !programSourceLost;

  // ── Stream Deck bridge (ADR 0024, studio-grade ADR 0096) ────────────────────
  // The Director drives the whole show from a paired deck: TAKE, FTB, HOLD, the
  // next rundown block, staged layout changes, the fan-facing program mute, and
  // per-source monitor levels on the dials. The console stays authoritative — the
  // bridge publishes rich state (so the plugin paints keys/dials) and long-polls
  // for presses/rotations; a failed bridge never blocks the on-screen controls.
  const [deckPairings, setDeckPairings] = useState<DeckPairing[]>([]);
  const [pairCode, setPairCode] = useState<string | null>(null);
  const [pairingStarted, setPairingStarted] = useState(false);
  const loadPairings = useCallback(() => {
    deckListPairings()
      .then((r) => mountedRef.current && setDeckPairings(r.pairings))
      .catch(() => undefined);
  }, []);
  useEffect(() => loadPairings(), [loadPairings]);

  // The next block in the lineup (ADR 0096): the cue after whichever currently
  // matches PROGRAM, wrapping to the top; the first cue when none is live.
  const currentCueIdx = rundown.findIndex((c) => cueMatches(c, program));
  const nextCueObj = rundown.length
    ? currentCueIdx >= 0
      ? rundown[(currentCueIdx + 1) % rundown.length]
      : rundown[0]
    : null;
  const pendingQuestions = fanQuestions.filter((q) => !sentMessageIds.has(q.id)).length;

  // The three audio dials (ADR 0096): rotate = operator-local monitor level, press
  // = fan-facing program mute (ADR 0055). Master + the two on-program slots.
  const deckAudioChannel = (key: string | undefined, name: string): DeckAudioChannel | undefined => {
    if (!key) return undefined;
    return { name, level: Math.round((channelOf(key).volume ?? 0) * 100), muted: !!program.audioMuted };
  };
  const sourceAName = feeds.find((f) => f.key === program.source)?.name;
  const sourceBName = feeds.find((f) => f.key === program.sourceB)?.name;
  const whiteboardLiveNow = program.overlay?.kind === "whiteboard";

  // Latest handlers + derived state for the single deck-intent dispatcher, so a
  // press always acts on current program/preview/mix without re-subscribing the
  // long-poll loop (mirrors the Legend Go-Live pattern).
  const clamp01 = (n: number) => Math.max(0, Math.min(1, n));
  const deckRef = useRef({
    take, toggleBlackout, toggleVideoPaused, toggleAudioMuted, takeOverlayOffAir,
    takeWhiteboardLive, endShow, changeProgramLayout, setMaster, setMix, channelOf,
    sentQuestions, fanQuestions, sentMessageIds, selectedQuestion, setSelectedQuestionId,
    transitionType, nextCueObj, takeCue, program, whiteboardLiveNow,
  });
  deckRef.current = {
    take, toggleBlackout, toggleVideoPaused, toggleAudioMuted, takeOverlayOffAir,
    takeWhiteboardLive, endShow, changeProgramLayout, setMaster, setMix, channelOf,
    sentQuestions, fanQuestions, sentMessageIds, selectedQuestion, setSelectedQuestionId,
    transitionType, nextCueObj, takeCue, program, whiteboardLiveNow,
  };
  const LAYOUT_CYCLE: ProgramLayout[] = ["single", "dual", "dual-board", "dueling"];
  const handleDeckAction = useCallback((action: DeckAction) => {
    const d = deckRef.current;
    // Parameterized intents first (ADR 0096 string encoding).
    if (action.startsWith("layout:")) {
      d.changeProgramLayout(action.slice("layout:".length) as ProgramLayout);
      return;
    }
    if (action.startsWith("layout-cycle:")) {
      const dir = Number(action.slice("layout-cycle:".length)) || 1;
      const cur = (d.program.layout ?? "single") as ProgramLayout;
      const i = LAYOUT_CYCLE.indexOf(cur);
      const base = i < 0 ? 0 : i;
      const next = LAYOUT_CYCLE[(base + (dir > 0 ? 1 : LAYOUT_CYCLE.length - 1)) % LAYOUT_CYCLE.length];
      d.changeProgramLayout(next);
      return;
    }
    if (action.startsWith("audio:")) {
      const [, src, deltaStr] = action.split(":");
      const delta = (Number(deltaStr) || 0) * 0.05; // per-tick monitor step
      if (src === "master") {
        d.setMaster((v) => clamp01(v + delta));
      } else {
        const key = src === "a" ? d.program.source : d.program.sourceB;
        if (!key) return;
        const vol = clamp01((d.channelOf(key).volume ?? 0) + delta);
        // Turning a source up un-mutes the local monitor so it's actually audible.
        d.setMix((m) => ({ ...m, [key]: { volume: vol, muted: vol <= 0.001 } }));
      }
      return;
    }
    if (action.startsWith("audio-mute:")) {
      d.toggleAudioMuted(); // dial press = fan-facing program mute (ADR 0055/0096)
      return;
    }
    switch (action) {
      case "live":
      case "take":
        void d.take(d.transitionType); // commit staged scene with the configured transition
        break;
      case "ftb":
        d.toggleBlackout();
        break;
      case "hold":
        d.toggleVideoPaused();
        break;
      case "mute":
        d.toggleAudioMuted();
        break;
      case "next":
        if (d.nextCueObj) void d.takeCue(d.nextCueObj);
        break;
      case "overlay-off":
        d.takeOverlayOffAir();
        break;
      case "board":
        if (d.whiteboardLiveNow) d.takeOverlayOffAir();
        else d.takeWhiteboardLive();
        break;
      case "question": {
        const q = d.selectedQuestion ?? d.fanQuestions.find((x) => !d.sentMessageIds.has(x.id));
        if (q) {
          d.setSelectedQuestionId(q.id);
          d.sentQuestions.send(q.id).catch(() => undefined);
        }
        break;
      }
      case "end":
        void d.endShow();
        break;
      default:
        break;
    }
  }, []);

  // Run the bridge whenever the console is open and a deck is paired (or being
  // paired). Cheap no-op for the majority with no deck. The plugin paints from
  // this rich state (ADR 0096): lifecycle, hold, FTB, staged layout, next block,
  // pending questions, and the three audio dials.
  const deckLinked = deckPairings.length > 0 || pairingStarted;
  useDeckBridge(
    !!broadcast && deckLinked,
    {
      broadcastId: broadcast?.id,
      role: "director",
      live: onAir,
      onAir,
      micMuted: !!program.audioMuted, // the deck MUTE key reflects the fan-facing program mute
      standby: !!program.videoPaused,
      ftb: !!program.blackout,
      layout: program.layout ?? "single",
      boardShown: whiteboardLiveNow,
      nextCue: nextCueObj?.name,
      pendingQuestions,
      master: { name: "Master", level: Math.round(master * 100), muted: !!program.audioMuted },
      audioA: deckAudioChannel(program.source, sourceAName || "Slot A"),
      audioB: deckAudioChannel(program.sourceB, sourceBName || "Slot B"),
    },
    handleDeckAction,
  );

  const startPairing = async () => {
    try {
      const { code } = await deckPair();
      if (!mountedRef.current) return;
      setPairCode(code);
      setPairingStarted(true);
      setTimeout(loadPairings, 8000); // the plugin claims the code out-of-band
    } catch (err: unknown) {
      if (mountedRef.current) setPushError(msg(err));
    }
  };
  const revokeDeck = async (ref: string) => {
    await deckRevoke(ref).catch(() => undefined);
    loadPairings();
  };

  // ── RelayStudio chrome (ADR 0087) — a faithful reproduction of the reference
  // mock, wired to real data. Exact iOS-dark palette + layout from studioChrome. ──
  const audioFeeds = feeds.filter((f) => f.hasAudio);
  const previewScene: ProgramState = { ...preview, boards: program.boards };
  const legendFeed = feeds.find((f) => !isGuestIdentity(f.identity));
  const guestFeed = feeds.find((f) => isGuestIdentity(f.identity)) ?? feeds.find((f) => f !== legendFeed);
  // Talkback targets (ADR 0213): the Legend, each guest by name, and — when there's
  // more than one talent — All talent. Each is a hold-to-talk button in the AUDIO rack.
  const talkbackTargets: { label: string; target: TalkbackTarget }[] = [];
  if (legendFeed) talkbackTargets.push({ label: "Legend", target: { kind: "one", key: legendFeed.key } });
  feeds
    .filter((f) => isGuestIdentity(f.identity))
    .forEach((g, i) => talkbackTargets.push({ label: g.name || `Guest ${i + 1}`, target: { kind: "one", key: g.key } }));
  if (feeds.length > 1) talkbackTargets.push({ label: "All talent", target: { kind: "all" } });
  const talkbackTargetName =
    talkbackTarget?.kind === "one" ? feeds.find((f) => f.key === talkbackTarget.key)?.name : undefined;
  // Progressive disclosure (ADR 0118): the Console simplifies itself by live context.
  // A guest is present when a guest publisher is live OR an invite is outstanding; the
  // console is "multi-capable" — the multi-head composition tools become relevant —
  // once there's a second publisher, a guest, or a multi-head layout already in play.
  const guestLive = feeds.some((f) => isGuestIdentity(f.identity));
  const hasGuest = guestLive || activeGuestInvites.length > 0;
  const multiCapable = feeds.length >= 2 || hasGuest || previewMulti || programMulti;

  // A source's live <video> filling a stage/thumb; resolves by stable key so a
  // reconnecting publisher keeps its slot (ADR 0042). refFor lets the caller point
  // the right monitor ref at it.
  const videoComposition = (
    state: ProgramState,
    refs: {
      a: React.RefObject<HTMLVideoElement | null>;
      b: React.RefObject<HTMLVideoElement | null>;
      c?: React.RefObject<HTMLVideoElement | null>;
      d?: React.RefObject<HTMLVideoElement | null>;
    },
    multi: boolean,
    label: string,
    overlay?: ProgramOverlay,
  ): ReactNode => {
    const vstyle: CSSProperties = { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", background: "#000" };
    if (multi && state.layout) {
      const refFor = (slot: SourceSlot) => (slot === "a" ? refs.a : slot === "b" ? refs.b : slot === "c" ? refs.c : refs.d);
      const sharedBoard = state.overlay?.kind === "whiteboard" ? state.overlay : null;
      return (
        <div style={{ position: "absolute", inset: 0, display: "grid", gap: 2, ...gridForLayout(state.layout) }} aria-label={`${label} layout ${state.layout}`}>
          {layoutSlots(state.layout).map((slot) => (
            <div key={slot} style={{ position: "relative", gridArea: slot, overflow: "hidden", background: "#000", boxShadow: `inset 0 0 0 2px ${slotAccent(slot)}` }}>
              <video ref={refFor(slot)} muted autoPlay playsInline aria-label={`${label} slot ${slot.toUpperCase()}`} style={vstyle} />
              <div style={{ position: "absolute", left: 5, top: 5, zIndex: 2 }}><SlotBadge slot={slot} ariaLabel={`${label} slot ${slot.toUpperCase()} identity`} /></div>
            </div>
          ))}
          {layoutHasSharedBoard(state.layout) && (
            <div style={{ position: "relative", gridArea: "board", overflow: "hidden", background: "#0a0a0c" }}>
              <WhiteboardCanvas strokes={sharedBoard?.strokes ?? []} board={sharedBoard?.board} boardImageUrl={boardImg(sharedBoard?.board)} ariaLabel={`${label} board`} style={{ borderRadius: 0, aspectRatio: "auto", width: "100%", height: "100%", border: "none" }} />
            </div>
          )}
          {state.layout === "dueling" && (
            <>
              <div style={{ position: "relative", gridArea: "boardA", overflow: "hidden", background: "#0a0a0c" }}>
                <WhiteboardCanvas strokes={state.boards?.a?.strokes ?? []} board={state.boards?.a?.board} boardImageUrl={boardImg(state.boards?.a?.board)} ariaLabel={`${label} slot A board`} style={{ borderRadius: 0, aspectRatio: "auto", width: "100%", height: "100%", border: "none" }} />
              </div>
              <div style={{ position: "relative", gridArea: "boardB", overflow: "hidden", background: "#0a0a0c" }}>
                <WhiteboardCanvas strokes={state.boards?.b?.strokes ?? []} board={state.boards?.b?.board} boardImageUrl={boardImg(state.boards?.b?.board)} ariaLabel={`${label} slot B board`} style={{ borderRadius: 0, aspectRatio: "auto", width: "100%", height: "100%", border: "none" }} />
              </div>
            </>
          )}
        </div>
      );
    }
    // Single layout. When a whiteboard-as-source overlay is composed over this single
    // source (NOT a multi-head board layout), the source is REPOSITIONED exactly like
    // the Fan (ConsumerWatch): e.g. pip-legend shrinks the legend to a small PiP
    // corner under the board. The board itself is drawn at the monitor overlay level
    // (lay.board), like ConsumerWatch's stage-level board, so single AND two-up (dual)
    // both show it — this is what keeps the confidence monitor matching Fan output and
    // fixes the pip-legend Preview exception (LSC-177).
    const wbSource =
      overlay?.kind === "whiteboard" && state.layout !== "dual-board" && state.layout !== "dueling"
        ? overlay
        : null;
    const layVideo = wbSource ? whiteboardLayout(wbSource.layout).video : null;
    return (
      <video
        ref={refs.a}
        muted
        autoPlay
        playsInline
        aria-label={label}
        style={layVideo ? { width: "100%", height: "100%", objectFit: "cover", display: "block", background: "#000", ...layVideo } : vstyle}
      />
    );
  };

  // ── header ──
  const header = (
    <header style={{ display: "flex", alignItems: "center", gap: 12, padding: "0 12px", height: 40, minWidth: 0, borderBottom: `1px solid ${SC.divider}`, background: SC.panel, flex: "none" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 22, height: 22, borderRadius: 9, background: SC.red, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff" }} />
        </div>
        <div style={{ fontWeight: 600, fontSize: 14, letterSpacing: "0.06em" }}>LEGEND</div>
        <div style={{ fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.06em", color: SC.dim, paddingTop: 1 }}>STUDIO</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "5px 12px 5px 10px", borderRadius: 999, background: onAir ? SC.redTint2 : "rgba(255,255,255,0.06)", border: `1px solid ${onAir ? SC.redEdge : SC.hair2}` }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: onAir ? SC.red : SC.dim, animation: onAir ? "tally 1.6s ease-in-out infinite" : undefined }} />
        <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", color: onAir ? SC.redSoft : SC.dim }}>{onAir ? "ON AIR" : program.standby ? "STANDBY" : program.ended ? "ENDED" : "OFF AIR"}</div>
        <div style={{ fontFamily: SC.mono, fontSize: 12, color: "#fff" }}>{formatClock(elapsedMs)}</div>
      </div>
      <div style={{ display: "block", flex: "0 1 auto", minWidth: 52, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: SC.mono, fontSize: 11, lineHeight: 1.4, color: SC.faint }}>
        {feeds.length} live · {conn === "live" ? "LINK OK" : conn === "reconnecting" ? "RECONNECTING" : conn === "reconnected" ? "LINK OK" : "LINK LOST"}{roomName ? ` · ROOM ${roomName}` : ""}
      </div>
      {broadcast?.rehearsal && (
        <span role="status" aria-label="Rehearsal — private, not public" style={{ fontFamily: SC.mono, fontSize: 11, letterSpacing: "0.06em", color: SC.yellow, border: `1px solid ${SC.yellow}`, borderRadius: 6, padding: "2px 6px" }}>REHEARSAL · NOT PUBLIC</span>
      )}
      {/* Director mic → On Air (ADR 0247): a prominent, always-visible badge whenever the
          Director's voice is in the fan Program mix — doubles as an immediately-accessible
          off control, so it's reachable regardless of which tab/panel holds the AUDIO rack. */}
      {micOnAir && (
        <button type="button" aria-label="Director mic on air — tap to go off" title="Your mic is in the fan Program mix — tap to go off air" onClick={() => void toggleMicOnAir()}
          style={{ display: "flex", alignItems: "center", gap: 7, padding: "4px 11px", borderRadius: 999, background: SC.redTint2, border: `1px solid ${SC.redEdge}`, color: SC.redSoft, fontFamily: SC.mono, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", cursor: "pointer", flex: "none", whiteSpace: "nowrap" }}>
          <span aria-hidden style={{ width: 8, height: 8, borderRadius: "50%", background: SC.red, animation: "tally 1.6s ease-in-out infinite" }} />
          MIC ON AIR
        </button>
      )}
      <div style={{ flex: 1 }} />
      {!program.ended && (
        <>
      <button type="button" aria-label="Stop broadcast" disabled={stopping || !broadcast} onClick={() => void stopShow()} style={{ padding: "8px 14px", borderRadius: 11, border: `1px solid ${SC.hair3}`, background: SC.chipBg, color: SC.text, fontFamily: SC.sans, fontSize: 12, fontWeight: 600, letterSpacing: "0.04em", cursor: "pointer", flex: "none", whiteSpace: "nowrap", opacity: stopping || !broadcast ? 0.5 : 1 }}>
        {stopping ? "Pausing…" : "Stop — standby"}
      </button>
      <button type="button" aria-label="End broadcast" disabled={ending || !broadcast} onClick={() => void endShow()} style={{ padding: "8px 16px", borderRadius: 11, border: "1px solid rgba(255,69,58,0.6)", background: SC.red, color: "#fff", fontFamily: SC.sans, fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", cursor: "pointer", flex: "none", whiteSpace: "nowrap", opacity: ending || !broadcast ? 0.6 : 1 }}>
        {ending ? "Ending…" : "END STREAM"}
      </button>
        </>
      )}
      {onClose && (
        <button type="button" aria-label="Exit" title="Exit to the prior view — does not end the broadcast" onClick={exitConsole} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 11, border: `1px solid ${SC.redEdge}`, background: SC.redTint, color: SC.redSoft, fontFamily: SC.sans, fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", cursor: "pointer", flex: "none", whiteSpace: "nowrap" }}><span aria-hidden style={{ fontSize: 13, lineHeight: 1 }}>←</span>Exit</button>
      )}
    </header>
  );

  // ── nav (workspace tabs) ──
  // The nav renders the VISIBLE workspaces in order (ADR 0123); ⚙ Tabs opens the editor.
  const nav = (
    <nav aria-label="Workspace" style={{ display: "flex", alignItems: "center", gap: 2, padding: "0 10px", height: 32, borderBottom: `1px solid ${SC.divider}`, background: "rgba(20,20,22,0.82)", flex: "none", overflowX: "auto" }}>
      {visibleWorkspaces(workspaces).map((t) => (
        <button key={t.id} type="button" aria-pressed={tab === t.id} onClick={() => setTab(t.id)} style={{ padding: "6px 15px", borderRadius: 999, border: `1px solid ${tab === t.id ? SC.blueEdge : "transparent"}`, background: tab === t.id ? SC.blueTint : "transparent", color: tab === t.id ? SC.blueHi : SC.dim, fontFamily: SC.sans, fontSize: 13, fontWeight: 450, cursor: "pointer", whiteSpace: "nowrap" }}>
          {t.name}
        </button>
      ))}
      <div style={{ flex: 1 }} />
      <button type="button" aria-label={editMode ? "Done editing layout" : "Edit layout"} aria-pressed={editMode} onClick={() => setEditMode((v) => !v)} style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid ${editMode ? SC.blueEdge : SC.hair3}`, background: editMode ? SC.blueTint : "transparent", color: editMode ? SC.blueHi : SC.dim, fontFamily: SC.mono, fontSize: 11, cursor: "pointer", whiteSpace: "nowrap", flex: "none" }}>{editMode ? "✓ Done" : "✎ Edit"}</button>
      <button type="button" aria-label="Edit tabs" aria-expanded={tabsEditorOpen} onClick={() => setTabsEditorOpen((v) => !v)} style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid ${tabsEditorOpen ? SC.blueEdge : SC.hair3}`, background: tabsEditorOpen ? SC.blueTint : "transparent", color: tabsEditorOpen ? SC.blueHi : SC.dim, fontFamily: SC.mono, fontSize: 11, cursor: "pointer", whiteSpace: "nowrap", flex: "none" }}>⚙ Tabs</button>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.06em", color: SC.dim, padding: "0 4px 0 8px", whiteSpace: "nowrap" }}>
        <span>SHOW</span>
        <span style={{ color: "#e5e5ea", letterSpacing: "0.04em", fontFamily: SC.sans, fontSize: 12 }}>{event.title}</span>
      </div>
    </nav>
  );

  // The STAGED graphics-bus overlay (ADR 0063), composed OFF-AIR over the preview's
  // staged scene so the Director previews EXACTLY what Send to Program / Take Live
  // would put on air (ADR 0107). This is the Director's own PREVIEW monitor only —
  // Fan Program is untouched until an explicit take (the stage-off-air → Send/Take
  // contract is preserved). Mirrors how the PROGRAM monitor + ConsumerWatch render a
  // LIVE overlay: SCLowerThird for the lower third, AdSurface for an ad, and the
  // shared WhiteboardCanvas positioned per its layout for telestration. The lower
  // third reuses SCLowerThird (like the program monitor), so its off-air band sits
  // where a TAKE would place it; the single-source whiteboard is `!previewMulti`
  // like ConsumerWatch (in a multi-head layout the board is part of the scene).
  const stagedWbLay = whiteboardLayout(wbLayout);
  const stagedOverlayPreview: ReactNode = (
    <div aria-label="Preview staged overlay" style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      {/* The whiteboard composes over ANY layout now (ADR 0116) — including a dual/
          two-up scene — as a region positioned by its pip/side/full layout, mirroring
          how ConsumerWatch renders the whiteboard overlay over the composition. */}
      {stagedKind === "whiteboard" && (
        <div style={stagedWbLay.board}>
          <WhiteboardCanvas strokes={strokes} board={wbBoard} boardImageUrl={boardImg(wbBoard)} ariaLabel="Preview whiteboard" style={{ borderRadius: 0, aspectRatio: "auto", width: "100%", height: "100%", border: "none" }} />
        </div>
      )}
      {stagedKind === "ad" && stagedAd && (
        <div style={{ position: "absolute", inset: 0 }}><AdSurface overlay={stagedAd} muted /></div>
      )}
      {stagedKind === "lower-third" && lowerThirdHasText && (
        <SCLowerThird line1={l3Title} line2={l3Subtitle} accent={l3Accent} />
      )}
    </div>
  );

  // ── dual-bus row (PREVIEW · TAKE · PROGRAM · transition column) ──
  const previewName = previewMulti ? preview.layout : preview.source || "—";
  const layoutName = programMulti ? `${program.layout}` : program.source ? "Full frame" : "No source";
  const transPills: { t: TransitionType; name: string }[] = [
    { t: "cut", name: "Cut" }, { t: "dissolve", name: "Dissolve" }, { t: "wipe", name: "Wipe" }, { t: "slide", name: "Slide" },
  ];
  const durPills = [200, 500, 1000];
  const tbarPct = `${Math.round(tbar * 100)}%`;
  const dualBus = (
    <div style={{ display: "flex", gap: 10, alignItems: "stretch", flex: "none", minWidth: 0, flexWrap: "wrap" }}>
      <SCMonitor bus="preview" name={previewName} slug={previewMulti ? `A ${preview.source || "—"} · B ${preview.sourceB || "—"}` : preview.source ? `source ${preview.source}` : "stage a source"} overlay={stagedOverlayPreview}>
        {videoComposition(previewScene, { a: previewVideoRef, b: previewVideoRefB, c: previewVideoRefC, d: previewVideoRefD }, previewMulti, "Preview", previewScene.overlay)}
        {!preview.source && !previewMulti && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: SC.dim, fontSize: 12, fontFamily: SC.mono }}>Stage a source</div>
        )}
      </SCMonitor>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 9, flex: "none" }}>
        <button type="button" aria-label="Take" title="Send preview to program" disabled={taking} onClick={() => void take("cut")} style={{ width: 64, height: 64, borderRadius: 16, border: `1px solid ${SC.red}`, background: SC.red, color: "#fff", fontSize: 26, lineHeight: 1, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 22px rgba(255,69,58,0.22)", opacity: taking ? 0.6 : 1 }}>→</button>
        <div style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.07em", color: SC.dim3 }}>TAKE</div>
      </div>
      <SCMonitor bus="program" name={layoutName} slug={programSourceLost ? "signal lost — reconnecting…" : program.ended ? "ended" : program.standby ? "standby" : program.source || "no source"}
        overlay={
          <>
            {program.overlay?.kind === "lower-third" && (program.overlay.title || program.overlay.subtitle) && (
              <SCLowerThird line1={program.overlay.title} line2={program.overlay.subtitle} />
            )}
            {program.overlay?.kind === "ad" && program.overlay.adId && (
              <div style={{ position: "absolute", inset: 0 }}><AdSurface overlay={program.overlay} muted /></div>
            )}
            {/* The LIVE whiteboard-as-source board, composed over the single/two-up
                source at its pip/side/full region — mirroring ConsumerWatch's
                stage-level board so the PROGRAM confidence monitor matches what fans
                receive (LSC-177). Multi-head board layouts (dual-board/dueling) draw
                their board inside videoComposition instead. */}
            {program.overlay?.kind === "whiteboard" && program.layout !== "dual-board" && program.layout !== "dueling" && (
              <div style={whiteboardLayout(program.overlay.layout).board}>
                <WhiteboardCanvas
                  strokes={program.overlay.strokes ?? []}
                  board={program.overlay.board}
                  boardImageUrl={boardImg(program.overlay.board)}
                  ariaLabel="Program whiteboard"
                  style={{ borderRadius: 0, aspectRatio: "auto", width: "100%", height: "100%", border: "none" }}
                />
              </div>
            )}
            {program.blackout && !program.ended && !program.standby && (
              <div role="status" aria-label="Program faded to black" style={{ position: "absolute", inset: 0, background: "#000", display: "flex", alignItems: "flex-end", justifyContent: "center", padding: 6 }}>
                <span style={{ fontFamily: SC.mono, fontSize: 9, color: SC.redSoft }}>FADE TO BLACK</span>
              </div>
            )}
            {onAir && (
              <div style={{ position: "absolute", left: 8, bottom: 8, padding: "4px 8px", borderRadius: 4, background: "rgba(28,28,30,0.8)", border: "1px solid rgba(255,255,255,0.18)", fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.05em", color: SC.text }}>ON AIR</div>
            )}
          </>
        }
      >
        {videoComposition(program, { a: programVideoRef, b: programVideoRefB, c: programVideoRefC, d: programVideoRefD }, programMulti, "Program preview", program.overlay)}
        {!program.source && !programMulti && program.overlay?.kind !== "ad" && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: SC.dim, fontSize: 12, fontFamily: SC.mono }}>No source on program</div>
        )}
        {programSourceLost && program.overlay?.kind !== "ad" && (
          <div role="status" aria-label="Program source lost" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: SC.redSoft, fontSize: 12, fontFamily: SC.mono }}>Source lost — reconnecting…</div>
        )}
      </SCMonitor>
      {/* transition column */}
      <div style={{ flex: 1, minWidth: 230, background: SC.panel, border: `1px solid ${SC.hair}`, borderRadius: 12, padding: 12, display: "flex", flexDirection: "column", gap: 11, justifyContent: "center" }}>
        <div style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.07em", color: SC.dim3, textAlign: "center" }}>TRANSITION · FTB</div>
        <div style={{ display: "flex", gap: 7 }}>
          <button type="button" aria-label="CUT" onClick={() => void take("cut")} style={{ flex: 1, padding: "11px 0", borderRadius: 11, border: `1px solid ${SC.hair3}`, background: SC.neutralBtn, color: "#fff", fontFamily: SC.sans, fontSize: 12, fontWeight: 600, letterSpacing: "0.04em", cursor: "pointer" }}>CUT</button>
          <button type="button" aria-label="AUTO" onClick={() => void take(transitionType)} style={{ flex: 1.4, padding: "8px 0", borderRadius: 11, border: `1px solid ${SC.red}`, background: SC.red, color: "#fff", fontFamily: SC.sans, fontSize: 12, fontWeight: 600, letterSpacing: "0.04em", cursor: "pointer" }}>AUTO</button>
          <button type="button" aria-label="FTB" aria-pressed={!!program.blackout} onClick={toggleBlackout} style={{ flex: 1, padding: "11px 0", borderRadius: 11, border: `1px solid ${program.blackout ? SC.red : SC.hair3}`, background: program.blackout ? SC.redTint : SC.neutralBtn, color: "#fff", fontFamily: SC.sans, fontSize: 12, fontWeight: 500, letterSpacing: "0.08em", cursor: "pointer" }}>FTB</button>
        </div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {transPills.map((x) => {
            const active = transitionType === x.t;
            return <button key={x.t} type="button" aria-label={`Transition ${x.name}`} aria-pressed={active} onClick={() => setTransitionType(x.t)} style={{ flex: 1, minWidth: 56, padding: "10px 12px", borderRadius: 12, border: `1px solid ${active ? SC.blueEdge : SC.hair3}`, background: active ? SC.blueTint : SC.chipBg, color: active ? SC.blueHi : SC.dim, fontFamily: SC.sans, fontSize: 11, cursor: "pointer" }}>{x.name}</button>;
          })}
        </div>
        <div style={{ display: "flex", gap: 5 }}>
          {durPills.map((ms) => {
            const active = durationMs === ms;
            return <button key={ms} type="button" aria-label={`Auto-out ${ms}`} aria-pressed={active} onClick={() => setDurationMs(ms)} style={{ flex: 1, padding: "5px 0", borderRadius: 12, border: `1px solid ${active ? SC.blueEdge : SC.hair3}`, background: active ? SC.blueTint : SC.chipBg, color: active ? SC.blueHi : SC.dim, fontFamily: SC.mono, fontSize: 10, cursor: "pointer" }}>{ms / 1000}s</button>;
          })}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.06em", color: SC.dim }}>T-BAR</div>
            <div style={{ fontFamily: SC.mono, fontSize: 10, color: "#b8b8bd" }}>{tbarPct}</div>
          </div>
          <input type="range" min={0} max={100} value={Math.round(tbar * 100)} aria-label="T-bar" aria-orientation="horizontal"
            onChange={(e) => setTbar(Math.min(1, Math.max(0, Number(e.target.value) / 100)))}
            onMouseUp={() => { if (tbar >= 1) { void take(transitionType); setTbar(0); } }}
            onTouchEnd={() => { if (tbar >= 1) { void take(transitionType); setTbar(0); } }}
            style={{ width: "100%", background: `linear-gradient(90deg, ${SC.red} 0%, ${SC.red} ${tbarPct}, #48484a ${tbarPct}, #48484a 100%)` }} />
        </div>
      </div>
    </div>
  );

  // Shared field/label/button styles for the panels + tabs below (moved up from the
  // tabs block so the Console panels — the new Ask + graphics quick-editors — can use
  // them too, ADR 0117).
  const mono10 = { fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.06em", color: SC.dim } as CSSProperties;
  const fieldLabel = { display: "block", fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.05em", color: SC.dim, marginBottom: 6 } as CSSProperties;
  const inputStyle = { width: "100%", padding: "8px 10px", borderRadius: 9, border: `1px solid ${SC.hair2}`, background: SC.meterBg, color: SC.text, fontFamily: SC.sans, fontSize: 13 } as CSSProperties;
  const selStyle = inputStyle;
  const btn = (bg: string, fg: string, bd: string): CSSProperties => ({ padding: "8px 14px", borderRadius: 10, border: `1px solid ${bd}`, background: bg, color: fg, fontFamily: SC.sans, fontSize: 12, fontWeight: 600, cursor: "pointer" });
  const primaryBtn = btn(SC.blue, "#fff", SC.blue);
  const ghostBtn = btn(SC.chipBg, SC.text, SC.hair3);

  // ── SOURCES panel ──
  const sourcesPanel = (
    <SCPanel label="SOURCES" ariaLabel="Multiview" right={<span style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.04em", color: SC.blue }}>→ {previewMulti ? `Slot ${stageSlot.toUpperCase()}` : "Preview"}</span>}>
      {/* Guest-presence indicator (ADR 0118) — makes it legible WHY the multi-head +
          mix controls have appeared: a guest is live, or an invite is outstanding. */}
      {(guestLive || activeGuestInvites.length > 0) && (
        <div role="status" style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.05em", color: guestLive ? SC.green : SC.blueHi }}>
          <span aria-hidden style={{ width: 6, height: 6, borderRadius: "50%", background: guestLive ? SC.green : SC.blueHi, flex: "none" }} />
          {guestLive ? "GUEST LIVE — multi-head + mix available" : `${activeGuestInvites.length} GUEST INVITE${activeGuestInvites.length === 1 ? "" : "S"} PENDING`}
        </div>
      )}
      {previewMulti && (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {layoutSlots(preview.layout).map((s) => {
            const on = stageSlot === s;
            return (
              <button key={s} type="button" aria-label={`Stage target slot ${s.toUpperCase()}`} aria-pressed={on} onClick={() => setStageSlot(s)} style={{ flex: 1, minWidth: 56, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 9, border: `1px solid ${on ? slotAccent(s) : SC.hair3}`, background: on ? slotAccent(s) : SC.chipBg, color: on ? SLOT_INK : SC.dim, fontFamily: SC.mono, fontSize: 9, fontWeight: 600, letterSpacing: "0.04em", cursor: "pointer" }}>
                <span aria-hidden style={{ width: 8, height: 8, borderRadius: 2, background: slotAccent(s), border: on ? "1px solid rgba(0,0,0,0.4)" : "none", flex: "none" }} />
                SLOT {s.toUpperCase()}
              </button>
            );
          })}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {feeds.length === 0 ? (
          <div style={{ fontSize: 11, color: SC.dim }}>Waiting for legends to go live…</div>
        ) : feeds.map((f) => {
          const pgmSlot = programSlotForKey(f.key);
          const pvwSlot = previewSlotForKey(f.key);
          const pgm = pgmSlot !== null;
          const pvw = pvwSlot !== null;
          const tally = pgm ? SC.red : pvw ? SC.blue : SC.dim2;
          const state = pgm ? (programMulti ? `PGM ${pgmSlot!.toUpperCase()}` : "PGM") : pvw ? (previewMulti ? `PVW ${pvwSlot!.toUpperCase()}` : "PVW") : "";
          // Which SLOT this feed occupies on the active multi-head bus (ADR 0108/0212)
          // — program takes priority over preview; null in single.
          const slotForFeed: SourceSlot | null = programMulti && pgmSlot ? pgmSlot : previewMulti && pvwSlot ? pvwSlot : null;
          const isGuest = isGuestIdentity(f.identity);
          const stage = () => { if (previewMulti) assignSlot(stageSlot, f.key); else selectSource(f.key); };
          return (
            <button key={f.identity} type="button" aria-label={previewMulti ? `Stage ${f.name || f.key} to preview slot ${stageSlot.toUpperCase()}` : `Stage ${f.name || f.key} to preview`} onClick={stage}
              style={{ display: "flex", gap: 9, alignItems: "center", padding: 6, borderRadius: 11, border: `1px solid ${pgm ? "rgba(255,69,58,0.5)" : pvw ? SC.blueEdge : SC.hair}`, background: pgm ? SC.redTint : pvw ? SC.blueTint : SC.chipBg, cursor: "pointer", textAlign: "left" }}>
              <div style={{ position: "relative", width: 64, flex: "none", aspectRatio: "16/9", borderRadius: 4, overflow: "hidden", background: "#000" }}
                onClick={eyedropper && pgm ? (e) => { e.stopPropagation(); sampleFromProgramTile(e.clientX, e.clientY); } : undefined}>
                <video ref={(el) => setTileEl(f.identity, el)} muted autoPlay playsInline aria-label={`Feed ${f.name || f.key}`} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                <div style={{ position: "absolute", left: 0, top: 0, width: pgm || pvw ? "100%" : "0%", height: 2, background: tally }} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: SC.text, letterSpacing: "0.02em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.name || f.key}</div>
                <div style={{ fontSize: 11, color: SC.dim, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{isGuest ? "Guest" : "Legend"}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flex: "none" }}>
                {slotForFeed && <SlotBadge slot={slotForFeed} size={16} />}
                <div style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.04em", color: tally }}>{state}</div>
              </div>
            </button>
          );
        })}
      </div>
    </SCPanel>
  );

  // ── SCENES panel (layout recipes staged into preview) ──
  const sceneDefs: { name: string; summary: string; layout: ProgramLayout; cells: number; cols: string }[] = [
    { name: "Solo", summary: legendFeed?.name ?? "one source", layout: "single", cells: 1, cols: "1fr" },
    { name: "Two-up", summary: "1×2", layout: "dual", cells: 2, cols: "1fr 1fr" },
    { name: "Two + board", summary: "heads + board", layout: "dual-board", cells: 2, cols: "1fr 1fr" },
    { name: "Dueling", summary: "2×2 boards", layout: "dueling", cells: 4, cols: "1fr 1fr" },
    { name: "3-up large", summary: "Legend + 2", layout: "tri", cells: 3, cols: "2fr 1fr" },
    { name: "3-up equal", summary: "3 balanced", layout: "tri-equal", cells: 3, cols: "1fr 1fr 1fr" },
    { name: "4-up large", summary: "Legend + 3", layout: "quad", cells: 4, cols: "2fr 1fr" },
    { name: "4-up (2×2)", summary: "quad grid", layout: "quad-equal", cells: 4, cols: "1fr 1fr" },
    { name: "Board + 3", summary: "board + 3 PiP", layout: "board-tri", cells: 3, cols: "1fr 1fr 1fr" },
    { name: "Board + 4", summary: "board + 4 PiP", layout: "board-quad", cells: 4, cols: "1fr 1fr 1fr 1fr" },
  ];
  const stageScene2 = (layout: ProgramLayout) => {
    if (layout === "single") { stageScene({ layout: "single", source: legendFeed?.key ?? preview.source }); return; }
    changeProgramLayout(layout);
    if (legendFeed) stageScene({ source: legendFeed.key });
    if (guestFeed && guestFeed !== legendFeed) stageScene({ sourceB: guestFeed.key });
  };
  // Multi-head scene cards (two-up / board / dueling) only compose something once a
  // second source is available — hide them until multi-capable so a solo show isn't
  // cluttered with layouts that can't do anything yet (ADR 0118). The Compose tab keeps
  // the full LAYOUTS rack always, for pre-staging.
  const visibleScenes = multiCapable ? sceneDefs : sceneDefs.filter((s) => s.layout === "single");
  const scenesPanel = (
    <SCPanel label="SCENES" right={multiCapable ? "One click stages into preview — then TAKE" : "Solo — multi-head appears with a 2nd source"}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 8 }}>
        {visibleScenes.map((s) => {
          const active = (preview.layout ?? "single") === s.layout;
          return (
            <button key={s.name} type="button" aria-label={`Stage scene ${s.name}`} aria-pressed={active} onClick={() => stageScene2(s.layout)}
              style={{ display: "flex", flexDirection: "column", gap: 7, padding: 8, borderRadius: 12, border: `1px solid ${active ? SC.blueEdge : SC.hair}`, background: active ? SC.blueTint : SC.chipBg, cursor: "pointer", textAlign: "left" }}>
              <div style={{ width: "100%", aspectRatio: "16/9", display: "grid", gap: 2, gridTemplateColumns: s.cols, gridTemplateRows: s.cells > 2 ? "1fr 1fr" : "1fr", padding: 3, borderRadius: 3, background: "#101012" }}>
                {Array.from({ length: s.cells }).map((_, i) => (
                  <div key={i} style={{ position: "relative", borderRadius: 1, overflow: "hidden", background: i % 2 ? "#2a3b4a" : "#3a4a5a" }}>
                    <div style={{ position: "absolute", left: "50%", bottom: 0, transform: "translateX(-50%)", width: "30%", height: "62%", borderRadius: "50% 50% 12% 12%", background: "rgba(255,255,255,0.25)" }} />
                  </div>
                ))}
              </div>
              <div style={{ minWidth: 0, width: "100%" }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: SC.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</div>
                <div style={{ fontFamily: SC.mono, fontSize: 9, color: SC.dim, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.summary}</div>
              </div>
            </button>
          );
        })}
      </div>
      {!multiCapable && (
        <div style={{ fontSize: 10, color: SC.dim, lineHeight: 1.4 }}>
          Multi-head layouts appear when a second publisher or guest is live. Full layouts are always in{" "}
          <button type="button" aria-label="Open Compose tab" onClick={() => setTab("compose")} style={{ background: "none", border: "none", padding: 0, color: SC.blueHi, cursor: "pointer", fontFamily: SC.sans, fontSize: 10 }}>Compose ›</button>.
        </div>
      )}
    </SCPanel>
  );

  // ── GRAPHICS BUS panel (overlays → open Graphics tab, staged) ──
  const busDefs: { slot: string; name: string; kind: OverlayKind }[] = [
    { slot: "F1", name: "Lower third", kind: "lower-third" },
    { slot: "F2", name: "Whiteboard", kind: "whiteboard" },
    { slot: "F3", name: "Ad break", kind: "ad" },
  ];
  const graphicsBusPanel = (
    <SCPanel label="GRAPHICS BUS" right="Stage a graphic — then TAKE to program">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 8 }}>
        {busDefs.map((k) => {
          // ADR 0117: the bus buttons STAGE the graphic inline (no jump to the Graphics
          // tab) so a normal show runs from Console; the chip reads STAGED (preview) vs
          // ON AIR (program), and the single program TAKE commits it (ADR 0116).
          const staged = stagedKind === k.kind;
          const live = liveKind === k.kind && (k.kind !== "lower-third" || lowerThirdOnAir) && (k.kind !== "ad" || adOnAir) && (k.kind !== "whiteboard" || whiteboardOnAir);
          const tone = live ? SC.redSoft : staged ? SC.blueHi : SC.dim;
          return (
            <button key={k.slot} type="button" aria-label={staged ? `Unstage ${k.name}` : `Stage ${k.name}`} aria-pressed={staged} onClick={() => (staged ? stageOverlayOff() : stageKind(k.kind))}
              style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start", padding: 8, borderRadius: 12, border: `1px solid ${live ? "rgba(255,69,58,0.5)" : staged ? SC.blueEdge : SC.hair}`, background: live ? SC.redTint : staged ? SC.blueTint : SC.chipBg, cursor: "pointer", textAlign: "left" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                <div style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.06em", color: tone }}>{k.slot}</div>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: live ? SC.red : staged ? SC.blue : "#3a3a3c" }} />
              </div>
              <div style={{ fontSize: 12, fontWeight: 500, color: SC.text, lineHeight: 1.25 }}>{k.name}</div>
              <div style={{ fontFamily: SC.mono, fontSize: 8, letterSpacing: "0.05em", color: tone }}>{live ? "ON AIR" : staged ? "STAGED" : "READY"}</div>
            </button>
          );
        })}
        <button type="button" aria-label="Stage graphics off" onClick={stageOverlayOff}
          style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start", padding: 8, borderRadius: 12, border: `1px solid ${SC.hair}`, background: SC.chipBg, cursor: "pointer", textAlign: "left" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <div style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.06em", color: SC.dim }}>F4</div>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#3a3a3c" }} />
          </div>
          <div style={{ fontSize: 12, fontWeight: 500, color: SC.text, lineHeight: 1.25 }}>Clear</div>
          <div aria-hidden style={{ fontFamily: SC.mono, fontSize: 8, letterSpacing: "0.05em", color: SC.dim }}>&nbsp;</div>
        </button>
      </div>
      {/* Inline quick-editor (ADR 0117): edit the staged graphic right here — a normal
          show never needs to leave Console. Full STYLE / auto-out / telestration drawing
          stays in the Graphics tab. Staging is off-air; the program TAKE puts it on air. */}
      {stagedKind === "lower-third" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <label style={{ minWidth: 0 }}><span style={fieldLabel}>LINE 1</span><input aria-label="Console lower-third line 1" value={l3Title} onChange={(e) => setL3Title(e.target.value)} style={inputStyle} /></label>
            <label style={{ minWidth: 0 }}><span style={fieldLabel}>LINE 2</span><input aria-label="Console lower-third line 2" value={l3Subtitle} onChange={(e) => setL3Subtitle(e.target.value)} style={inputStyle} /></label>
          </div>
          {/* Auto-out (timed display) restored to the Console (LSC-178) — the lower
              third takes itself off air after N seconds without a tab hop. Shares
              autoOutS with the Graphics STYLE rack; the on-program timer (arm on take)
              is unchanged. */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={fieldLabel}>AUTO-OUT</span>
            <div style={{ display: "flex", gap: 6, flex: 1, minWidth: 180 }}>
              {[{ s: 0, n: "Manual" }, { s: 5, n: "5s" }, { s: 8, n: "8s" }, { s: 12, n: "12s" }].map((x) => {
                const on = autoOutS === x.s;
                return <button key={x.n} type="button" aria-label={`Console auto-out ${x.n}`} aria-pressed={on} onClick={() => setAutoOutS(x.s)} style={{ flex: 1, padding: "8px 0", borderRadius: 9, border: `1px solid ${on ? SC.blueEdge : SC.hair3}`, background: on ? SC.blueTint : SC.chipBg, color: on ? SC.blueHi : SC.dim, fontFamily: SC.mono, fontSize: 11, cursor: "pointer" }}>{x.n}</button>;
              })}
            </div>
          </div>
        </div>
      )}
      {stagedKind === "whiteboard" && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label><span style={fieldLabel}>Board</span><select aria-label="Console board" value={wbBoard} onChange={(e) => stageBoard(e.target.value as BoardId)} style={selStyle}>{boardOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
          <label><span style={fieldLabel}>Layout</span><select aria-label="Console board layout" value={wbLayout} onChange={(e) => stageLayout(e.target.value as WhiteboardLayout)} style={selStyle}><option value="pip-legend">PIP · legend small</option><option value="pip-board">PIP · board small</option><option value="side">Side by side</option><option value="full">Full screen</option></select></label>
        </div>
      )}
      {stagedKind === "ad" && (
        <label style={{ display: "block", maxWidth: 320 }}><span style={fieldLabel}>Ad</span><select aria-label="Console ad" value={selectedAdId} onChange={(e) => setSelectedAdId(e.target.value)} style={selStyle}>{ads.length === 0 && <option value="">No ads yet</option>}{ads.map((a) => <option key={a.id} value={a.id}>{a.name}{a.kind === "video" ? " (video)" : ""}</option>)}</select></label>
      )}
      {stagedKind !== "none" && (
        <div style={{ fontSize: 11, color: SC.dim, lineHeight: 1.4 }}>
          Staged in preview — the program <strong style={{ color: SC.blueHi }}>TAKE</strong> puts it on air.{" "}
          <button type="button" aria-label="Edit graphic in Graphics tab" onClick={() => setTab("graphics")} style={{ background: "none", border: "none", padding: 0, color: SC.blueHi, cursor: "pointer", fontFamily: SC.sans, fontSize: 11 }}>Edit in Graphics ›</button>
        </div>
      )}
    </SCPanel>
  );

  // ── ASK THE LEGEND panel (ADR 0117) — the core live interaction, brought onto
  // Console so a Director never has to leave for the Questions tab to run a normal
  // show. Toggle + pending count + selectable queue + Send to Legend. The full
  // Questions tab still exists (identical handlers). ──
  const askToggle = (
    <button type="button" aria-label={program.askDisabled ? "Enable Ask the Legend" : "Disable Ask the Legend"} aria-pressed={!program.askDisabled} onClick={() => void push({ ...programRef.current, askDisabled: !program.askDisabled })}
      style={{ ...ghostBtn, padding: "5px 10px", fontSize: 11 }}>{program.askDisabled ? "Off" : "On"}</button>
  );
  const askPanel = (
    <SCPanel label="ASK THE LEGEND" ariaLabel="Ask the Legend" right={askToggle}>
      {program.askDisabled ? (
        <div style={{ fontSize: 11, color: SC.dim }}>Off — fans can't submit questions for this CaveCast.</div>
      ) : (
        <section aria-label="Ask the Legend queue" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={mono10}>{pendingQuestions} PENDING</span>
            {selectedQuestion && <span style={{ ...mono10, color: SC.blueHi }}>SELECTED</span>}
          </div>
          {fanQuestions.length === 0 ? (
            <div style={{ fontSize: 11, color: SC.dim }}>No Fan questions yet.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 5, maxHeight: 168, overflow: "auto", paddingRight: 2 }}>
              {fanQuestions.slice(-8).reverse().map((q) => {
                const selected = q.id === selectedQuestionId;
                const sent = sentMessageIds.has(q.id);
                return (
                  <button key={q.id} type="button" aria-label={`Select question from ${q.user}`} aria-pressed={selected} onClick={() => setSelectedQuestionId(q.id)}
                    style={{ padding: "7px 9px", textAlign: "left", cursor: "pointer", color: SC.text, background: selected ? SC.blueTint : SC.chipBg, border: `1px solid ${selected ? SC.blueEdge : SC.hair}`, borderRadius: 8 }}>
                    <strong style={{ color: SC.blueHi, fontSize: 11 }}>{q.user}</strong>
                    {sent && <span style={{ fontFamily: SC.mono, fontSize: 9, marginLeft: 6, color: SC.green }}>SENT</span>}
                    <span style={{ display: "block", marginTop: 2, fontSize: 12, lineHeight: 1.3 }}>{q.text}</span>
                  </button>
                );
              })}
            </div>
          )}
          <button type="button" aria-label="Send to Legend" disabled={!selectedQuestion || sendingQuestion || (selectedQuestion ? sentMessageIds.has(selectedQuestion.id) : false)} onClick={() => void sendSelectedQuestion()}
            style={{ ...primaryBtn, padding: "9px 0", opacity: !selectedQuestion || sendingQuestion ? 0.5 : 1 }}>
            {sendingQuestion ? "Sending…" : selectedQuestion && sentMessageIds.has(selectedQuestion.id) ? "Sent to Legend" : "Send to Legend"}
          </button>
          {questionError && <p role="alert" style={{ color: SC.redSoft, fontSize: 11 }}>{questionError}</p>}
        </section>
      )}
    </SCPanel>
  );

  // ── AUDIO panel (operator-local monitor mixer, ADR 0085/0106) ──
  // `silenced` reflects when a channel makes no sound at the desk for a reason other
  // than its own mute (i.e. Master is muted) so an unmuted channel still reads as
  // silent — making "why can't I hear it" unmistakable (ADR 0106).
  const faderStrip = (key: string | null, label: string, volume: number, muted: boolean, onVol: (v: number) => void, onMute: () => void, silenced = false) => {
    const pct = `${Math.round(Math.min(1, Math.max(0, volume)) * 100)}%`;
    const off = muted || silenced;
    return (
      <div key={key ?? "master"} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 7 }}>
        <div style={{ fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.04em", color: off ? SC.redSoft : SC.text }}>{muted ? "MUTE" : silenced ? "—" : pct}</div>
        <div role="slider" aria-label={`${label} volume`} aria-valuenow={Math.round(volume * 100)} aria-valuemin={0} aria-valuemax={100} tabIndex={0}
          onKeyDown={(e) => { if (e.key === "ArrowUp") onVol(Math.min(1, volume + 0.05)); if (e.key === "ArrowDown") onVol(Math.max(0, volume - 0.05)); }}
          onPointerDown={(e) => { const r = e.currentTarget.getBoundingClientRect(); onVol(Math.min(1, Math.max(0, (r.bottom - e.clientY) / r.height))); }}
          style={{ position: "relative", width: "100%", height: 104, borderRadius: 9, background: SC.meterBg, border: `1px solid ${off ? SC.redEdge : "rgba(255,255,255,0.10)"}`, cursor: "ns-resize", overflow: "hidden" }}>
          <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: pct, background: off ? "rgba(255,69,58,0.25)" : "rgba(31,178,74,0.35)" }} />
          <div style={{ position: "absolute", left: 2, right: 2, bottom: pct, height: 2, background: off ? SC.redSoft : "#fff", borderRadius: 1 }} />
        </div>
        <button type="button" aria-label={muted ? `Unmute ${label}` : `Mute ${label}`} aria-pressed={muted} onClick={onMute}
          style={{ width: "100%", padding: "9px 0", borderRadius: 9, border: `1px solid ${muted ? SC.red : SC.hair3}`, background: muted ? SC.redTint : SC.chipBg, color: muted ? SC.redSoft : SC.green, fontFamily: SC.mono, fontSize: 9, fontWeight: 600, letterSpacing: "0.08em", cursor: "pointer" }}>{muted ? "MUTED" : "ON"}</button>
        <div style={{ fontSize: 10, color: SC.dim3, textAlign: "center", lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{label}</div>
      </div>
    );
  };
  const mutedSourceCount = audioFeeds.filter((f) => channelOf(f.key).muted).length;
  const audioPanel = (
    <SCPanel label="AUDIO" ariaLabel="Audio mixer" collapsible collapsed={collapsedPanels.audio} onToggleCollapsed={() => setCollapsedPanels((c) => ({ ...c, audio: !c.audio }))} right={<span style={{ fontFamily: SC.mono, color: masterMuted ? SC.redSoft : "#b8b8bd" }}>{masterMuted ? "MASTER MUTED" : `${Math.round(master * 100)}%`}</span>}>
      {/* ADR 0106: name the two things operators kept tripping on — sources start
          MUTED (opt-in monitor) and this is the DESK monitor, never the fan feed. */}
      <div role="note" style={{ fontSize: 10, lineHeight: 1.35, color: SC.dim }}>
        Desk monitor only — never the fan mix. Sources start <strong style={{ color: SC.redSoft }}>muted</strong>; unmute one (with Master up) to hear it here.
      </div>
      {monitorAudioBlocked && (
        <button type="button" aria-label="Enable monitor audio" onClick={() => void enableMonitorAudio()}
          style={{ width: "100%", padding: "9px 0", borderRadius: 9, border: `1px solid ${SC.red}`, background: SC.redTint, color: SC.redSoft, fontFamily: SC.mono, fontSize: 10, fontWeight: 600, letterSpacing: "0.05em", cursor: "pointer" }}>
          ▶ Enable monitor audio — the browser blocked playback
        </button>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 8 }}>
        {faderStrip(null, "Master", master, masterMuted, setMaster, () => { const wasMuted = masterMuted; setMasterMuted(!wasMuted); if (wasMuted) void enableMonitorAudio(); })}
        {audioFeeds.length === 0 ? (
          <div style={{ gridColumn: "span 4", fontSize: 11, color: SC.dim, alignSelf: "center" }}>No live audio sources yet.</div>
        ) : audioFeeds.slice(0, 4).map((f) => {
          const ch = channelOf(f.key);
          return faderStrip(f.identity, f.name || f.key, ch.volume, ch.muted, (v) => setChannelVolume(f.key, v), () => { const wasMuted = ch.muted; setChannelMuted(f.key, !wasMuted); if (wasMuted) void enableMonitorAudio(); }, masterMuted);
        })}
      </div>
      {audioFeeds.length > 0 && (mutedSourceCount > 0 || masterMuted) && (
        <div role="status" style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.05em", color: SC.redSoft }}>
          {masterMuted ? "MASTER MUTED — nothing monitored" : `${mutedSourceCount} SOURCE${mutedSourceCount === 1 ? "" : "S"} MUTED — unmute to monitor`}
        </div>
      )}
      {/* Talkback (ADR 0213, LSC-423) — the RETURN path: hold to speak privately to
          talent. Never the fan/recording mix (SFU-restricted to the target). */}
      <div style={{ height: 1, background: SC.divider }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }} aria-label="Director talkback">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={mono10}>TALKBACK</span>
          <span style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.05em", color: talkbackTarget ? SC.redSoft : SC.dim }}>
            {talkbackTarget ? `ON AIR TO ${talkbackTargetLabel(talkbackTarget, talkbackTargetName).toUpperCase()}` : "HOLD TO TALK"}
          </span>
        </div>
        <div role="note" style={{ fontSize: 10, lineHeight: 1.35, color: SC.dim }}>
          Private direction to talent — <strong style={{ color: SC.text2 }}>never</strong> heard by fans or in the recording.
        </div>
        {talkbackTargets.length === 0 ? (
          <div style={{ fontSize: 11, color: SC.dim }}>No talent connected yet.</div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {talkbackTargets.map((t) => {
              const on =
                !!talkbackTarget &&
                (t.target.kind === "all"
                  ? talkbackTarget.kind === "all"
                  : talkbackTarget.kind === "one" && talkbackTarget.key === t.target.key);
              return (
                <button
                  key={t.label}
                  type="button"
                  aria-label={`Hold to talk to ${t.label}`}
                  aria-pressed={on}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    void startTalkback(t.target);
                  }}
                  onPointerUp={stopTalkback}
                  onPointerLeave={() => on && stopTalkback()}
                  onPointerCancel={stopTalkback}
                  style={{
                    flex: "1 1 auto",
                    minWidth: 64,
                    padding: "9px 10px",
                    borderRadius: 9,
                    border: `1px solid ${on ? SC.red : SC.hair3}`,
                    background: on ? SC.redTint : SC.chipBg,
                    color: on ? SC.redSoft : SC.dim,
                    fontFamily: SC.mono,
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    cursor: "pointer",
                    touchAction: "none",
                  }}
                >
                  {on ? "● " : ""}
                  {t.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {/* Director mic → On Air (ADR 0247, LSC-633) — the PUBLIC counterpart to talkback:
          a LATCHED toggle that joins the Director's voice to the fan Program mix +
          recording (no video slot, no TAKE). Off by default; the toggle is itself the
          immediate off control (acceptance). Distinct from the hold-to-talk above. */}
      <div style={{ height: 1, background: SC.divider }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }} aria-label="Director mic on air">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={mono10}>DIRECTOR MIC</span>
          <span style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.05em", color: micOnAir ? SC.redSoft : SC.dim }}>
            {micOnAir ? "● ON AIR TO FANS" : "OFF AIR"}
          </span>
        </div>
        <div role="note" style={{ fontSize: 10, lineHeight: 1.35, color: SC.dim }}>
          Your voice joins the <strong style={{ color: SC.text2 }}>fan Program mix</strong> + recording — no video slot, no TAKE. Hold a talkback button to cut privately to talent.
        </div>
        <button
          type="button"
          aria-label={micOnAir ? "Take Director mic off air" : "Put Director mic on air"}
          aria-pressed={micOnAir}
          onClick={() => void toggleMicOnAir()}
          style={{
            width: "100%",
            padding: "11px 12px",
            borderRadius: 9,
            border: `1px solid ${micOnAir ? SC.red : SC.hair3}`,
            background: micOnAir ? SC.redTint : SC.chipBg,
            color: micOnAir ? SC.redSoft : SC.green,
            fontFamily: SC.mono,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          {micOnAir && (
            <span aria-hidden style={{ width: 9, height: 9, borderRadius: "50%", background: SC.red, animation: "tally 1.6s ease-in-out infinite" }} />
          )}
          {micOnAir ? "MIC ON AIR — TAP TO GO OFF" : "GO ON AIR"}
        </button>
        {micErr && (
          <div role="alert" style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.04em", color: SC.redSoft }}>
            {micErr}
          </div>
        )}
      </div>
    </SCPanel>
  );

  // ── HEALTH panel (real signals; encoder bitrate dropped — RTC, not RTMP) ──
  const healthRows: { label: string; value: string; pct: string; fill: string }[] = [
    { label: "Link", value: conn === "live" || conn === "reconnected" ? "Connected" : conn === "reconnecting" ? "Reconnecting" : "Lost", pct: conn === "live" || conn === "reconnected" ? "100%" : conn === "reconnecting" ? "50%" : "8%", fill: conn === "live" || conn === "reconnected" ? SC.green : conn === "reconnecting" ? SC.yellow : SC.red },
    { label: "Live sources", value: String(feeds.length), pct: `${Math.min(100, feeds.length * 25)}%`, fill: SC.blue },
    { label: "Program", value: onAir ? "On air" : program.standby ? "Standby" : program.ended ? "Ended" : "Off air", pct: onAir ? "100%" : "20%", fill: onAir ? SC.red : SC.dim2 },
    { label: "Monitor mix", value: masterMuted ? "Muted" : `${Math.round(master * 100)}%`, pct: masterMuted ? "0%" : `${Math.round(master * 100)}%`, fill: SC.green },
  ];
  const healthPanel = (
    <SCPanel label="HEALTH" collapsible collapsed={collapsedPanels.health} onToggleCollapsed={() => setCollapsedPanels((c) => ({ ...c, health: !c.health }))}>
      {healthRows.map((h) => (
        <div key={h.label} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <div style={{ fontSize: 12, color: SC.text2 }}>{h.label}</div>
            <div style={{ fontFamily: SC.mono, fontSize: 11, color: SC.text }}>{h.value}</div>
          </div>
          <div style={{ height: 3, borderRadius: 2, background: SC.divider, overflow: "hidden" }}>
            <div style={{ height: "100%", width: h.pct, background: h.fill }} />
          </div>
        </div>
      ))}
    </SCPanel>
  );

  // ── PROGRAM HOLD panel — fan-facing SFU mute/pause (ADR 0055), distinct from the
  // operator monitor mixer. Kept from the mock's spirit as a compact rack control. ──
  const holdBtn = (on: boolean, label: string, aria: string, onClick: () => void): ReactNode => (
    <button type="button" aria-label={aria} aria-pressed={on} onClick={onClick}
      style={{ flex: 1, padding: "9px 0", borderRadius: 9, border: `1px solid ${on ? SC.red : SC.hair3}`, background: on ? SC.redTint : SC.chipBg, color: on ? SC.redSoft : SC.dim, fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.06em", cursor: "pointer" }}>{label}</button>
  );
  const holdPanel = (
    <SCPanel label="PROGRAM HOLD" ariaLabel="Program hold">
      <section aria-label="Program hold" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {holdBtn(!!program.audioMuted, program.audioMuted ? "Unmute" : "Mute audio", program.audioMuted ? "Unmute audio" : "Mute audio", toggleAudioMuted)}
          {holdBtn(!!program.videoPaused, program.videoPaused ? "Resume" : "Pause video", program.videoPaused ? "Resume video" : "Pause video", toggleVideoPaused)}
        </div>
        {!program.videoPaused && (
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.05em", color: SC.dim }}>AUTO-RESUME</span>
            <select aria-label="Auto-resume after" value={pauseSecondsStr} onChange={(e) => setPauseSecondsStr(e.target.value)} style={{ flex: 1, padding: "6px 8px", borderRadius: 8, border: `1px solid ${SC.hair2}`, background: SC.meterBg, color: SC.text, fontFamily: SC.sans, fontSize: 12 }}>
              {HOLD_DURATIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </label>
        )}
        {(program.audioMuted || program.videoPaused) && (
          <div role="status" style={{ fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.05em", color: SC.redSoft }}>
            FANS ON HOLD{program.audioMuted ? " · AUDIO MUTED" : ""}{program.videoPaused ? " · VIDEO PAUSED" : ""}
          </div>
        )}
      </section>
    </SCPanel>
  );

  // (widget registry + canvas prep moved below, after every tab/panel const is defined)

  // ── other tabs (Compose / Chroma / Graphics / Ads / Guests / Questions) ──
  // ── Compose tab (mock: LAYOUTS · STAGING·PREVIEW · Slot staged) ──
  const layoutCards: { value: ProgramLayout; name: string; desc: string; cells: number; cols: string }[] = [
    { value: "single", name: "Full frame", desc: "One source, full raster", cells: 1, cols: "1fr" },
    { value: "dual", name: "1×2 split", desc: "Two presenters side by side", cells: 2, cols: "1fr 1fr" },
    { value: "dual-board", name: "1×2 + board", desc: "Two heads + shared board", cells: 3, cols: "1fr 1fr" },
    { value: "dueling", name: "2×2 quad", desc: "Two heads + own boards", cells: 4, cols: "1fr 1fr" },
    { value: "tri", name: "3-up — Legend large", desc: "Legend dominant + 2 guests", cells: 3, cols: "2fr 1fr" },
    { value: "tri-equal", name: "3-up — equal", desc: "Three balanced heads", cells: 3, cols: "1fr 1fr 1fr" },
    { value: "quad", name: "4-up — Legend large", desc: "Legend dominant + 3 guests", cells: 4, cols: "2fr 1fr" },
    { value: "quad-equal", name: "4-up — equal (2×2)", desc: "Four heads in a 2×2 grid", cells: 4, cols: "1fr 1fr" },
    { value: "board-tri", name: "Board + 3-up", desc: "Board dominant + 3 small heads", cells: 3, cols: "1fr 1fr 1fr" },
    { value: "board-quad", name: "Board + 4-up", desc: "Board dominant + 4 small heads", cells: 4, cols: "1fr 1fr 1fr 1fr" },
  ];
  const composeTab = (
    <div aria-label="Compose program bus" style={{ display: "grid", gridTemplateColumns: "minmax(148px,236px) minmax(0,1fr) minmax(200px,300px)", gap: 10, alignItems: "start" }}>
      {/* LAYOUTS */}
      <section aria-label="Program layout controls" style={{ background: SC.panel, border: `1px solid ${SC.hair}`, borderRadius: 12, padding: 11, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={mono10}>LAYOUTS</div>
        <div style={{ fontSize: 11, color: SC.dim, lineHeight: 1.35 }}>Stage a layout, assign each slot a source below, then <strong style={{ color: SC.text2 }}>TAKE</strong>. <strong style={{ color: SC.text2 }}>2×2 quad</strong> = dueling — each publisher draws their own board.</div>
        {layoutCards.map((p) => {
          const active = (preview.layout ?? "single") === p.value;
          return (
            <button key={p.value} type="button" aria-label={`Layout ${p.name}`} aria-pressed={active} onClick={() => changeProgramLayout(p.value)}
              style={{ display: "flex", gap: 11, alignItems: "center", padding: 9, borderRadius: 9, border: `1px solid ${active ? SC.blueEdge : SC.hair}`, background: active ? SC.blueTint : SC.chipBg, cursor: "pointer", textAlign: "left" }}>
              <div style={{ width: 52, flex: "none", aspectRatio: "16/9", display: "grid", gap: 2, gridTemplateColumns: p.cols, gridTemplateRows: p.cells > 2 ? "1fr 1fr" : "1fr", padding: 3, borderRadius: 3, background: SC.bg }}>
                {Array.from({ length: p.cells }).map((_, i) => <div key={i} style={{ background: "#5a5a5e", borderRadius: 1 }} />)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: SC.text }}>{p.name}</div>
                <div style={{ fontSize: 11, color: SC.dim, lineHeight: 1.3 }}>{p.desc}</div>
              </div>
            </button>
          );
        })}
        <div style={{ height: 1, background: SC.divider }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ fontSize: 12, color: SC.text2 }}>Cell gap</div>
            <div style={{ fontSize: 11, color: "#ebebf0" }}>{cellGap}px</div>
          </div>
          <input type="range" min={0} max={8} value={cellGap} aria-label="Cell gap" onChange={(e) => setCellGap(Number(e.target.value))} style={{ width: "100%", accentColor: SC.blue }} />
          <button type="button" aria-label="Title-safe guides" aria-pressed={titleSafe} onClick={() => setTitleSafe((v) => !v)}
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 11px", borderRadius: 11, border: `1px solid ${titleSafe ? SC.blueEdge : SC.hair3}`, background: titleSafe ? SC.blueTint : SC.chipBg, cursor: "pointer" }}>
            <span style={{ fontSize: 12, color: "#e5e5ea" }}>Title-safe guides</span>
            <span style={{ fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.04em", color: "#d1d1d6" }}>{titleSafe ? "ON" : "OFF"}</span>
          </button>
        </div>
      </section>
      {/* STAGING · PREVIEW */}
      <section aria-label="Staging preview" style={{ background: SC.panel, border: `1px solid ${SC.hair}`, borderRadius: 12, padding: 11, display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span style={{ padding: "3px 8px", borderRadius: 4, background: SC.blueTint, border: `1px solid ${SC.blueEdge}`, fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.05em", color: SC.blueHi }}>STAGING · PREVIEW</span>
            <span style={{ fontSize: 13, color: SC.text }}>{previewMulti ? preview.layout : preview.source || "—"}</span>
          </div>
          <button type="button" aria-label="Take to program" onClick={() => void take("cut")} style={{ padding: "10px 18px", borderRadius: 16, border: `1px solid ${SC.red}`, background: SC.red, color: "#fff", fontFamily: SC.sans, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>TAKE TO PROGRAM →</button>
        </div>
        <div style={{ position: "relative", width: "100%", aspectRatio: "16/9", maxWidth: 640, alignSelf: "center", borderRadius: 11, overflow: "hidden", background: SC.stageBg, border: `1px solid ${SC.hair2}` }}>
          <div style={{ position: "absolute", inset: cellGap, display: "grid", gap: cellGap, ...(previewMulti && preview.layout ? gridForLayout(preview.layout) : { gridTemplateColumns: "1fr", gridTemplateAreas: '"a"' }) }}>
            {(previewMulti ? layoutSlots(preview.layout) : (["a"] as const)).map((slot) => {
              const key = slotSource(preview, slot);
              const on = stageSlot === slot;
              return (
                <button key={slot} type="button" aria-label={`Compose slot ${slot.toUpperCase()}`} onClick={() => setStageSlot(slot)}
                  style={{ gridArea: slot, position: "relative", borderRadius: 3, overflow: "hidden", border: `2px solid ${on ? slotAccent(slot) : "transparent"}`, boxShadow: `inset 0 0 0 2px ${slotAccent(slot)}66`, background: "#1c2530", cursor: "pointer", padding: 0 }}>
                  <div style={{ position: "absolute", left: "50%", bottom: 0, transform: "translateX(-50%)", width: "26%", height: "60%", borderRadius: "50% 50% 12% 12%", background: "rgba(255,255,255,0.22)" }} />
                  <div style={{ position: "absolute", left: 6, top: 6 }}><SlotBadge slot={slot} size={18} /></div>
                  <div style={{ position: "absolute", left: 7, bottom: 7, fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.05em", color: SC.text }}>{key || "— unassigned"}</div>
                </button>
              );
            })}
          </div>
          {titleSafe && <div style={{ position: "absolute", inset: "5%", border: "1px dashed rgba(255,255,255,0.16)", borderRadius: 2, pointerEvents: "none" }} />}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {previewMulti && layoutSlots(preview.layout).length === 2 && (
            <button type="button" aria-label="Swap slots" onClick={() => stageScene({ source: preview.sourceB, sourceB: preview.source })} style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 14px", borderRadius: 11, border: `1px solid ${SC.hair3}`, background: SC.neutralBtn, color: SC.text, fontFamily: SC.sans, fontSize: 12, cursor: "pointer" }}>Swap <SlotBadge slot="a" size={16} /> ⇄ <SlotBadge slot="b" size={16} /></button>
          )}
          <div style={{ flex: 1 }} />
          <div style={{ fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.04em", color: "#8a8a8e" }}>MUX OUT · 1920×1080 · SINGLE PROGRAM RASTER</div>
        </div>
      </section>
      {/* Slot staged */}
      <section aria-label="Slot staged" style={{ background: SC.panel, border: `1px solid ${SC.hair}`, borderRadius: 12, padding: 11, display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <SlotBadge slot={stageSlot} size={16} />
            <span style={mono10}>SLOT {stageSlot.toUpperCase()} · STAGED</span>
          </div>
          <div style={{ fontSize: 12, color: SC.text2, marginTop: 4 }}>{slotSource(preview, stageSlot) || "— assign a source"}</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {feeds.length === 0 ? <div style={{ fontSize: 11, color: SC.dim }}>Waiting for legends…</div> : feeds.map((f) => {
            const on = slotSource(preview, stageSlot) === f.key;
            return (
              <button key={f.identity} type="button" aria-label={`Assign ${f.name || f.key} to slot ${stageSlot.toUpperCase()}`} onClick={() => (previewMulti ? assignSlot(stageSlot, f.key) : selectSource(f.key))}
                style={{ display: "flex", gap: 9, alignItems: "center", padding: 6, borderRadius: 11, border: `1px solid ${on ? SC.blueEdge : SC.hair}`, background: on ? SC.blueTint : SC.chipBg, cursor: "pointer", textAlign: "left" }}>
                <div style={{ width: 44, flex: "none", aspectRatio: "16/9", borderRadius: 3, overflow: "hidden", background: "#000", position: "relative" }}>
                  <video ref={(el) => setTileEl(f.identity, el)} muted autoPlay playsInline aria-label={`Feed ${f.name || f.key}`} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 12, color: SC.text }}>{f.name || f.key}</div>
                  <div style={{ fontSize: 11, color: SC.dim, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{isGuestIdentity(f.identity) ? "Guest" : "Legend"}</div>
                </div>
                {on && <SlotBadge slot={stageSlot} size={16} />}
              </button>
            );
          })}
        </div>
        {(preview.layout === "dueling" || program.layout === "dueling") && (
          <>
            <div style={{ height: 1, background: SC.hair }} />
            <div style={mono10}>SLOT BOARDS</div>
            {/* Show whenever dueling is STAGED (preview) OR ON AIR (program) — the
                selectors read/write program.boards LIVE, so the Director keeps
                per-slot board control after the TAKE, not only while staging it
                (LSC-487: control vanished once preview moved off dueling). */}
            <div style={{ fontSize: 11, color: SC.dim, lineHeight: 1.35 }}>
              Dueling gives each publisher their own board — pick a template per slot
              {program.layout === "dueling" && preview.layout !== "dueling" ? " (on air now)" : ""}.
            </div>
            <label style={{ display: "block" }}><span style={fieldLabel}>Board A</span>
              <div style={{ display: "flex", gap: 6, alignItems: "stretch" }}>
                <select aria-label="Board A" value={program.boards?.a?.board ?? "blank"} onChange={(e) => changeDuelBoard("a", e.target.value as BoardId)} style={{ ...selStyle, flex: 1 }}>{boardOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
                <button type="button" aria-label="Clear Board A" title="Clear Board A strokes (keeps the slate)" onClick={() => clearDuelBoard("a")} style={ghostBtn}>Clear</button>
              </div>
            </label>
            <label style={{ display: "block" }}><span style={fieldLabel}>Board B</span>
              <div style={{ display: "flex", gap: 6, alignItems: "stretch" }}>
                <select aria-label="Board B" value={program.boards?.b?.board ?? "blank"} onChange={(e) => changeDuelBoard("b", e.target.value as BoardId)} style={{ ...selStyle, flex: 1 }}>{boardOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
                <button type="button" aria-label="Clear Board B" title="Clear Board B strokes (keeps the slate)" onClick={() => clearDuelBoard("b")} style={ghostBtn}>Clear</button>
              </div>
            </label>
          </>
        )}
        <div style={{ height: 1, background: SC.hair }} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={mono10}>CHROMA</div>
          <button type="button" aria-label="Toggle chroma key" onClick={toggleDirectorBackground} style={{ padding: "9px 12px", borderRadius: 9, border: `1px solid ${chroma?.enabled ? SC.blueEdge : SC.hair3}`, background: chroma?.enabled ? SC.blueTint : SC.chipBg, color: chroma?.enabled ? SC.blueHi : "#ebebf0", fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.04em", cursor: "pointer" }}>{chroma?.enabled ? "KEYED" : "OFF"}</button>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {["#3adb6a", "#4caf50", "#0a84ff", "#3a5aff"].map((hex) => (
            <button key={hex} type="button" aria-label={`Key colour ${hex}`} onClick={() => setDirectorChroma({ color: hex, enabled: true })} style={{ width: 38, height: 38, borderRadius: 12, border: `2px solid ${chroma?.color === hex ? "#fff" : "rgba(255,255,255,0.25)"}`, background: hex, cursor: "pointer" }} />
          ))}
          <button type="button" aria-label="Tune key" onClick={() => setTab("chroma")} style={{ flex: 1, padding: "8px 0", borderRadius: 12, border: `1px solid ${SC.hair3}`, background: SC.neutralBtn, color: "#e5e5ea", fontFamily: SC.sans, fontSize: 11, cursor: "pointer" }}>Tune key</button>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <div style={mono10}>BACKGROUND PLATE</div>
          <label style={{ ...mono10, color: SC.blueHi, cursor: "pointer" }}>＋ Upload plate<input type="file" accept="image/png,image/jpeg,image/webp" aria-label="Upload background plate" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void onDirUploadBackground(f); e.target.value = ""; }} /></label>
        </div>
        {backgrounds.length === 0 ? (
          <div style={{ fontSize: 11, color: SC.dim }}>No plates yet — upload a still to key the Legend over it.</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6 }}>
            {backgrounds.slice(0, 8).map((b) => (
              <div key={b.id} style={{ position: "relative" }}>
                <button type="button" aria-label={`Plate ${b.name}`} onClick={() => selectDirectorBackground(b.id)} style={{ padding: 0, borderRadius: 9, overflow: "hidden", border: `1px solid ${chroma?.backgroundId === b.id ? SC.blueEdge : SC.hair}`, aspectRatio: "16/9", position: "relative", cursor: "pointer", background: "#2a2a2e", width: "100%", display: "block" }}>
                  <img src={b.imageUrl} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                </button>
                <button type="button" aria-label={`Remove plate ${b.name}`} onClick={() => void onDirRemoveBackground(b.id)} style={{ position: "absolute", top: 2, right: 2, width: 18, height: 18, borderRadius: "50%", border: "none", background: "rgba(16,16,18,0.8)", color: SC.dim, fontSize: 11, lineHeight: "18px", cursor: "pointer", padding: 0 }}>✕</button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );

  // ── Chroma key tab (KEY SOURCE + the SHARED ChromaControls, ADR 0120) ──
  // The Director's hand-rolled KEY CONTROLS are gone: the console-specific KEY SOURCE
  // list stays (which live feed is the key subject + the eyedropper target), and the
  // actual controls come from the same studioChrome `ChromaControls` the Legend uses
  // (ADR 0070/0071) — one implementation, resolving the ADR 0088 follow-up. The Director
  // sets program.chroma with by:"director" (setDirectorChroma), so its push wins.
  const chromaTab = (
    <div aria-label="Legend background" style={{ display: "grid", gridTemplateColumns: "minmax(148px,236px) minmax(0,1fr)", gap: 10, alignItems: "start" }}>
      <section aria-label="Key source" style={{ background: SC.panel, border: `1px solid ${SC.hair}`, borderRadius: 12, padding: 11, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={mono10}>KEY SOURCE</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {feeds.length === 0 ? <div style={{ fontSize: 11, color: SC.dim }}>No live sources.</div> : feeds.map((f) => {
            const onProgram = program.source === f.key;
            const arm = eyedropper && onProgram;
            return (
              <div key={f.identity} onClick={arm ? (e) => sampleFromProgramTile(e.clientX, e.clientY) : undefined}
                style={{ display: "flex", gap: 9, alignItems: "center", padding: 6, borderRadius: 11, border: `1px solid ${onProgram ? SC.blueEdge : SC.hair}`, background: onProgram ? SC.blueTint : SC.chipBg, cursor: arm ? "crosshair" : "default" }}>
                <div style={{ width: 44, flex: "none", aspectRatio: "16/9", borderRadius: 3, overflow: "hidden", background: "#000", position: "relative" }}>
                  <video ref={(el) => setTileEl(f.identity, el)} muted autoPlay playsInline aria-label={`Feed ${f.name || f.key}`} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: SC.text }}>{f.name || f.key}</div>
                  <div style={{ fontSize: 11, color: SC.dim }}>{isGuestIdentity(f.identity) ? "Guest" : "Legend"}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ fontSize: 10, color: SC.dim, lineHeight: 1.4 }}>
          The Legend's feed is composited before Program. Arm the eyedropper in the controls, then click the on-program tile above to sample the key colour.
        </div>
      </section>
      <section aria-label="Key controls" style={{ background: SC.panel, border: `1px solid ${SC.hair}`, borderRadius: 12, padding: 11 }}>
        <ChromaControls
          chroma={chroma}
          backgrounds={backgrounds}
          bgBusy={uploading}
          onSelectBackground={selectDirectorBackground}
          onToggleEnabled={toggleDirectorBackground}
          onSetMode={(m) => setDirectorChroma({ mode: m })}
          onSet={setDirectorChroma}
          onTune={tuneDirectorChroma}
          onUpload={onDirUploadBackground}
          onRemove={onDirRemoveBackground}
          eyedropper={{ armed: eyedropper, onToggle: () => setEyedropper((v) => !v) }}
        />
      </section>
    </div>
  );

  // ── Graphics tab (mock: GRAPHICS list · editor · STYLE) ──
  const gfxItems: { kind: OverlayKind; name: string; sub: string }[] = [
    { kind: "lower-third", name: "Lower third", sub: l3Title || "name / title" },
    { kind: "whiteboard", name: "Whiteboard", sub: "telestration board" },
    { kind: "ad", name: "Ad break", sub: ads.find((a) => a.id === selectedAdId)?.name || "sponsor / clip" },
  ];
  const gfxLive = (k: OverlayKind) => (k === "lower-third" ? lowerThirdOnAir : k === "whiteboard" ? whiteboardOnAir : k === "ad" ? adOnAir : false);
  const accentSwatches = ["#ff453a", "#3adb6a", "#0a84ff", "#f7f7fa"];
  const graphicsTab = (
    <div aria-label="Overlay controls" style={{ display: "grid", gridTemplateColumns: "minmax(160px,262px) minmax(0,1fr) minmax(200px,300px)", gap: 10, alignItems: "start" }}>
      {/* GRAPHICS list */}
      <section style={{ background: SC.panel, border: `1px solid ${SC.hair}`, borderRadius: 12, padding: 11, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={mono10}>GRAPHICS</div>
          <button type="button" aria-label="Prepare overlay" onClick={() => stageKind("lower-third")} style={{ padding: "9px 12px", borderRadius: 12, border: `1px solid ${SC.hair2}`, background: SC.panel2, color: "#d1d1d6", fontFamily: SC.sans, fontSize: 11, cursor: "pointer" }}>+ New</button>
        </div>
        {gfxItems.map((g) => {
          const sel = stagedKind === g.kind; // staged into the preview bus (ADR 0116)
          const live = gfxLive(g.kind); // actually on Fan Program
          const disabled = g.kind === "lower-third" && !lowerThirdHasText;
          return (
            <div key={g.kind} aria-label={g.name} style={{ display: "flex", flexDirection: "column", gap: 9, padding: 11, borderRadius: 9, border: `1px solid ${sel ? SC.blueEdge : SC.hair}`, background: sel ? SC.blueTint : SC.chipBg }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: live ? SC.red : sel ? SC.blue : "#3a3a3c", flex: "none" }} />
                <button type="button" aria-label={`Select ${g.name}`} onClick={() => stageKind(g.kind)} style={{ flex: 1, minWidth: 0, textAlign: "left", background: "none", border: "none", padding: 0, cursor: "pointer" }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: SC.text }}>{g.name}</div>
                  <div style={{ fontSize: 11, color: SC.dim, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{g.sub}</div>
                </button>
                <div style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.04em", color: live ? SC.redSoft : sel ? SC.blueHi : SC.dim2 }}>{live ? "ON AIR" : sel ? "STAGED" : "READY"}</div>
              </div>
              {/* Stage the graphic into the preview bus — the program TAKE puts it on
                  air (ADR 0116). Clicking the staged one un-stages it. No direct-to-program. */}
              <button type="button" aria-label={sel ? `Unstage ${g.name}` : `Stage ${g.name}`}
                onClick={() => (sel ? stageOverlayOff() : stageKind(g.kind))}
                disabled={!sel && disabled}
                style={{ padding: "11px 0", borderRadius: 10, border: `1px solid ${sel ? SC.blueEdge : SC.hair3}`, background: sel ? SC.blueTint : SC.panel2, color: sel ? SC.blueHi : "#d1d1d6", fontFamily: SC.sans, fontSize: 11, fontWeight: 500, letterSpacing: "0.04em", cursor: "pointer", opacity: disabled ? 0.5 : 1 }}>
                {sel ? "STAGED — click to unstage" : "STAGE TO PREVIEW"}
              </button>
            </div>
          );
        })}
      </section>
      {/* editor */}
      <section style={{ background: SC.panel, border: `1px solid ${SC.hair}`, borderRadius: 12, padding: 11, display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, color: "#f7f7fa" }}>{gfxItems.find((g) => g.kind === stagedKind)?.name ?? "Graphic"}</div>
            {/* ADR 0116: graphics stage into the PREVIEW bus; the single program TAKE
                (in the dual-bus row) puts them on air — no separate Send/Take here. */}
            <div style={{ fontSize: 12, color: SC.dim }}>Staged in preview — the program <strong style={{ color: SC.blueHi }}>TAKE</strong> puts it on air.</div>
          </div>
          {stagedKind !== "none" && (
            <button type="button" aria-label="Unstage graphic" onClick={stageOverlayOff} style={{ padding: "10px 15px", borderRadius: 11, border: `1px solid ${SC.hair3}`, background: SC.panel2, color: "#d1d1d6", fontFamily: SC.sans, fontSize: 12, fontWeight: 500, letterSpacing: "0.06em", cursor: "pointer" }}>Unstage</button>
          )}
        </div>
        {stagedKind === "lower-third" && (
          <>
            <div style={{ position: "relative", width: "100%", maxWidth: 520, aspectRatio: "16/9", background: SC.stageBg, borderRadius: 11, border: `1px solid ${SC.hair2}`, overflow: "hidden" }} aria-label="Lower third preview">
              {lowerThirdHasText ? <SCLowerThird line1={l3Title} line2={l3Subtitle} accent={l3Accent} /> : <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", ...mono10 }}>enter a title or subtitle</span>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, minWidth: 0 }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 7, minWidth: 0 }}><span style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.06em", color: SC.dim }}>LINE 1</span><input aria-label="Name / title" value={l3Title} onChange={(e) => setL3Title(e.target.value)} style={inputStyle} /></label>
              <label style={{ display: "flex", flexDirection: "column", gap: 7, minWidth: 0 }}><span style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.06em", color: SC.dim }}>LINE 2</span><input aria-label="Subtitle" value={l3Subtitle} onChange={(e) => setL3Subtitle(e.target.value)} style={inputStyle} /></label>
            </div>
          </>
        )}
        {stagedKind === "whiteboard" && (
          <div style={{ display: "grid", gap: 8 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
              <label><span style={fieldLabel}>Board</span><select aria-label="Board" value={wbBoard} onChange={(e) => stageBoard(e.target.value as BoardId)} style={selStyle}>{boardOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
              <label><span style={fieldLabel}>Layout</span><select aria-label="Layout" value={wbLayout} onChange={(e) => stageLayout(e.target.value as WhiteboardLayout)} style={selStyle}><option value="pip-legend">PIP · legend small</option><option value="pip-board">PIP · board small</option><option value="side">Side by side</option><option value="full">Full screen</option></select></label>
              <button type="button" aria-label="Clear whiteboard" onClick={() => drawWhiteboard([])} style={ghostBtn}>Clear</button>
            </div>
            <WhiteboardCanvas strokes={strokes} board={wbBoard} boardImageUrl={boardImg(wbBoard)} editable onChange={drawWhiteboard} color="#0a84ff" ariaLabel="Whiteboard editor" />
          </div>
        )}
        {stagedKind === "ad" && (
          <div style={{ display: "grid", gap: 10 }} aria-label="Ad controls">
            <label style={{ maxWidth: 320 }}><span style={fieldLabel}>Ad</span><select aria-label="Ad" value={selectedAdId} onChange={(e) => setSelectedAdId(e.target.value)} style={selStyle}>{ads.length === 0 && <option value="">No ads yet</option>}{ads.map((a) => <option key={a.id} value={a.id}>{a.name}{a.kind === "video" ? " (video)" : ""}</option>)}</select></label>
            {stagedAd && !adOnAir && <div style={{ position: "relative", width: "100%", maxWidth: 520, aspectRatio: "16/9", background: SC.stageBg, borderRadius: 11, overflow: "hidden" }} aria-label="Ad preview"><AdSurface overlay={stagedAd} muted /></div>}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <label style={{ ...mono10, color: SC.blueHi, cursor: uploading ? "default" : "pointer" }}>{uploading ? "Uploading…" : "＋ Upload video ad"}<input type="file" accept="video/mp4,video/webm,video/quicktime" aria-label="Upload video ad" disabled={uploading} style={{ display: "none" }} onChange={(e) => void onUploadAd("video")(e.target.files?.[0] ?? null)} /></label>
              <label style={{ ...mono10, color: SC.blueHi, cursor: uploading ? "default" : "pointer" }}>{uploading ? "Uploading…" : "＋ Upload slate"}<input type="file" accept="image/png,image/jpeg,image/webp,image/gif" aria-label="Upload slate image" disabled={uploading} style={{ display: "none" }} onChange={(e) => void onUploadAd("image")(e.target.files?.[0] ?? null)} /></label>
            </div>
          </div>
        )}
        {stagedKind === "none" && <div style={{ fontSize: 12, color: SC.dim }}>Pick a graphic on the left to edit it.</div>}
      </section>
      {/* STYLE */}
      <section style={{ background: SC.panel, border: `1px solid ${SC.hair}`, borderRadius: 12, padding: 11, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={mono10}>STYLE</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 12, color: SC.text2 }}>Accent</div>
          <div style={{ display: "flex", gap: 7 }}>
            {accentSwatches.map((hex) => (
              <button key={hex} type="button" aria-label={`Accent ${hex}`} onClick={() => setL3Accent(hex)} style={{ width: 42, height: 42, borderRadius: 11, border: `2px solid ${l3Accent === hex ? "#fff" : "rgba(255,255,255,0.25)"}`, background: hex, cursor: "pointer" }} />
            ))}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 12, color: SC.text2 }}>Position</div>
          <div style={{ display: "flex", gap: 6 }}>
            {(["left", "center"] as const).map((p) => {
              const on = l3Position === p;
              return <button key={p} type="button" aria-label={`Position ${p}`} aria-pressed={on} onClick={() => setL3Position(p)} style={{ flex: 1, padding: "11px 0", borderRadius: 10, border: `1px solid ${on ? SC.blueEdge : SC.hair3}`, background: on ? SC.blueTint : SC.chipBg, color: on ? SC.blueHi : SC.dim, fontFamily: SC.sans, fontSize: 12, cursor: "pointer", textTransform: "capitalize" }}>{p}</button>;
            })}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 12, color: SC.text2 }}>Auto-out</div>
          <div style={{ display: "flex", gap: 6 }}>
            {[{ s: 0, n: "Manual" }, { s: 5, n: "5s" }, { s: 8, n: "8s" }, { s: 12, n: "12s" }].map((x) => {
              const on = autoOutS === x.s;
              return <button key={x.n} type="button" aria-label={`Auto-out ${x.n}`} aria-pressed={on} onClick={() => setAutoOutS(x.s)} style={{ flex: 1, padding: "11px 0", borderRadius: 10, border: `1px solid ${on ? SC.blueEdge : SC.hair3}`, background: on ? SC.blueTint : SC.chipBg, color: on ? SC.blueHi : SC.dim, fontFamily: SC.mono, fontSize: 11, cursor: "pointer" }}>{x.n}</button>;
            })}
          </div>
        </div>
        <button type="button" aria-label="Clear graphics (stage off)" onClick={stageOverlayOff} style={{ padding: "10px 0", borderRadius: 11, border: `1px solid ${SC.hair3}`, background: SC.panel2, color: "#e5e5ea", fontFamily: SC.sans, fontSize: 12, cursor: "pointer" }}>Stage graphics off — TAKE to clear</button>
      </section>
    </div>
  );

  const takeIntermission = async () => {
    setPushError(null);
    try {
      const ad = await createSlateAd(caveId, { name: boardTitle || "Intermission", brand: boardSub || "", offer: boardTitle || "We'll be right back", tier: "Intermission" });
      if (!mountedRef.current) return;
      setAds((list) => [ad, ...list]);
      setSelectedAdId(ad.id);
      // Build the overlay from the freshly-returned ad — `ads` state hasn't updated
      // yet, so `adOverlayFor` (which reads that array) can't resolve it here.
      const overlay: ProgramOverlay = {
        kind: "ad",
        adId: ad.id,
        adName: ad.name,
        adImageUrl: ad.imageUrl || undefined,
        adSlate: { brand: ad.brand, offer: ad.offer, tier: ad.tier },
      };
      void push({ ...programRef.current, overlay });
    } catch (err: unknown) {
      if (mountedRef.current) setPushError(msg(err));
    }
  };
  const adsTab = (
    <div aria-label="Ads and breaks" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(260px,380px)", gap: 10, alignItems: "start" }}>
      {/* INVENTORY */}
      <section style={{ background: SC.panel, border: `1px solid ${SC.hair}`, borderRadius: 12, padding: 11, display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div style={mono10}>INVENTORY</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ ...mono10, color: SC.blueHi, cursor: uploading ? "default" : "pointer" }}>{uploading ? "Uploading…" : "＋ Upload video"}<input type="file" accept="video/mp4,video/webm,video/quicktime" aria-label="Upload video ad" disabled={uploading} style={{ display: "none" }} onChange={(e) => void onUploadAd("video")(e.target.files?.[0] ?? null)} /></label>
            <label style={{ ...mono10, color: SC.blueHi, cursor: uploading ? "default" : "pointer" }}>{uploading ? "Uploading…" : "＋ Upload slate"}<input type="file" accept="image/png,image/jpeg,image/webp,image/gif" aria-label="Upload slate image" disabled={uploading} style={{ display: "none" }} onChange={(e) => void onUploadAd("image")(e.target.files?.[0] ?? null)} /></label>
            <button type="button" aria-label="Add sponsor slate" onClick={() => void addSponsorSlate()} style={{ ...mono10, color: SC.blueHi, background: "none", border: "none", cursor: "pointer", padding: 0 }}>＋ Slate card</button>
          </div>
        </div>
        {ads.length === 0 ? (
          <div style={{ fontSize: 12, color: SC.dim, padding: "10px 0" }}>No ads yet — upload a clip or slate to build the break inventory.</div>
        ) : ads.map((a) => {
          const live = adOnAir && program.overlay?.kind === "ad" && program.overlay.adId === a.id;
          const staged = stagedKind === "ad" && selectedAdId === a.id; // staged into preview (ADR 0116)
          return (
            <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: 13, borderRadius: 9, border: `1px solid ${live ? "rgba(255,69,58,0.5)" : staged ? SC.blueEdge : SC.hair}`, background: SC.panel2 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: SC.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.name}</div>
                <div style={{ fontSize: 11, color: SC.dim }}>{a.kind === "video" ? "Video clip" : "Sponsor slate"}</div>
              </div>
              <div style={{ padding: "4px 10px", borderRadius: 999, background: live ? SC.redTint : staged ? SC.blueTint : SC.chipBg, color: live ? SC.redSoft : staged ? SC.blueHi : SC.dim, fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.04em", width: 74, textAlign: "center" }}>{live ? "PLAYED" : staged ? "STAGED" : "QUEUED"}</div>
              {/* Stage the ad into preview — the program TAKE plays it full-frame (ADR 0116). */}
              <button type="button" aria-label={staged ? `Unstage ${a.name}` : `Stage ${a.name}`}
                onClick={() => (staged ? stageOverlayOff() : armAd(a.id))}
                style={{ padding: "11px 16px", borderRadius: 10, border: `1px solid ${staged ? SC.blueEdge : SC.hair2}`, background: staged ? SC.blueTint : SC.panel2, color: staged ? SC.blueHi : "#e5e5ea", fontFamily: SC.sans, fontSize: 11, fontWeight: 500, cursor: "pointer" }}>{staged ? "Staged" : "Stage"}</button>
            </div>
          );
        })}
      </section>
      {/* INTERMISSION BOARD */}
      <section aria-label="Intermission board" style={{ background: SC.panel, border: `1px solid ${SC.hair}`, borderRadius: 12, padding: 11, display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={mono10}>INTERMISSION BOARD</div>
        <div style={{ position: "relative", aspectRatio: "16/9", borderRadius: 11, overflow: "hidden", background: "radial-gradient(120% 120% at 50% 0%, #23242a 0%, #0c0c0f 70%)", border: `1px solid ${SC.hair2}`, display: "flex", flexDirection: "column", justifyContent: "center", gap: 10, padding: "8%" }}>
          <div style={{ width: 34, height: 3, borderRadius: 2, background: l3Accent }} />
          <div style={{ fontSize: 26, fontWeight: 600, lineHeight: 1.15, color: "#fff" }}>{boardTitle || "We'll be right back"}</div>
          <div style={{ fontSize: 13, color: "#d1d1d6" }}>{boardSub}</div>
        </div>
        <label style={{ display: "flex", flexDirection: "column", gap: 7 }}><span style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.06em", color: SC.dim }}>HEADLINE</span><input aria-label="Headline" value={boardTitle} onChange={(e) => setBoardTitle(e.target.value)} style={inputStyle} /></label>
        <label style={{ display: "flex", flexDirection: "column", gap: 7 }}><span style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.06em", color: SC.dim }}>SUBLINE</span><input aria-label="Subline" value={boardSub} onChange={(e) => setBoardSub(e.target.value)} style={inputStyle} /></label>
        <button type="button" aria-label="Take intermission board" onClick={() => void takeIntermission()} style={{ padding: "13px 0", borderRadius: 11, border: `1px solid ${SC.red}`, background: SC.red, color: "#fff", fontFamily: SC.sans, fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", cursor: "pointer" }}>TAKE BOARD TO PROGRAM</button>
      </section>

      {/* END SLATE (ADR 0104) — an OPTIONAL custom "Broadcast Ended" board shown to
          fans when the show ends, picked from the cave slate library (reuses the ad
          slate uploads above). It rides the opaque program state (endedBoard = the
          image URL), relayed + persisted, and survives into the ended state (the end
          handler merges `ended` without discarding it). When unset, fans get the
          resolved post-roll or the default "That's a wrap" card. */}
      <section aria-label="End slate" style={{ background: SC.panel, border: `1px solid ${SC.hair}`, borderRadius: 12, padding: 11, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={mono10}>END SLATE</div>
        <div style={{ fontSize: 12, color: SC.dim }}>Optional image shown to fans when the broadcast ends, instead of the default card. Upload one as a slate above, then pick it here.</div>
        <label style={{ maxWidth: 360 }}><span style={fieldLabel}>Broadcast-ended board</span>
          <select aria-label="Broadcast-ended board" value={program.endedBoard || ""} onChange={(e) => void push({ ...programRef.current, endedBoard: e.target.value || undefined })} style={selStyle}>
            <option value="">Default “That’s a wrap” card</option>
            {ads.filter((a) => a.imageUrl).map((a) => <option key={a.id} value={a.imageUrl}>{a.name}</option>)}
          </select>
        </label>
        {program.endedBoard && (
          <img src={program.endedBoard} alt="End slate preview" style={{ width: "100%", aspectRatio: "16/9", objectFit: "contain", borderRadius: 11, border: `1px solid ${SC.hair2}`, background: "#000" }} />
        )}
      </section>
    </div>
  );

  // ── Rundown tab (RUN OF SHOW — client-side cues, ADR 0088) ──
  const rundownTab = (
    <section aria-label="Run of show" style={{ background: SC.panel, border: `1px solid ${SC.hair}`, borderRadius: 12, padding: 11, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={mono10}>RUN OF SHOW</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", fontFamily: SC.mono, fontSize: 11 }}>
          <span style={{ color: SC.dim }}>CUES <span style={{ color: SC.text }}>{rundown.length}</span></span>
          <span style={{ color: SC.dim }}>ELAPSED <span style={{ color: SC.text }}>{formatClock(elapsedMs)}</span></span>
          <button type="button" aria-label="Add cue from preview" onClick={addCue} style={{ padding: "8px 12px", borderRadius: 10, border: `1px solid ${SC.blueEdge}`, background: SC.blueTint, color: SC.blueHi, fontFamily: SC.sans, fontSize: 11, cursor: "pointer" }}>＋ Add cue from preview</button>
        </div>
      </div>
      {rundown.length === 0 ? (
        <div style={{ fontSize: 12, color: SC.dim, padding: "10px 0", lineHeight: 1.5 }}>
          No cues yet. Stage anything in <strong style={{ color: SC.text2 }}>preview</strong> — a source/layout (Console · Compose), a lower third or whiteboard (Graphics), or an <strong style={{ color: SC.text2 }}>ad</strong> (Ads &amp; breaks) — then <strong style={{ color: SC.blueHi }}>＋ Add cue from preview</strong> to build the run of show. Step it live with Next / Take.
        </div>
      ) : rundown.map((c, i) => {
        const live = cueMatches(c, program);
        const next = cueMatches(c, preview);
        const label = live ? "LIVE" : next ? "NEXT" : "QUEUED";
        const bar = live ? SC.red : next ? SC.blue : "#3a3a3c";
        const s = c.scene;
        const sceneExtra = [s.sourceB, s.sourceC, s.sourceD].filter(Boolean);
        const sceneDetail = `${s.layout ?? "single"}${s.source ? ` · ${s.source}` : ""}${sceneExtra.length ? ` + ${sceneExtra.join(" + ")}` : ""}`;
        const gfx = overlayLabel(s.overlay);
        return (
          <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 16, padding: "13px 14px", borderRadius: 9, background: live ? SC.redTint : SC.panel2, border: `1px solid ${SC.divider}` }}>
            <div style={{ width: 3, alignSelf: "stretch", borderRadius: 2, background: bar, flex: "none" }} />
            <div style={{ fontFamily: SC.mono, fontSize: 11, color: SC.dim2, width: 22, flex: "none" }}>{String(i + 1).padStart(2, "0")}</div>
            <div style={{ flex: 1.4, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
              <div style={{ fontSize: 13, color: "#f7f7fa", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</div>
              {gfx && <div style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.04em", color: SC.blueHi }}>◆ GRAPHIC</div>}
            </div>
            <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: SC.dim3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sceneDetail}</div>
            <div style={{ padding: "4px 10px", borderRadius: 999, background: live ? SC.redTint : next ? SC.blueTint : SC.chipBg, color: live ? SC.redSoft : next ? SC.blueHi : SC.dim, fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.04em", width: 70, textAlign: "center" }}>{label}</div>
            <button type="button" aria-label={`Next ${c.name}`} onClick={() => cueToPreview(c)} style={{ padding: "11px 15px", borderRadius: 10, border: `1px solid ${SC.blueEdge}`, background: SC.blueTint, color: SC.blueHi, fontFamily: SC.sans, fontSize: 11, cursor: "pointer" }}>Next</button>
            <button type="button" aria-label={`Take ${c.name}`} onClick={() => takeCue(c)} style={{ padding: "11px 17px", borderRadius: 10, border: "1px solid rgba(255,69,58,0.55)", background: "rgba(255,69,58,0.14)", color: SC.redSoft, fontFamily: SC.sans, fontSize: 11, fontWeight: 500, cursor: "pointer" }}>Take</button>
            <button type="button" aria-label={`Remove ${c.name}`} onClick={() => removeCue(c.id)} style={{ padding: "11px 12px", borderRadius: 10, border: `1px solid ${SC.hair3}`, background: SC.chipBg, color: SC.dim, fontFamily: SC.sans, fontSize: 11, cursor: "pointer" }}>✕</button>
          </div>
        );
      })}
    </section>
  );

  // ── Stream tab (mock: destinations → dropped; health + event log we DO have) ──
  const encoderRows: { k: string; v: string }[] = [
    { k: "Delivery", v: "LiveKit WebRTC" },
    { k: "Room", v: roomName || "—" },
    { k: "Link", v: conn === "live" || conn === "reconnected" ? "Connected" : conn === "reconnecting" ? "Reconnecting" : "Lost" },
    { k: "Live sources", v: String(feeds.length) },
    { k: "Program", v: onAir ? "On air" : program.standby ? "Standby" : program.ended ? "Ended" : "Off air" },
    { k: "Elapsed", v: formatClock(elapsedMs) },
  ];
  const eventLog: { t: string; msg: string; tone: string }[] = [
    ...(programSourceLost ? [{ t: formatClock(elapsedMs), msg: `Program source ${program.source} lost — reconnecting`, tone: SC.redSoft }] : []),
    ...(program.ended ? [{ t: formatClock(elapsedMs), msg: "Broadcast ended", tone: SC.dim }] : program.standby ? [{ t: formatClock(elapsedMs), msg: "Standby — paused", tone: SC.faint }] : []),
    { t: formatClock(elapsedMs), msg: onAir ? `On program: ${program.source}` : "No source on program", tone: SC.text2 },
    { t: "0:00:00", msg: `Joined room ${roomName || "—"}`, tone: SC.dim },
  ];
  const streamTab = (
    <div aria-label="Stream" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 10, alignItems: "start" }}>
      <section style={{ background: SC.panel, border: `1px solid ${SC.hair}`, borderRadius: 12, padding: 11, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={mono10}>ENCODER</div>
        {encoderRows.map((e) => (
          <div key={e.k} style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "7px 0", borderBottom: `1px solid ${SC.panel2}` }}>
            <div style={{ fontSize: 12, color: SC.faint }}>{e.k}</div>
            <div style={{ fontFamily: SC.mono, fontSize: 12, color: SC.text, textAlign: "right" }}>{e.v}</div>
          </div>
        ))}
      </section>
      <section style={{ background: SC.panel, border: `1px solid ${SC.hair}`, borderRadius: 12, padding: 11, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={mono10}>EVENT LOG</div>
        {eventLog.map((l, i) => (
          <div key={i} style={{ display: "flex", gap: 14, alignItems: "baseline", padding: "6px 0", borderBottom: `1px solid ${SC.panel2}` }}>
            <div style={{ fontFamily: SC.mono, fontSize: 11, color: "#7c7c80", flex: "none" }}>{l.t}</div>
            <div style={{ fontSize: 12, color: l.tone, lineHeight: 1.45 }}>{l.msg}</div>
          </div>
        ))}
      </section>
    </div>
  );

  // ── Deck tab (ADR 0024/0096): pair a Stream Deck for studio-grade control —
  //    TAKE · FTB · HOLD · NEXT block · staged layouts · program mute · audio dials.
  const deckKeyLine = "TAKE · FTB · HOLD · NEXT · LAYOUT · MUTE · AUDIO DIALS";
  const deckTab = (
    <SCPanel label="STREAM DECK">
      <div style={{ display: "grid", gap: 8, maxWidth: 560 }}>
        <span style={{ ...mono10, color: SC.faint }}>Runs inside the Elgato app — no need to quit it. Every director control on the deck: {deckKeyLine}.</span>
        {deckPairings.length > 0 ? (
          <div style={{ display: "grid", gap: 6 }}>
            <span style={{ ...mono10, color: SC.green }}>✓ PAIRED · {onAir ? "ON AIR" : "OFF AIR"} · {nextCueObj ? `NEXT ▸ ${nextCueObj.name}` : "NO CUES"}</span>
            {deckPairings.map((p) => (
              <div key={p.token} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontFamily: SC.mono, fontSize: 11, color: SC.dim }}>{p.label} · {p.token}</span>
                <button type="button" aria-label={`Unpair ${p.label}`} onClick={() => void revokeDeck(p.ref)} style={{ ...ghostBtn, padding: "5px 10px", fontSize: 11 }}>Unpair</button>
              </div>
            ))}
            <div>
              <button type="button" aria-label="Pair another Stream Deck" onClick={() => void startPairing()} style={{ ...ghostBtn, padding: "6px 12px", fontSize: 11 }}>Pair another</button>
            </div>
          </div>
        ) : (
          <div>
            <button type="button" aria-label="Pair a Stream Deck" onClick={() => void startPairing()} style={{ ...primaryBtn, padding: "6px 12px", fontSize: 11 }}>Pair a Stream Deck</button>
          </div>
        )}
        {pairCode && (
          <div role="status" style={{ padding: "10px 14px", borderRadius: 11, border: `1px solid ${SC.hair3}`, background: SC.blueTint, display: "grid", gap: 6 }}>
            <span style={mono10}>PAIRING CODE</span>
            <strong style={{ fontFamily: SC.mono, fontSize: 22, letterSpacing: "0.28em", color: SC.blueHi }}>{pairCode}</strong>
            <ol style={{ fontFamily: SC.mono, fontSize: 11, color: SC.dim, margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
              <li>
                <a href={PLUGIN_DOWNLOAD_PATH} download style={{ color: SC.blueHi }}>Install the plugin</a>{" "}
                (once per computer)
              </li>
              <li>Add the “Legend Sports Cave” actions to your Stream Deck keys &amp; dials</li>
              <li>Enter this code in the action’s settings — it expires in 5 min</li>
            </ol>
          </div>
        )}
      </div>
    </SCPanel>
  );

  const guestsTab = (
    <SCPanel label="GUESTS">
      <section aria-label="Guest publishers" style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 560 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <label><span style={fieldLabel}>Guest name</span><input aria-label="Guest name" value={guestLabel} onChange={(e) => setGuestLabel(e.target.value)} style={inputStyle} /></label>
          <button type="button" aria-label="Invite guest" disabled={invitingGuest || !broadcast} onClick={() => void inviteGuest()} style={{ ...primaryBtn, opacity: invitingGuest || !broadcast ? 0.5 : 1 }}>{invitingGuest ? "Inviting…" : "Invite guest"}</button>
        </div>
        {activeGuestInvites.length === 0 ? (
          <div style={{ fontSize: 12, color: SC.dim }}>No active guest invites. Invite one to get a shareable join link.</div>
        ) : (
          <ul aria-label="Active guest invites" style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
            {activeGuestInvites.map((inv) => (
              <li key={inv.code} style={{ display: "flex", gap: 12, flexWrap: "wrap", padding: "10px 12px", background: SC.chipBg, border: `1px solid ${SC.hair}`, borderRadius: 9 }}>
                {guestQrs[inv.code] && (
                  <img src={guestQrs[inv.code]} alt={`Join QR code for ${inv.label || "Guest"}`} width={96} height={96} style={{ borderRadius: 8, background: "#fff", padding: 6, flex: "none" }} />
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0, flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <strong style={{ color: SC.blueHi, fontSize: 12 }}>{inv.label || "Guest"}</strong>
                    {inv.used && <span style={{ fontFamily: SC.mono, fontSize: 10, color: SC.green }}>JOINED</span>}
                    <button type="button" aria-label={`Revoke invite for ${inv.label || "Guest"}`} onClick={() => void revokeGuest(inv.code)} style={{ ...ghostBtn, marginLeft: "auto" }}>Revoke</button>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <a href={inv.joinUrl} aria-label={`Join link for ${inv.label || "Guest"}`} style={{ fontFamily: SC.mono, fontSize: 11, color: SC.blue, overflowWrap: "anywhere", minWidth: 0 }}>{inv.joinUrl}</a>
                    <button type="button" title="Copy link" aria-label={copiedGuest === inv.code ? `Link for ${inv.label || "Guest"} copied` : `Copy link for ${inv.label || "Guest"}`} onClick={() => void copyGuestLink(inv.joinUrl, inv.code)}
                      style={{ flex: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 8, border: `1px solid ${SC.hair3}`, background: SC.chipBg, color: copiedGuest === inv.code ? SC.green : SC.dim, cursor: "pointer" }}>
                      {copiedGuest === inv.code ? (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M20 6 9 17l-5-5" /></svg>
                      ) : (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
                      )}
                    </button>
                  </div>
                  <div style={{ fontSize: 10, color: SC.dim }}>Scan the QR or share the link — the guest joins with no account.</div>
                </div>
              </li>
            ))}
          </ul>
        )}
        {guestError && <p role="alert" style={{ color: SC.redSoft, fontSize: 12 }}>{guestError}</p>}
      </section>
    </SCPanel>
  );

  const questionsTab = (
    <SCPanel label="QUESTIONS">
      <section aria-label="Fan question queue" style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 560 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={mono10}>Fan questions</span>
          <button type="button" aria-label={program.askDisabled ? "Enable Ask the Legend" : "Disable Ask the Legend"} aria-pressed={!program.askDisabled} onClick={() => void push({ ...programRef.current, askDisabled: !program.askDisabled })} style={ghostBtn}>
            {program.askDisabled ? "Ask the Legend: Off" : "Ask the Legend: On"}
          </button>
        </div>
        {program.askDisabled ? (
          <p style={{ fontSize: 12, color: SC.dim }}>Ask the Legend is off — fans can't submit questions for this CaveCast.</p>
        ) : fanQuestions.length === 0 ? (
          <p style={{ fontSize: 12, color: SC.dim }}>No Fan questions yet.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {fanQuestions.map((q) => {
              const selected = q.id === selectedQuestionId;
              const sent = sentMessageIds.has(q.id);
              return (
                <button key={q.id} type="button" aria-label={`Select question from ${q.user}`} aria-pressed={selected} onClick={() => setSelectedQuestionId(q.id)}
                  style={{ padding: "9px 11px", textAlign: "left", cursor: "pointer", color: SC.text, background: selected ? SC.blueTint : SC.chipBg, border: `1px solid ${selected ? SC.blueEdge : SC.hair}`, borderRadius: 9 }}>
                  <strong style={{ color: SC.blueHi, fontSize: 12 }}>{q.user}</strong>
                  {sent && <span style={{ fontFamily: SC.mono, fontSize: 10, marginLeft: 8, color: SC.green }}>SENT</span>}
                  <span style={{ display: "block", marginTop: 3, fontSize: 13 }}>{q.text}</span>
                </button>
              );
            })}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button type="button" aria-label="Send to Legend" disabled={!selectedQuestion || sendingQuestion || (selectedQuestion ? sentMessageIds.has(selectedQuestion.id) : false)} onClick={() => void sendSelectedQuestion()} style={{ ...primaryBtn, opacity: !selectedQuestion || sendingQuestion ? 0.5 : 1 }}>
            {sendingQuestion ? "Sending…" : selectedQuestion && sentMessageIds.has(selectedQuestion.id) ? "Sent to Legend" : "Send to Legend"}
          </button>
          {selectedQuestion && <span style={mono10}>SELECTED · {selectedQuestion.user}</span>}
        </div>
        {questionError && <p role="alert" style={{ color: SC.redSoft, fontSize: 12 }}>{questionError}</p>}
      </section>
    </SCPanel>
  );

  // ── widget registry + active-workspace canvas prep (ADR 0124) ────────────────────
  // Pair each widget id with its in-render node (the panel/tab consts above, closures
  // over console state). The WorkspaceCanvas lays out the active tab's placed widgets;
  // the toolbox lists widgets not placed on ANY tab (singletons).
  const prodChatPanel = (
    <ProductionChatPanel messages={prodChat.messages} onSend={prodChat.send} unread={prodChat.unread} onRead={prodChat.markRead} peerLabel="Legend" />
  );
  const widgetNodes: Record<string, ReactNode> = {
    sources: sourcesPanel, ask: askPanel, scenes: scenesPanel, graphicsBus: graphicsBusPanel, audio: audioPanel, health: healthPanel, hold: holdPanel, prodChat: prodChatPanel,
    compose: composeTab, chroma: chromaTab, graphics: graphicsTab, ads: adsTab, rundown: rundownTab, guests: guestsTab, questions: questionsTab, stream: streamTab, deck: deckTab,
  };
  const widgetDefFor = (id: string): WidgetDef | null => {
    const m = DIRECTOR_WIDGET_META[id];
    return m ? { id, title: m.title, node: widgetNodes[id], minW: m.minW, minH: m.minH } : null;
  };
  const activeWs = workspaces.find((w) => w.id === tab) ?? workspaces.find((w) => w.id === HOME_WORKSPACE_ID) ?? workspaces[0];
  const placedWidgets = (activeWs?.widgets ?? []).map(widgetDefFor).filter((x): x is WidgetDef => x !== null);
  // Where each widget currently lives (id → tab name), for the toolbox "move here" label.
  const widgetLocation: Record<string, string> = {};
  for (const w of workspaces) for (const id of w.widgets ?? []) widgetLocation[id] = w.name;
  // Toolbox = widgets NOT on this tab — movable here (singletons, ADR 0125); each labeled
  // with its current tab (or none if unplaced). This makes any tab — including a fresh
  // custom one — composable from the full widget set.
  const onActive = new Set(activeWs?.widgets ?? []);
  const toolboxWidgets = DIRECTOR_WIDGET_IDS
    .filter((id) => !onActive.has(id))
    .map((id): WidgetDef | null => { const d = widgetDefFor(id); return d ? { ...d, location: widgetLocation[id] } : null; })
    .filter((x): x is WidgetDef => x !== null);
  const updateActiveLayout = (l: WidgetLayout[]) => {
    if (!activeWs) return;
    setWorkspaces((ws) => {
      const cur = ws.find((w) => w.id === activeWs.id)?.layout ?? [];
      return layoutEqual(cur, l) ? ws : setWorkspaceLayout(ws, activeWs.id, l);
    });
  };
  const addWidgetToActive = (id: string) => {
    if (!activeWs) return;
    const m = DIRECTOR_WIDGET_META[id];
    setWorkspaces((ws) => placeWidget(ws, id, activeWs.id, { w: m.w, h: m.h, minW: m.minW, minH: m.minH }));
  };
  const removeWidgetFromActive = (id: string) => setWorkspaces((ws) => removeWidget(ws, id));
  // Custom workspaces (ADR 0125): create a new empty tab (switch to it in edit mode so the
  // operator fills it from the toolbox), and reset everything to the built-in defaults.
  const createWorkspace = () => {
    const nums = workspaces.filter((w) => !w.builtin).map((w) => Number(w.id.replace(/^ws-/, ""))).filter((n) => Number.isFinite(n));
    const n = (nums.length ? Math.max(...nums) : 0) + 1;
    const id = `ws-${n}`;
    setWorkspaces((ws) => [...ws, { id, name: `Workspace ${n}`, builtin: false, hidden: false, widgets: [], layout: [] }]);
    setTab(id);
    setEditMode(true);
    setTabsEditorOpen(false);
  };
  const resetWorkspaces = () => {
    setWorkspaces(validateWidgetPlacement(seedWidgetDefaults(reconcileWorkspaces(undefined, DIRECTOR_BUILTIN_WORKSPACES), DIRECTOR_WIDGET_DEFAULTS), new Set(DIRECTOR_WIDGET_IDS)));
    setTab(HOME_WORKSPACE_ID);
    setEditMode(false);
    setTabsEditorOpen(false);
  };

  return (
    <div role="dialog" aria-modal="true" aria-label={`Direct · ${event.title}`}
      style={{ position: "fixed", inset: 0, zIndex: 200, height: "calc(100dvh / var(--ui-zoom, 1))", overflow: "hidden", display: "flex", flexDirection: "column", color: SC.text, background: SC.bg, fontFamily: SC.sans }}>
      {header}
      {nav}
      {tabsEditorOpen && (
        <WorkspaceTabsEditor workspaces={workspaces} onChange={setWorkspaces} onClose={() => setTabsEditorOpen(false)} onCreate={createWorkspace} onReset={resetWorkspaces} />
      )}
      {/* Always-mounted operator monitor audio (ADR 0085) — persists across tabs. */}
      <div aria-hidden style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}>
        {audioFeeds.map((f) => <audio key={f.identity} ref={(el) => setAudioEl(f, el)} autoPlay />)}
      </div>
      <main style={{ flex: 1, display: "flex", flexDirection: "column", padding: 10, gap: 10, minHeight: 0, overflow: "hidden" }}>
        {conn !== "live" && <ConnectionBanner state={conn} onRejoin={rejoin} />}
        {dualBus}
        <div style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", gap: 8, paddingRight: 2 }}>
          <div style={{ flex: 1, minHeight: 0 }}>
            <WorkspaceCanvas
              placed={placedWidgets}
              toolbox={toolboxWidgets}
              layout={activeWs?.layout ?? []}
              editMode={editMode}
              onLayoutChange={updateActiveLayout}
              onAdd={addWidgetToActive}
              onRemove={removeWidgetFromActive}
            />
          </div>
          {(error || pushError) && <p role="alert" style={{ color: SC.redSoft, fontSize: 12 }}>{error || pushError}</p>}
        </div>
      </main>
    </div>
  );
}

// A prominent, colour-coded A/B slot-identity badge (ADR 0108). Rendered on the
// preview/program monitor tiles and the staged-source controls so the slot a feed
// occupies is unmistakable. `ariaLabel` makes it queryable; omit for decor only.
function SlotBadge({ slot, ariaLabel, size = 20 }: { slot: SourceSlot; ariaLabel?: string; size?: number }): ReactNode {
  return (
    <span
      aria-label={ariaLabel}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: size,
        height: size,
        padding: "0 5px",
        borderRadius: 6,
        background: slotAccent(slot),
        color: SLOT_INK,
        fontFamily: SC.mono,
        fontSize: Math.round(size * 0.6),
        fontWeight: 700,
        letterSpacing: "0.02em",
        border: "1px solid rgba(0,0,0,0.35)",
        boxShadow: "0 1px 3px rgba(0,0,0,0.45)",
        flex: "none",
      }}
    >
      {slot.toUpperCase()}
    </span>
  );
}

// Client-side composition grid for the studio monitors (ADR 0054/0212 layouts) —
// the SAME template the fan uses (programLayout.layoutGridTemplate), so the
// confidence monitors match Fan output.
function gridForLayout(layout: ProgramLayout): CSSProperties {
  return layoutGridTemplate(layout);
}

// H:MM:SS elapsed clock (guards NaN/negative to 0).
function formatClock(ms: number): string {
  const total = Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 1000) : 0;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${h}:${pad(m)}:${pad(s)}`;
}

function msg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// The program-bus "scene" fields that stage in PREVIEW and publish on a TAKE (ADR
// 0081): which source(s) compose and how. Overlay / chroma / hold / lifecycle are
// NOT part of the staged scene — overlays take on their own graphics bus (ADR
// 0063), chroma is a live legend-compositing override, and hold/blackout/lifecycle
// are live interventions. Absent fields are omitted so a TAKE never clobbers a
// live overlay/hold with `undefined`.
function sceneOf(p: ProgramState): ProgramState {
  const scene: ProgramState = {};
  if (p.source !== undefined) scene.source = p.source;
  if (p.sourceB !== undefined) scene.sourceB = p.sourceB;
  if (p.sourceC !== undefined) scene.sourceC = p.sourceC;
  if (p.sourceD !== undefined) scene.sourceD = p.sourceD;
  if (p.layout !== undefined) scene.layout = p.layout;
  // The overlay (graphics bus) is now part of the staged scene (ADR 0116): a graphic
  // stages into preview.overlay and the single program TAKE commits it, exactly like
  // source/layout — no separate "Send to Program".
  if (p.overlay !== undefined) scene.overlay = p.overlay;
  return scene;
}
